"use client";

/**
 * VirtualSpace public renderer — Phase 3.
 *
 * Rendered at `/share/[token]` when a share_link resolves to an
 * `entity_type = 'virtual_space'`. Takes a list of already-resolved
 * block rows (visible=true only — filtered upstream). Each block is
 * one **space** in the recipient's forward walk (Matterport, PDFs,
 * file list, location card, …), snap-scrolled as a unit with the
 * public scroll rail.
 *
 * Block renderers live in this file so `/share/[token]/page.tsx`
 * stays focused on resolution. Heavy sub-components (point cloud
 * viewer, 3D tiles) are dynamic-imported so a VirtualSpace with no
 * point cloud block never ships the ~600 KB Giro3D bundle.
 *
 * Client component because several block types need DOM APIs
 * (clipboard, copy-link buttons) or client-only loaders (Google
 * Maps, Matterport iframe, dnd viewer chrome).
 */

import dynamic from "next/dynamic";
import Link from "next/link";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Code2,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Mail,
  MapPin,
  Phone,
  Plane,
  Search,
  Share2,
  Star,
  Clock,
  Utensils,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DownloadZipButton from "@/components/virtualspace/download-zip-button";
import { MultiDeliverableViewerTabs } from "@/components/virtualspace/multi-deliverable-viewer-tabs";
import { buildVirtualSpaceShareUrl } from "@/lib/virtualspace/virtual-space-host";
import {
  linkedPointCloudSetIdsFromScan,
  shouldFallbackNavvisEmbedToLidarViewer,
} from "@/lib/virtualspace/navvis-embed-lidar-fallback";
import {
  titleCaseProperName,
  formatCityState,
  deriveCityFilters,
  groupLocationsByCity,
  buildLaunchEmbedSnippet,
  hueFromString,
  monogramFor,
  countArchived,
  filterByArchivedVisibility,
} from "@/lib/virtualspace/property-portfolio";
import {
  formatCaptureDate,
  floorTimelineSummary,
  isTimeSeries,
  pickComparePair,
  buildingTimelineStats,
  latestThumbnail,
  readyScanCount,
  nearestReadyScanIndex,
  nearestScanIndexAtPercent,
  floorFreshness,
  filterFloors,
  sortFloors,
  scanUnavailableLabel,
  isoToUtcMs,
  type BuildingFloor,
  type BuildingScan,
  type FloorSortMode,
} from "@/lib/virtualspace/building-scan-timeline";
import { buildSatelliteStaticUrl } from "@/lib/virtualspace/location-imagery";
import { MAP_PIN_GOOGLE, MAP_POPUP } from "@/lib/virtualspace/map-brand";

import type {
  AerialImageConfig,
  AlignedBimCloudConfig,
  GaussianSplatTourConfig,
  ProgramLocationMapConfig,
  ProgramPropertyGridConfig,
  BuildingFloorTimelineConfig,
  BimViewerConfig,
  CalendarBookingConfig,
  ContactCardConfig,
  DroneVideoConfig,
  ExternalEmbedConfig,
  FileListConfig,
  FloorPlanViewConfig,
  Google3DTilesConfig,
  GoogleMapConfig,
  HeadingConfig,
  HeroAerialConfig,
  HeroImageConfig,
  LidarViewerConfig,
  LinkButtonsConfig,
  LocationCardConfig,
  MatterportConfig,
  NavvisEmbedConfig,
  PointCloudConfig,
  RestaurantBookingConfig,
  RichTextConfig,
  ScanListConfig,
  SpacerConfig,
  SpaceSurveyLinkConfig,
  VirtualSpaceBlockRow,
  StreetViewConfig,
  VideoConfig,
} from "@/lib/virtualspace/virtual-space-types";
import { publicVirtualSpaceBlockSectionAriaLabel } from "@/lib/virtualspace/public-virtual-space-rail";
import { PUBLIC_VIRTUAL_SPACE_TEXT_TIER_CLASS } from "@/lib/virtualspace/virtualspace-public-viewport";
import { VirtualSpaceExploreGate } from "@/components/virtualspace/virtual-space-explore-gate";
import { SpatialAsBuiltDisclaimer } from "@/components/virtualspace/spatial-as-built-disclaimer";
import { isTheaterEligibleBlock } from "@/lib/virtualspace/public-virtual-space-theater";
import {
  buildMatterportEmbedUrl,
  matterportThumbnailUrl,
} from "@/lib/virtualspace/matterport";
import { MatterportLaunchViewer } from "@/components/virtualspace/matterport-sdk-launch-block";
import { useLaunchActiveModel } from "@/components/virtualspace/launch-active-model-context";
import {
  isSpaceRowTheaterEligible,
  planSpaceRows,
  type SpaceRow,
} from "@/lib/virtualspace/space-row-planner";
import { cn } from "@/lib/utils";
import { VirtualSpaceTheaterToggle } from "@/components/virtualspace/virtual-space-media-frame";
import {
  compareBimCadFileOrder,
  interleaveFloorBands,
} from "@/lib/virtualspace/bim-file-sort";
import {
  BIM_FILE_TYPE_COUNT_CLASS,
  BIM_FLOOR_BAND_ROW_CLASS,
  BIM_FLOOR_BAND_TITLE_CLASS,
} from "@/lib/virtualspace/bim-file-list-ui";
import BimCadFolderTree, {
  useFolderTreeLeafStyle,
} from "@/components/virtualspace/deliverables/bim-cad-folder-tree";
import {
  deliverableFileBaseName,
  shouldUseFolderTree,
} from "@/lib/virtualspace/deliverable-file-folders";
import {
  firstNonEmptyLine,
  formatVersionDate,
} from "@/lib/virtualspace/version-date-format";
import { omitEmptyPublicFileListBlocks } from "@/lib/virtualspace/public-file-list-visibility";
import { safeExternalHref } from "@/lib/virtualspace/safe-external-href";
import { publicPortfolioResults, type PortfolioSort } from "@/lib/virtualspace/public-portfolio-results";
import { hostMatchesDomain } from "@/lib/url-host";
import { StandaloneCompositionProvider, PublicCompositionBlockProvider, PublicCompositionSectionProvider, usePublicCompositionSectionId, CompositionViewerMount, usePublicComposition, usePublicCompositionBlockId, usePublicCompositionLocation, setPublicCompositionLocation } from "./public-composition-context";
import { PublicMediaFrame } from "./public-media-frame";
import { encodePublicCompositionLocation } from "@/lib/virtualspace/public-composition-location";
import { publicVideoSource } from "@/lib/virtualspace/public-video-source";
import { PublicCopyMenu } from "./public-copy-menu";
import { LaunchViewerSessionProvider } from "./launch-viewer-session-context";

// Heavy sub-components. Lazy-load so VirtualSpaces without these
// blocks never pay the bundle cost. ssr:false because each has
// browser-only deps (WebGL, window.google, iframes).
const PointCloudViewer = dynamic(
  () => import("./point-cloud-viewer"),
  {
    ssr: false,
    loading: () => (
      <ViewerSkeleton height="min(70dvh, 800px)" label="Loading 3D viewer…" />
    ),
  },
);

// Reused inside `LocationCardBlock` — no longer mounted directly
// in the location card body. Kept as a top-level dynamic import
// because other callers (e.g. legacy `google_3d_tiles` block)
// still mount it directly.
const ThreeDTilesView = dynamic(
  () => import("./three-d-tiles-view"),
  {
    ssr: false,
    loading: () => (
      <ViewerSkeleton height="min(70dvh, 800px)" label="Loading 3D map…" />
    ),
  },
);

const AutodeskViewer = dynamic(
  () => import("./autodesk-viewer"),
  {
    ssr: false,
    loading: () => (
      <ViewerSkeleton
        height="min(70dvh, 800px)"
        label="Loading CAD/BIM Viewer…"
      />
    ),
  },
);

// Photoreal Gaussian Splat tour viewer (migration 385). PlayCanvas
// Spark + Three.js — heavy WebGL, SSR-off + lazy.
// Phase F wraps it in MultiFormatTourViewer so a single block
// can expose Photoreal / Surface / Showcase pills when
// alternate artifacts are available.
const MultiFormatTourViewer = dynamic(
  () =>
    import("./multi-format-tour-viewer").then((m) => ({
      default: m.MultiFormatTourViewer,
    })),
  {
    ssr: false,
    loading: () => (
      <ViewerSkeleton
        height="min(70dvh, 800px)"
        label="Loading Photoreal Tour…"
      />
    ),
  },
);

// BIM ↔ Point Cloud aligned overlay viewer (migration 382). Same
// SSR-off + lazy-load story as the other heavy WebGL viewers.
const AlignedBimCloudViewer = dynamic(
  () =>
    import("./aligned-bim-cloud-viewer").then((m) => ({
      default: m.AlignedBimCloudViewer,
    })),
  {
    ssr: false,
    loading: () => (
      <ViewerSkeleton
        height="min(70dvh, 800px)"
        label="Loading aligned overlay…"
      />
    ),
  },
);

/**
 * {@link LocationSurface} is the canonical tabbed location card on
 * the dashboard's locations page. We reuse it inside the
 * VirtualSpace `location_card` block so the public share matches
 * the dashboard 1:1, with the Parcel View tab hidden via the
 * customer preset (parcel data is operator-internal scoping info).
 */
const LocationSurface = dynamic(
  () => import("./location-surface"),
  {
    ssr: false,
    loading: () => (
      <ViewerSkeleton
        height="min(70dvh, 800px)"
        label="Loading location views…"
      />
    ),
  },
);

/**
 * `AerialViewEmbed` is the on-demand Google Aerial View
 * fetcher used by the dashboard's location card. The
 * `hero_aerial` block reuses it instead of rolling its own
 * <video> against the cached `locations.aerial_video_uris`
 * column — that column is only populated for locations
 * created after migration 194, and even then only after the
 * background render completes (~2-5 min). Going through
 * AerialViewEmbed gives us the same robust state-machine
 * UX (fetching → processing → ready → error) the dashboard
 * already uses.
 */
const AerialViewEmbed = dynamic(
  () => import("./aerial-view-embed"),
  {
    ssr: false,
    loading: () => (
      <ViewerSkeleton
        height="min(70dvh, 800px)"
        label="Loading aerial fly-over…"
      />
    ),
  },
);

// ── Data shapes for renderer-only extras ──────────────────────────

/**
 * File-asset rows resolved server-side by `loadVirtualSpaceAssets`
 * before the renderer mounts. Keyed by file_asset_id so the
 * file_list block can look them up without another query.
 */
export type ResolvedFileAsset = {
  id: string;
  file_name: string | null;
  public_url: string | null;
  mime_type: string | null;
  file_size: number | null;
  /** Internal storage path. `aps:<urn>` = Autodesk-stored
   *  (no public URL — must go through the share-token-gated
   *  download endpoint). `upgrade/...` = Supabase Storage
   *  bucket. Anything else = legacy public_url-only. */
  storage_path: string | null;
  /** Cloudflare R2 mirror URL when the APS file's been
   *  successfully duplicated. Free egress + custom domain;
   *  always preferred for download links over the
   *  signed-URL roundtrip. */
  r2_public_url: string | null;
  r2_mirror_state: string | null;
  /** Optional revision linkage so the file_list block can
   *  render version chrome (V1 / V2 dividers + reviewer
   *  notes) when a deliverable has multiple uploads. */
  revision_id: string | null;
  revision_number: number | null;
  reviewer_notes: string | null;
  /** Operator-set version label (migration 260). When set,
   *  the file_list block renders "V2 — Pre-MEP review"
   *  instead of just "V2" in the version header. Mirrors
   *  the dashboard detail page's behaviour. */
  revision_name: string | null;
  /** Operator-set editorial date for the file's parent
   *  revision (migration 372). YYYY-MM-DD wire string from
   *  PostgREST; null when unset. Stamped redundantly on
   *  every file in the same version so the renderer's
   *  per-version grouping can read the date directly off
   *  the row without re-querying the revision table. */
  revision_date: string | null;
  /** `file_assets.created_at` — shown next to size on download
   *  rows (same pattern as the operator BIM file library). */
  created_at: string | null;
  /** Dense manual order from migration 349; null = heuristic sort. */
  display_sort_key: number | null;
  /** Operator-set short note for this individual file (migration 371).
   *  Renders as a callout under the file row in the file_list
   *  block, AND surfaces as a banner above the Autodesk Viewer
   *  when this file is the active model. Null when unset. */
  note: string | null;
};

/** One APS-backed model in a federated BIM/CAD/floor-plan viewer.
 *  Matches `ViewerModelInput` + share-page resolver output. */
export type ResolvedApsUrnEntry = {
  urn: string;
  fileName: string | null;
  revisionNumber: number | null;
  versionLabel: string | null;
  versionNotes: string | null;
  createdAt: string | null;
  fileSize: number | null;
  displaySortKey: number | null;
};

/**
 * Single `deliverable_revisions` row, projected for the
 * version chrome on inline viewer blocks. Drives:
 *   - The "V·N Latest" chip above the BIM / floor-plan viewer.
 *   - The reviewer-notes callout above the same viewer when
 *     the operator wrote release notes for the latest version.
 *   - The total-revisions count in the chip ("V2 · 2 versions")
 *     so recipients know more than one upload exists, even when
 *     the viewer itself only shows the newest.
 */
export type ResolvedDeliverableRevision = {
  revision_id: string;
  revision_number: number;
  reviewer_notes: string | null;
  /** Operator-set version label (migration 260). Surfaced
   *  next to the V<n> chip ("V2 · Pre-MEP review · Latest")
   *  so the recipient sees the same identifying string the
   *  operator uses internally. */
  name: string | null;
  total_revisions: number;
};

/**
 * Resolved deliverable — polymorphic projection that pulls
 * type-specific data off the per-type detail tables
 * (matterport_details, navvis_details, bim_details, etc.) so
 * the renderer never needs to do its own joins. Migration 256
 * introduced deliverable-aware blocks that lean heavily on
 * this projection.
 *
 * Back-compat: the legacy field names at the top
 * (provider / showcase_link / provider_model_id /
 * thumbnail_url) stay populated from `matterport_details` so
 * the existing `matterport` block keeps reading them under the
 * same shape it always did.
 */
export type ResolvedScan = {
  id: string;
  deliverable_type: string;
  output_url?: string | null;
  space_name: string | null;
  location_name: string | null;

  // Back-compat (kept populated from matterport_details so the
  // existing matterport block keeps working without touching
  // its consumer).
  provider: string | null;
  showcase_link: string | null;
  thumbnail_url: string | null;
  provider_model_id: string | null;

  // Polymorphic per-type detail rows. Exactly one is non-null
  // per deliverable (matching its `deliverable_type`); others
  // are null.
  matterport_details: {
    provider_model_id: string | null;
    showcase_link: string | null;
    workshop_link: string | null;
    secondary_showcase_link: string | null;
    provider_thumbnail_url: string | null;
  } | null;
  navvis_details: {
    point_cloud_set_id: string | null;
    /** Every linked registered cloud (migration 906); primary first. */
    linked_point_cloud_set_ids?: string[];
    coordinate_frame: string | null;
    spatial_accuracy_meters: number | null;
    e57_file_ref: string | null;
    /** Per-deliverable NavVis IVION viewer URL (migration 422).
     *  Auto-falls-back as the `navvis_embed` block's iframe src
     *  when the block's own `share_url_override` is null —
     *  operators paste the URL once on the deliverable and every
     *  VirtualSpace embed of it picks it up. */
    viewer_url: string | null;
  } | null;
  /** Used for both Leica + Faro deliverables — the source tables
   *  share the same column shape; the renderer can't tell them
   *  apart by intent so we collapse to one resolved field. */
  lidar_details: {
    point_cloud_set_id: string | null;
    /** Every linked registered cloud (migration 906); primary first. */
    linked_point_cloud_set_ids?: string[];
    coordinate_frame: string | null;
    spatial_accuracy_meters: number | null;
    e57_file_ref: string | null;
  } | null;
  bim_details: {
    lod_level: string | null;
    file_format: string | null;
    /** Federation set across ALL revisions of this
     *  deliverable. Each URN carries `revisionNumber` +
     *  `versionLabel` + `versionNotes` so the viewer's
     *  Models panel can group rows by version AND surface
     *  the operator's per-version reviewer notes inline.
     *  Sorted with the latest-version's highest-priority
     *  file first so the viewer's initial camera frames it. */
    aps_urns: ResolvedApsUrnEntry[];
    /** Newest `deliverable_revisions` row for this deliverable.
     *  Drives the "V·N · Latest" chip + reviewer-notes callout
     *  on the inline viewer block. Null when the deliverable has
     *  zero revisions (which means the viewer can't render
     *  anything anyway). */
    latest_revision: ResolvedDeliverableRevision | null;
  } | null;
  cad_details: {
    file_format: string | null;
    aps_urns: ResolvedApsUrnEntry[];
    latest_revision: ResolvedDeliverableRevision | null;
  } | null;
  floor_plan_details: {
    pdf_url: string | null;
    plan_style: string | null;
    aps_urns: ResolvedApsUrnEntry[];
    latest_revision: ResolvedDeliverableRevision | null;
  } | null;
  drone_details: {
    video_url: string | null;
    photo_count: number | null;
    video_duration_seconds: number | null;
  } | null;
  /** Set when the deliverable is type=`space_survey` and a
   *  `space_surveys` row exists with a share token. */
  space_survey: {
    share_token: string | null;
    template_name: string | null;
  } | null;
};

/**
 * Resolved location — pulled by `loadEntity` for any block that
 * references a location (today: `location_card`). Carries the
 * pre-rendered Google Aerial View fly-over URI so the location
 * card's Aerial tab can render the cached video instead of a
 * static satellite image.
 */
export type ResolvedLocation = {
  id: string;
  name: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  /** Optional program metadata for the location-card's chip
   *  row. The auto-composer enriches this when the VirtualSpace
   *  is generated from an order so the card surfaces the
   *  operator's program label without the renderer needing a
   *  follow-up query. */
  program_name: string | null;
  lat: number | null;
  lng: number | null;
  /** Google Aerial View URIs keyed by quality
   *  ('LOW' | 'MEDIUM' | 'HIGH'); each value is `{ landscape,
   *  portrait }`. See migration 194 + `pickAerialVideoUrl`. */
  aerial_video_uris: Record<string, unknown> | null;
};

/**
 * One program location resolved for the program "Portfolio" blocks
 * (`program_location_map` + `program_property_grid`). Loaded server-
 * side from the program's locations + per-building Launch tokens.
 */
export type ResolvedProgramLocation = {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lng: number | null;
  /** Number of launchable models at this building. */
  modelCount: number;
  /** Public share token of one of this building's Launches (opening
   *  it shows that model + the in-place floor switcher to the rest).
   *  Null when the location has no launchable model yet. */
  launchToken: string | null;
  /** Google Aerial View photorealistic 3D thumbnail
   *  (`aerial_video_uris.IMAGE.landscape`), resolved server-side only
   *  when the render is `active`. The preferred card image — a true
   *  aerial of the building. Null when no render exists yet → the card
   *  falls back to a Static Maps satellite still. */
  aerialThumbnailUrl: string | null;
  /** True when this building is archived — it has launchable scans but
   *  EVERY one's Launch VirtualSpace is `status='archived'` (no live
   *  tour left). Drives the "Archived" badge + greyed-out card + the
   *  show/hide-archived toggle. Resolved server-side. */
  isArchived: boolean;
  /** Unknown stays visible without implying a currently available tour. */
  tourAccessStatus?: "available" | "archived" | "unknown";
};

/**
 * Resolved point-cloud set metadata + COPC URL. Looked up once by
 * the server and passed in via `resolvedPointClouds`. If the set is
 * missing (deleted, not yet processed) the block falls back to a
 * "not available" placeholder.
 */
export type ResolvedPointCloud = {
  id: string;
  copc_url: string | null;
  file_name: string | null;
  floor_labels: Record<string, string>;
  panos: Array<{
    id: string;
    label: string | null;
    imageUrl: string;
    position: { x: number; y: number; z: number };
    orientation: { x: number; y: number; z: number; w: number };
    sortOrder: number;
  }>;
  /**
   * Source format hint for the in-viewer Download button
   * ('e57' / 'laz' / 'las' / 'copc'). Null when unknown.
   */
  source_format?: string | null;
};

/**
 * Pre-resolved data for a `gaussian_splat_tour` block (migration 385).
 * Server resolver looks up the splat URL from the linked
 * `gaussian_splat_tour` deliverable + carries enough metadata for
 * the renderer to decide between "ready", "preparing", and "failed"
 * placeholder states without further round-trips.
 *
 * Phase B (sweep teleport) — `sweep_positions` carries the
 * Matterport sweep XYZs already y-flipped into the gsplat scene
 * frame so the viewer can drop click-to-teleport orbs at each
 * scan position without re-running the coordinate transform on
 * the client.
 *
 * Phase A (hero thumbnail) — `thumbnail_url` is the trained
 * splat's 1200x630 JPEG preview, used as the viewer's poster
 * image during Spark's first-paint window.
 *
 * Phase F (multi-format toggle) — `alt_formats` carries every
 * other ready reconstruction tied to the same source
 * classification, plus the Matterport showcase model id when
 * available. The viewer renders a segmented pill for each
 * ready format so customers can flip between Photoreal /
 * Surface mesh / Point cloud / Showcase from the same source.
 */
export type ResolvedSplatTour = {
  /** Underlying gaussian_splat_tour deliverable id. */
  deliverable_id: string;
  /** Cached splat URL — SPZ (preferred) or .splat fallback. Null
   *  when the conversion is still in flight. */
  splat_url: string | null;
  splat_format: "spz" | "splat" | "ply" | "sog" | null;
  processing_status: "pending" | "processing" | "ready" | "failed";
  processing_error: string | null;
  /** Trained-splat hero image (Phase A). */
  thumbnail_url?: string | null;
  /** Matterport sweep positions in the gsplat scene frame
   *  (post y-flip). Each carries an optional label like
   *  "Living Room" or just the pano index. (Phase B) */
  sweep_positions?: Array<{
    x: number;
    y: number;
    z: number;
    label?: string;
  }>;
  /** Equipment hotspots (Phase D). Resolved server-side from
   *  the source matterport's classification + canonical
   *  detections, filtered by the block's confidence threshold
   *  and optional type allowlist. Already y-flipped for the
   *  gsplat scene frame. Undefined when the block opted out
   *  (show_equipment_hotspots !== true). */
  equipment_hotspots?: Array<
    import("@/lib/virtualspace/splat-tour-equipment-hotspots").EquipmentHotspot
  >;
  /** Alternate format URLs ready alongside the splat (Phase F).
   *  Populated only when the underlying artifacts are in the
   *  ready state — the renderer hides toggle pills for any
   *  format that's not present, so a tour with only the splat
   *  shows no toggle UI at all. */
  alt_formats?: {
    /** 2DGS-Room surface mesh (.glb) — premium tier mesh. */
    surface_mesh_url?: string | null;
    /** VGGT or MASt3R point cloud (.ply). */
    point_cloud_url?: string | null;
    /** Matterport model id for the original showcase iframe. */
    matterport_model_id?: string | null;
  };
};

/**
 * Pre-resolved data for an `aligned_bim_cloud` block (migration 382).
 * Carries the anchor point cloud's COPC URL + every alignment row's
 * 6-DOF transform + cached glb URL so the renderer can mount the
 * Three.js scene without server round-trips.
 *
 * `bookmarks` (migration 384) carries operator-curated public
 * snapshots — customers see these as a tour dropdown for jumping
 * to specific viewpoints.
 */
export type ResolvedAlignmentGroup = {
  id: string;
  name: string;
  copc_url: string | null;
  members: Array<{
    id: string;
    bim_name: string | null;
    glb_url: string | null;
    glb_processing_status: "pending" | "processing" | "ready" | "failed";
    glb_processing_error: string | null;
    position_x: number;
    position_y: number;
    position_z: number;
    rotation_x: number;
    rotation_y: number;
    rotation_z: number;
    scale: number;
  }>;
  bookmarks?: Array<{
    id: string;
    label: string;
    description: string | null;
    state: unknown;
    mode: "overlay" | "compare";
  }>;
};

type RendererProps = {
  blocks: VirtualSpaceBlockRow[];
  brandColor: string | null;
  accentColor: string | null;
  /** Google Maps API key. Pulled from NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
   * on the server and passed in so nothing leaks at bundle time. */
  googleMapsKey: string | null;
  /** Mapbox token for aerial tile fallback. */
  mapboxToken: string | null;
  /** File assets resolved server-side, keyed by id. */
  fileAssets: Record<string, ResolvedFileAsset>;
  /** Scans resolved server-side, keyed by id. */
  scans: Record<string, ResolvedScan>;
  /** Point cloud sets resolved server-side, keyed by point_cloud_set_id. */
  pointClouds: Record<string, ResolvedPointCloud>;
  /** Locations resolved server-side, keyed by id (used by
   *  `location_card` blocks). */
  locations: Record<string, ResolvedLocation>;
  /** Alignment groups resolved server-side, keyed by group id (used by
   *  `aligned_bim_cloud` blocks). Optional — undefined falls back to
   *  the block-level "Preparing alignment…" placeholder. */
  alignmentGroups?: Record<string, ResolvedAlignmentGroup>;
  /** Photoreal Splat Tours resolved server-side, keyed by the
   *  `gaussian_splat_tour` deliverable_id. Optional — undefined
   *  falls back to the block-level "Preparing tour…" placeholder. */
  splatTours?: Record<string, ResolvedSplatTour>;
  programLocations?: ResolvedProgramLocation[];
  /** Building "scan timeline" floors (building_floor_timeline). Empty
   *  except on location-anchored VirtualSpaces. */
  buildingFloors?: BuildingFloor[];
  /** Share token for the parent share_link. Used by
   *  `bim_viewer` / `floor_plan_view` blocks to mint a public
   *  APS viewer token via the share-token-gated route. */
  shareToken: string | null;
  /** When using `VirtualSpacePublicScrollRail`, offset for `data-vs-rail-index` (intro strip is index 0). */
  railSectionBaseIndex?: number;
  /**
   * Launch mode (2026-05-22 follow-up to multi-domain split).
   * When true, the renderer skips the SpaceRow / theater button
   * decoration and renders the single block directly into a
   * full-bleed container. Single-purpose: pair with
   * `<LaunchPublicShell>` for the customer-facing /explore page
   * when block count === 1. Viewer blocks adapt their own
   * sizing + gate via this signal (see `MatterportBlock` for the
   * reference pattern).
   */
  launchMode?: boolean;
  standaloneEnhancements?: boolean;
  /** Operator-only preview must never mint or advertise a public share. */
  privatePreview?: boolean;
  publicShareUrl?: string | null;
};

/**
 * Static lookup of every `sm:col-span-N` Tailwind class for N ∈ 1..12.
 *
 * Tailwind v4 scans source files for class strings. A template-literal
 * `sm:col-span-${n}` is invisible to that scanner — it'd resolve at
 * runtime to a class Tailwind never compiled, and the block would
 * silently render full-width. Listing every value statically keeps
 * the bundle honest and the planner's output renders as designed.
 *
 * On mobile (< sm) every cell stays full width via the `col-span-12`
 * base — a 12-col layout makes no sense at 390px wide.
 */
const SM_COL_SPAN_CLASS: Record<number, string> = {
  1: "sm:col-span-1",
  2: "sm:col-span-2",
  3: "sm:col-span-3",
  4: "sm:col-span-4",
  5: "sm:col-span-5",
  6: "sm:col-span-6",
  7: "sm:col-span-7",
  8: "sm:col-span-8",
  9: "sm:col-span-9",
  10: "sm:col-span-10",
  11: "sm:col-span-11",
  12: "sm:col-span-12",
};

function smColSpanClassFor(colSpan: number): string {
  const clamped = Math.max(1, Math.min(12, Math.floor(colSpan || 12)));
  return SM_COL_SPAN_CLASS[clamped] ?? SM_COL_SPAN_CLASS[12];
}

/**
 * Main entry point — walks the block list in order and groups
 * consecutive blocks into Space rows (one row of the 12-col grid
 * per Space). Each Space row renders as a single `<section>` that
 * carries the rail anchor + (when appropriate) theater eligibility.
 *
 * Pre-353 every block was its own `<section>`; post-353 the same
 * legacy block list still renders identically because every block
 * has `col_span = 12` by default — 12 fills the row exactly, so
 * the planner closes the row and opens a fresh one for the next
 * block. Side-by-side compositions only happen when the operator
 * explicitly sets `col_span < 12`.
 */
/**
 * Launch-mode signal threaded through the renderer via React
 * context (instead of prop-drilling through every block type).
 * Viewer blocks (Matterport, NavVis, point cloud, etc.) read
 * this with `useLaunchMode()` and adapt their sizing + explore-
 * gate behaviour accordingly.
 *
 * Default false — only the top-level renderer enables it when
 * the caller passes `launchMode={true}`. Other surfaces (editor
 * preview, share-dialog block preview) consume the renderer
 * without participating.
 */
// ── Program "Portfolio" blocks (migration 367) ────────────────────
// The map reuses the shared <LocationsMap> (Mapbox GL) that the
// operator /locations + missions-inbox surfaces use; ssr:false keeps
// mapbox-gl out of the server bundle, matching the other viewers.
const ProgramLocationsMap = dynamic(() => import("./locations-map"), {
  ssr: false,
  loading: () => (
    <div
      className="w-full animate-pulse rounded-xl bg-muted/30"
      style={{ height: 520 }}
    />
  ),
});

function escapeProgramHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Window CustomEvent name the portfolio grid dispatches on card
 * hover and the map listens for (via `LocationsMap`'s opt-in
 * `highlightEventName`) — hovering a building's list entry lights
 * up its map pin. A `window` event bridges the two independently-
 * rendered blocks (map + grid) without a shared parent / refactor.
 */
const PORTFOLIO_HIGHLIGHT_EVENT = "vs:portfolio-hover";

/**
 * Second bridge: the grid dispatches this on city-filter click with
 * the ids of that city's locations (null = "All"), and the map's
 * opt-in `focusEventName` listener flies the viewport to frame them.
 */
const PORTFOLIO_FOCUS_EVENT = "vs:portfolio-focus-city";

function dispatchPortfolioHighlight(id: string | null): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(PORTFOLIO_HIGHLIGHT_EVENT, { detail: { id } }),
  );
}

function dispatchPortfolioFocus(ids: string[] | null): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(PORTFOLIO_FOCUS_EVENT, { detail: { ids } }),
  );
}

/**
 * Full-bleed map with one pin per program location. Each pin's popup
 * links into that building's Launch (opening it shows the model + the
 * in-place floor switcher). Resolves live from `programLocations`.
 *
 * Building name + address are title-cased for the popup so a
 * mis-cased import ("780 Third avenue") reads polished. The map's
 * height comes through `heightPx` so the Mapbox canvas fills the
 * configured block height exactly (it previously locked to the
 * shared component's 400px default inside a 560px block, leaving
 * dead space below). `highlightEventName` wires the card-hover →
 * pin-highlight bridge.
 */
function ProgramLocationMapBlock({
  config,
  programLocations,
}: {
  config: ProgramLocationMapConfig;
  programLocations: ResolvedProgramLocation[];
}) {
  const pins = programLocations
    .filter(
      (l): l is ResolvedProgramLocation & { lat: number; lng: number } =>
        typeof l.lat === "number" && typeof l.lng === "number",
    )
    .map((l) => {
      const url = l.launchToken
        ? buildVirtualSpaceShareUrl(l.launchToken, "launch")
        : null;
      const name = escapeProgramHtml(titleCaseProperName(l.name) || "Location");
      const addr = escapeProgramHtml(titleCaseProperName(l.address));
      const cityState = escapeProgramHtml(
        formatCityState(l.city, l.state) ?? "",
      );
      const tours = l.modelCount === 1 ? "1 tour" : `${l.modelCount} tours`;
      const cta = url
        ? `<a href="${url}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:8px;background:${MAP_POPUP.redFill};color:#fff;padding:6px 12px;border-radius:${MAP_POPUP.buttonRadius};font-size:12px;font-weight:600;text-decoration:none">View tour →</a>`
        : `<span style="display:inline-block;margin-top:8px;color:${MAP_POPUP.textSecondary};font-size:12px">Tour coming soon</span>`;
      // The dot keeps the archived marker's grey (markerColor below).
      const archivedTag = l.isArchived
        ? `<div style="margin-top:6px"><span style="display:inline-flex;align-items:center;gap:5px;background:${MAP_POPUP.line};color:${MAP_POPUP.textSecondary};padding:2px 8px;border-radius:999px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em"><span style="width:6px;height:6px;border-radius:50%;background:#a1a1aa"></span>Archived</span></div>`
        : "";
      const popupHtml = `<div style="min-width:200px;font-family:${MAP_POPUP.font}"><div style="font-weight:600;font-size:14px;color:${MAP_POPUP.text}">${name}</div>${
        cityState
          ? `<div style="color:${MAP_POPUP.textSecondary};font-size:12px;margin-top:2px">${cityState}</div>`
          : addr
            ? `<div style="color:${MAP_POPUP.textSecondary};font-size:12px;margin-top:2px">${addr}</div>`
            : ""
      }<div style="color:${MAP_POPUP.textTertiary};font-size:11px;margin-top:4px">${escapeProgramHtml(
        tours,
      )}</div>${archivedTag}${cta}</div>`;
      return {
        id: l.id,
        name: titleCaseProperName(l.name) || "Location",
        lat: l.lat,
        lng: l.lng,
        address: titleCaseProperName(l.address),
        spacesCount: l.modelCount,
        popupHtml,
        // Archived pins read muted grey vs the brand-red active pins so
        // the map stays geographically complete while de-emphasizing
        // archived buildings. (Dynamic hide-on-toggle would need the
        // shared LocationsMap to rebuild markers — left as a follow-up.)
        markerColor: l.isArchived ? "rgb(161,161,170)" : undefined,
      };
    });
  if (pins.length === 0) return null;
  const height = config.height ?? 520;
  return (
    <ProgramLocationsMap
      locations={pins}
      heightPx={height}
      highlightEventName={PORTFOLIO_HIGHLIGHT_EVENT}
      hoverDispatchEventName={PORTFOLIO_HIGHLIGHT_EVENT}
      focusEventName={PORTFOLIO_FOCUS_EVENT}
      pinClickOpensPopupOnly
    />
  );
}

/**
 * One building card — Airbnb-listing-style: a large hero aerial that
 * dominates the card, a glassy "N tours" badge overlaid in the corner,
 * a soft lift + image zoom on hover, and an accent-colored "View tour"
 * CTA. The Link/Embed power-user actions are tucked behind a small
 * share menu so the card reads clean for visitors. Hovering the card
 * lights up the matching map pin (and `highlighted` — driven by the
 * reverse bridge — lights this card up when its pin is hovered).
 */
function ProgramPropertyCard({
  loc,
  compact,
  googleMapsKey,
  highlighted,
}: {
  loc: ResolvedProgramLocation;
  compact: boolean;
  googleMapsKey: string | null;
  highlighted: boolean;
}) {
  const { enabled: enhanced } = usePublicComposition();
  const [copied, setCopied] = useState<null | "link" | "embed">(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const url = buildVirtualSpaceShareUrl(loc.launchToken as string, "launch");
  const name = titleCaseProperName(loc.name) || "Location";
  const address = titleCaseProperName(loc.address);
  const cityState = formatCityState(loc.city, loc.state);
  const tours = loc.modelCount === 1 ? "1 tour" : `${loc.modelCount} tours`;
  const hue = hueFromString(name);

  // Card image, in priority order:
  //   1. Google Aerial View 3D thumbnail (a true aerial of the
  //      building, resolved server-side when a render is active).
  //   2. Google Static Maps satellite still — keyed off the building's
  //      (re-geocoded) lat/lng, with the full address as the geocode
  //      fallback when coords are somehow missing.
  //   3. Hue-gradient + monogram (no key / no coords / both images 404).
  // The aerial thumbnail URLs rotate off Google's CDN after ~15-30 min,
  // so `onError` degrades aerial → static → gradient at view time.
  const aerialUrl = loc.aerialThumbnailUrl;
  const staticUrl = buildSatelliteStaticUrl({
    lat: loc.lat,
    lng: loc.lng,
    address:
      [loc.address, loc.city, loc.state]
        .map((p) => (typeof p === "string" ? p.trim() : ""))
        .filter(Boolean)
        .join(", ") || null,
    mapsKey: googleMapsKey,
    // Wider + taller than the old 480×200 band — the card now leads
    // with a hero image, so request a 16:10-ish tile at 2x for crisp
    // retina rendering.
    width: 640,
    height: 400,
    scale: 2,
    zoom: 18,
  });
  const initialStage: "aerial" | "static" | "fallback" = aerialUrl
    ? "aerial"
    : staticUrl
      ? "static"
      : "fallback";
  const [imgStage, setImgStage] = useState<"aerial" | "static" | "fallback">(
    initialStage,
  );
  const currentSrc =
    imgStage === "aerial" ? aerialUrl : imgStage === "static" ? staticUrl : null;
  const showImg = currentSrc != null;
  const handleImgError = () => {
    // New src will load → reset the fade so the next image fades in
    // cleanly behind the shimmer instead of popping at full opacity.
    setImgLoaded(false);
    setImgStage((s) =>
      s === "aerial" ? (staticUrl ? "static" : "fallback") : "fallback",
    );
  };

  // Fade-in safety net: if the image finished loading BEFORE React
  // attached `onLoad` (cached / fast network / hydration race), the
  // load event never fires and the img would stay stuck at opacity-0
  // behind the shimmer. Sync the loaded flag on mount + whenever the
  // src changes by reading the element's `complete` flag directly.
  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setImgLoaded(true);
  }, [currentSrc]);

  const copy = async (text: string, key: "link" | "embed") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
    } catch {
      // Clipboard blocked (insecure context / permissions) — no-op;
      // the View link still works as the primary action.
    }
  };

  return (
    <div
      onMouseEnter={() => dispatchPortfolioHighlight(loc.id)}
      onMouseLeave={() => dispatchPortfolioHighlight(null)}
      className={cn(
        // STABLE hover target: NO transform here, so the cursor
        // hit-region never moves. The lift lives on the inner card
        // (group-hover) below — when the lift was on this element it
        // shifted under the cursor, flip-flopping :hover and making
        // the image zoom shake. z-index is safe here (no layout move).
        "group relative flex flex-col",
        menuOpen ? "z-30" : "hover:z-10",
      )}
    >
      {/* Visible card frame — border / bg / shadow / lift / accent
          ring all live HERE and animate via `group-hover:` off the
          unmoving outer target, so a single smooth lift fires with no
          flicker. The `highlighted` (pin-hover) treatment is
          React-state-driven, so it never oscillates either. No
          `overflow-hidden` → the share-menu popover still escapes. */}
      <div
        className={cn(
          "flex flex-1 flex-col rounded-xl border vs-border-card bg-card shadow-sm transition-all duration-200",
          "group-hover:-translate-y-1 group-hover:shadow-xl group-hover:shadow-black/20",
          // Archived dims the whole card + keeps a neutral hover
          // border; active cards get the accent hover border.
          loc.isArchived
            ? "opacity-90 group-hover:border-border"
            : "group-hover:border-[color:var(--sv-brand)]/60",
          highlighted
            ? "-translate-y-1 border-[color:var(--sv-brand)] shadow-xl shadow-black/20 ring-2 ring-[color:var(--sv-brand)]"
            : "border-border",
        )}
      >
      {/* Hero image — leads the card at a 16:10 ratio (~60% of the
          card height). Aerial View 3D thumbnail → Static Maps
          satellite → hue-gradient + monogram, degrading via `onError`.
          `overflow-hidden` + `rounded-t-xl` live HERE (not on the card
          root) so the image zoom clips + the top corners round, while
          the share menu below can still escape the card. */}
      <div
        className="relative aspect-[16/10] w-full overflow-hidden rounded-t-xl"
        style={
          showImg
            ? undefined
            : {
                background: `linear-gradient(135deg, hsl(${hue} 38% 24%), hsl(${hue} 30% 13%))`,
              }
        }
      >
        {showImg ? (
          <>
            {/* Shimmer placeholder until the aerial paints. */}
            {!imgLoaded ? (
              <span
                aria-hidden
                className="absolute inset-0 animate-pulse bg-muted"
              />
            ) : null}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              ref={imgRef}
              src={currentSrc as string}
              alt={`Aerial view of ${name}`}
              loading="lazy"
              onLoad={() => setImgLoaded(true)}
              onError={handleImgError}
              className={cn(
                "absolute inset-0 h-full w-full object-cover transition-[transform,opacity,filter] duration-500 ease-out group-hover:scale-[1.05]",
                // Archived → desaturated + dimmed; un-greys on hover to
                // confirm the tour can still be opened. Single ternary
                // chain so opacity classes never conflict.
                !imgLoaded
                  ? "opacity-0"
                  : loc.isArchived
                    ? "opacity-55 grayscale group-hover:opacity-90 group-hover:grayscale-0"
                    : "opacity-100",
              )}
            />
            {/* Bottom scrim so the rounded edge reads cleanly. */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/5"
            />
          </>
        ) : (
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center text-4xl font-semibold text-white/85",
              loc.isArchived && "opacity-60 grayscale",
            )}
          >
            {monogramFor(name)}
          </span>
        )}
        {/* "N tours" badge — glassy pill, top-left corner. */}
        <span className="absolute left-2.5 top-2.5 z-[2] inline-flex items-center rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          {tours}
        </span>
        {/* Status badge — glassy pill, top-right corner. Active = a
            subtle emerald dot; Archived = a muted grey dot + label so
            an archived building reads inactive at a glance. */}
        <span
          className={cn(
            "absolute right-2.5 top-2.5 z-[2] inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold shadow-sm backdrop-blur-sm",
            loc.isArchived ? "text-white/75" : "text-white",
          )}
        >
          <span
            aria-hidden
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              loc.isArchived || loc.tourAccessStatus !== "available" ? "bg-zinc-400" : "bg-emerald-400",
            )}
          />
          {loc.isArchived ? "Archived" : loc.tourAccessStatus === "available" ? "Tour available by link" : "Tour access not checked"}
        </span>
        {/* Stretched link: the whole image opens the tour. */}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${name} tour`}
          className="absolute inset-0 z-[1]"
        />
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "text-sm font-semibold leading-snug hover:underline",
              loc.isArchived ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {name}
          </a>
          {/* Share menu — collapses the Link/Embed power-user actions
              behind a single icon so the card reads clean. The card
              root has no `overflow-hidden`, so this popover escapes. */}
          {enhanced ? <PublicCopyMenu label={`Share ${name}`} actions={[{ label: "Copy tour link", value: url }, { label: "Copy embed code", value: buildLaunchEmbedSnippet(loc.launchToken as string) }]} /> : <div className="relative shrink-0">
            <button
              type="button"
              aria-label="Share or embed this tour"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-full border transition",
                menuOpen
                  ? "border-[color:var(--sv-brand)] text-[color:var(--sv-brand)]"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
              )}
            >
              <Share2 className="h-3.5 w-3.5" />
            </button>
            {menuOpen ? (
              <>
                {/* Click-away backdrop. */}
                <button
                  type="button"
                  aria-hidden
                  tabIndex={-1}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpen(false);
                  }}
                  className="fixed inset-0 z-40 cursor-default"
                />
                <div
                  role="menu"
                  className="absolute right-0 top-9 z-50 w-44 overflow-hidden rounded-lg border border-border bg-popover py-1 text-left shadow-xl"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => copy(url, "link")}
                    className="flex w-full items-center gap-2 px-3 py-2 text-xs text-foreground transition hover:bg-muted"
                  >
                    {copied === "link" ? (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    {copied === "link" ? "Link copied" : "Copy link"}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() =>
                      copy(
                        buildLaunchEmbedSnippet(loc.launchToken as string),
                        "embed",
                      )
                    }
                    className="flex w-full items-center gap-2 px-3 py-2 text-xs text-foreground transition hover:bg-muted"
                  >
                    {copied === "embed" ? (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <Code2 className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    {copied === "embed" ? "Embed copied" : "Copy embed code"}
                  </button>
                </div>
              </>
            ) : null}
          </div>}
        </div>
        {cityState ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            <span>{cityState}</span>
          </div>
        ) : null}
        {!compact && address ? (
          <div className="mt-0.5 text-xs text-muted-foreground/80">
            {address}
          </div>
        ) : null}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold shadow-sm transition",
            enhanced && "min-h-11",
            // Archived stays clickable (the tour may still open) but is
            // muted/secondary so the accent CTA is reserved for active.
            loc.isArchived
              ? "border border-border bg-muted text-muted-foreground hover:text-foreground"
              : "text-white hover:brightness-110",
          )}
          style={loc.isArchived ? undefined : { background: "var(--sv-brand)" }}
        >
          <Eye className="h-3.5 w-3.5" /> View tour
        </a>
      </div>
      </div>
    </div>
  );
}

/**
 * Card grid — one card per building that has a Launch. Adds a city
 * filter chip row + group-by-city sections (when viewing "All") so a
 * 37-building portfolio is scannable. Resolves live from
 * `programLocations`.
 */
function ProgramPropertyGridBlock({
  config,
  programLocations,
  googleMapsKey,
}: {
  config: ProgramPropertyGridConfig;
  programLocations: ResolvedProgramLocation[];
  googleMapsKey: string | null;
}) {
  const { enabled: enhanced } = usePublicComposition();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<PortfolioSort>("name");
  const [visibleCount, setVisibleCount] = useState(24);
  const cards = useMemo(() => programLocations.filter((l) => l.launchToken), [programLocations]);
  const compact = config.card_layout === "compact";
  const [activeCity, setActiveCity] = useState<string>("all");
  // Archived buildings are hidden by default; the toggle reveals them.
  const [showArchived, setShowArchived] = useState(false);
  // Which card is currently "lit" by the reverse hover bridge — set
  // when a map pin is hovered (the map dispatches the shared
  // `vs:portfolio-hover` event) AND when a card hovers itself. Single
  // source of truth so the card↔pin highlight stays in sync.
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const id =
        (e as CustomEvent<{ id: string | null }>).detail?.id ?? null;
      setHighlightedId(id);
    };
    window.addEventListener(
      PORTFOLIO_HIGHLIGHT_EVENT,
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        PORTFOLIO_HIGHLIGHT_EVENT,
        handler as EventListener,
      );
  }, []);

  // Total archived across the whole portfolio (the toggle is a global
  // control, so its "(N)" is the total hidden count, not per-city).
  const archivedCount = useMemo(() => countArchived(cards), [cards]);
  // The visible set drives the city chips, the group-by-city sections,
  // AND the map fly-to focus — so hiding archived drops them from the
  // counts, the sections (an all-archived city disappears), and the
  // focus set together.
  const visibleCards = useMemo(
    () => filterByArchivedVisibility(cards, showArchived),
    [cards, showArchived],
  );
  const searchedCards = useMemo(() => enhanced ? publicPortfolioResults(visibleCards, search, sort) : visibleCards, [enhanced, visibleCards, search, sort]);
  const cityFilters = useMemo(
    () => deriveCityFilters(searchedCards),
    [searchedCards],
  );
  useEffect(() => { setVisibleCount(24); }, [search, sort, activeCity, showArchived]);
  const showFilters = cityFilters.length > 1;

  // If hiding archived empties the active city (its chip disappears),
  // fall back to "All" so we never strand the user on an empty view.
  useEffect(() => {
    if (
      activeCity !== "all" &&
      !cityFilters.some((c) => c.city === activeCity)
    ) {
      setActiveCity("all");
    }
  }, [activeCity, cityFilters]);

  // Pick a city filter AND fly the sibling map to that city's pins.
  // Frames only the VISIBLE pins (respects the archived toggle). The
  // map intersects these ids with its own markers, so passing coord'd
  // or coordless ids is safe.
  const pickCity = (city: string | "all") => {
    setActiveCity(city);
    if (city === "all") {
      dispatchPortfolioFocus(null);
      return;
    }
    const ids = filterByArchivedVisibility(programLocations, showArchived)
      .filter((l) => ((l.city ?? "").trim() || "Other") === city)
      .map((l) => l.id);
    dispatchPortfolioFocus(ids);
  };

  const matchedCards = activeCity === "all" ? searchedCards : searchedCards.filter(location => ((location.city ?? "").trim() || "Other") === activeCity);
  const boundedCards = enhanced ? matchedCards.slice(0, visibleCount) : matchedCards;
  const sections = activeCity === "all" && showFilters && (!enhanced || sort === "city") ? groupLocationsByCity(boundedCards) : [{ city: "", locations: boundedCards }];

  if (cards.length === 0) return null;

  return (
    <div className="flex flex-col gap-5">
      {enhanced ? <div className="flex flex-wrap items-center gap-3">
        <input type="search" aria-label="Search properties" placeholder="Search by property or address" value={search} onChange={event => setSearch(event.target.value)} className="min-h-11 min-w-0 flex-1 rounded-lg border border-border bg-background px-3 text-sm" />
        <label className="flex items-center gap-2 text-sm">Sort <select aria-label="Sort properties" value={sort} onChange={event => setSort(event.target.value as PortfolioSort)} className="min-h-11 rounded-lg border border-border bg-background px-3"><option value="name">Name</option><option value="city">City</option><option value="models">Most tours</option></select></label>
        <p role="status" className="w-full text-xs text-muted-foreground">Showing {boundedCards.length} of {matchedCards.length} properties</p>
      </div> : null}
      {showFilters || archivedCount > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {showFilters ? (
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip
                label={`All (${enhanced ? searchedCards.length : visibleCards.length})`}
                active={activeCity === "all"}
                onClick={() => pickCity("all")}
              />
              {cityFilters.map((c) => (
                <FilterChip
                  key={c.city}
                  label={`${c.city} (${c.count})`}
                  active={activeCity === c.city}
                  onClick={() => pickCity(c.city)}
                />
              ))}
            </div>
          ) : null}
          {archivedCount > 0 ? (
            // Pushed to the right + divided off so it reads as a
            // show/hide CONTROL, not another city filter. An eye /
            // eye-off ghost toggle (no solid pill border, muted text,
            // faint hover bg) is visually distinct from the
            // solid/bordered city chips. On narrow widths it wraps to
            // its own right-aligned line; the divider hides there so a
            // stray vertical rule never floats on a wrapped line.
            <div className="ml-auto flex items-center gap-3">
              {showFilters ? (
                <span aria-hidden className="hidden h-5 w-px bg-border sm:block" />
              ) : null}
              <button
                type="button"
                aria-pressed={showArchived}
                onClick={() => setShowArchived((v) => !v)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition",
                  enhanced && "min-h-11",
                  showArchived
                    ? "text-foreground hover:bg-muted/60"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                )}
              >
                {showArchived ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                {showArchived
                  ? "Hide archived"
                  : `Show archived (${archivedCount})`}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {(enhanced ? matchedCards.length : visibleCards.length) === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
          {enhanced && search.trim() ? "No properties match your search." : archivedCount > 0
            ? "Every building here is archived. Use \u201CShow archived\u201D above to view them."
            : "No buildings to show yet."}
        </div>
      ) : null}

      {sections.map((section, idx) => (
        <div
          key={section.city || `section-${idx}`}
          className={cn(
            "flex flex-col gap-3",
            // Subtle divider between city groups so the headers
            // aren't the only thing breaking the visual flow.
            section.city && idx > 0
              ? "border-t border-border/70 pt-5"
              : undefined,
          )}
        >
          {section.city ? (
            <h3 className="vs-font-brand flex items-center gap-2 text-sm text-foreground">
              <span
                aria-hidden
                className="h-3.5 w-1 rounded-full"
                style={{ background: "var(--sv-brand)" }}
              />
              {section.city}
              <span className="font-normal text-muted-foreground/70">
                {section.locations.length}
              </span>
            </h3>
          ) : null}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {section.locations.map((l) => (
              <ProgramPropertyCard
                key={l.id}
                loc={l}
                compact={compact}
                googleMapsKey={googleMapsKey}
                highlighted={l.id === highlightedId}
              />
            ))}
          </div>
        </div>
      ))}
      {enhanced && boundedCards.length < matchedCards.length ? <button type="button" onClick={() => setVisibleCount(count => count + 24)} className="vs-btn-outline min-h-11 self-center px-6">Show more properties</button> : null}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const { enabled: enhanced } = usePublicComposition();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition",
        enhanced && "min-h-11",
        active
          ? "text-white shadow-sm"
          : "border-border bg-transparent text-muted-foreground hover:border-foreground/30 hover:text-foreground",
      )}
      style={
        active
          ? { background: "var(--sv-brand)", borderColor: "var(--sv-brand)" }
          : undefined
      }
    >
      {label}
    </button>
  );
}

/**
 * One scan's Matterport showcase, embedded in-page (no nav away). Swaps by
 * model id. Falls back to friendly copy when the scan has no synced model
 * yet (the tour still appears in the list with its date + an Open-tour link).
 */
function MatterportShowcaseFrame({
  modelId,
  height,
  title,
  fallback,
}: {
  modelId: string | null;
  height: number;
  title: string;
  fallback?: React.ReactNode;
}) {
  const { enabled: enhanced } = usePublicComposition();
  if (!modelId) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-muted/30 px-4 text-center"
        style={{ height }}
      >
        {fallback ?? (
          <>
            <Clock className="h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              This capture is still being processed — check back soon.
            </p>
          </>
        )}
      </div>
    );
  }
  const sdkKey = process.env.NEXT_PUBLIC_MATTERPORT_SDK_KEY ?? "";
  if (enhanced && sdkKey) return <div className="w-full overflow-hidden rounded-lg border border-border bg-black" style={{ height }}><CompositionViewerMount label="capture"><LaunchViewerSessionProvider><MatterportLaunchViewer key={modelId} modelId={modelId} sdkKey={sdkKey} /></LaunchViewerSessionProvider></CompositionViewerMount></div>;
  return (
    <iframe
      title={title}
      src={`https://my.matterport.com/show/?m=${encodeURIComponent(modelId)}&play=1&qs=1`}
      className="w-full rounded-lg border border-border"
      style={{ height }}
      allow="xr-spatial-tracking; vr; gyroscope; accelerometer; fullscreen"
      loading="lazy"
      allowFullScreen
    />
  );
}

/** Freshness dot — emerald (recent ≤30d) / amber (≤90d) / gray (stale) with a
 *  hover tooltip of the relative age. */
function FreshnessDot({ floor }: { floor: BuildingFloor }) {
  const f = useMemo(() => floorFreshness(floor, Date.now()), [floor]);
  const color =
    f.state === "recent"
      ? "#10b981"
      : f.state === "aging"
        ? "#f59e0b"
        : f.state === "stale"
          ? "#9ca3af"
          : "#d1d5db";
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
      title={`Last scanned: ${f.label}`}
    >
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ background: color }}
        aria-hidden
      />
      {f.label}
    </span>
  );
}

/**
 * Horizontal floor-chip carousel — the 2026-07-09 timeline standard applied
 * to the floor selector: fixed-width chips, arrow paddles, edge fades, the
 * active chip auto-centered. Replaces the desktop left-column list.
 */
function FloorCarouselRail({
  floors,
  activeSpaceId,
  onSelect,
  emptyQuery,
}: {
  floors: BuildingFloor[];
  activeSpaceId: string;
  onSelect: (spaceId: string) => void;
  emptyQuery: string;
}) {
  const { enabled: enhanced } = usePublicComposition();
  const railRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const syncPaddles = () => {
    const rail = railRef.current;
    if (!rail) return;
    setCanLeft(rail.scrollLeft > 8);
    setCanRight(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 8);
  };

  useEffect(() => {
    syncPaddles();
    const rail = railRef.current;
    if (!rail) return;
    const onScroll = () => syncPaddles();
    rail.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      rail.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floors.length]);

  // Center the active chip when the selection changes.
  useEffect(() => {
    const rail = railRef.current;
    const chip = rail?.querySelector<HTMLElement>(
      `[data-floor-chip="${CSS.escape(activeSpaceId)}"]`,
    );
    if (rail && chip) {
      const target =
        chip.offsetLeft - rail.clientWidth / 2 + chip.offsetWidth / 2;
      rail.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    }
  }, [activeSpaceId]);

  if (floors.length === 0) {
    return (
      <p className="px-1 py-2 text-xs text-muted-foreground">
        No floors match “{emptyQuery}”.
      </p>
    );
  }

  const page = (dir: -1 | 1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div
        ref={railRef}
        className="overflow-x-auto py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {/*
          Inner row is at least full-width so few floors center; grows with
          content so many floors still scroll left→right (founder 2026-07-09).
        */}
        <div className="mx-auto flex w-max min-w-full justify-center gap-2">
          {floors.map((f) => (
            <FloorCard
              key={f.spaceId}
              floor={f}
              active={f.spaceId === activeSpaceId}
              onSelect={() => onSelect(f.spaceId)}
            />
          ))}
        </div>
      </div>
      {canLeft && (
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-background to-transparent"
          aria-hidden
        />
      )}
      {canRight && (
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background to-transparent"
          aria-hidden
        />
      )}
      <button
        type="button"
        onClick={() => page(-1)}
        aria-label="Scroll floors left"
        className={cn(
          "absolute left-1 top-1/2 z-20 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 shadow-md backdrop-blur-sm transition-opacity hover:bg-muted",
          enhanced && "min-h-11 min-w-11",
          canLeft ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => page(1)}
        aria-label="Scroll floors right"
        className={cn(
          "absolute right-1 top-1/2 z-20 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 shadow-md backdrop-blur-sm transition-opacity hover:bg-muted",
          enhanced && "min-h-11 min-w-11",
          canRight ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function FloorCard({
  floor,
  active,
  onSelect,
}: {
  floor: BuildingFloor;
  active: boolean;
  onSelect: () => void;
}) {
  const thumb = latestThumbnail(floor);
  const modelId =
    [...floor.scans].reverse().find((s) => s.modelId)?.modelId ?? null;
  const monogram = floor.floorLabel
    .replace(/[^0-9A-Za-z]/g, "")
    .slice(0, 3)
    .toUpperCase();
  return (
    <button
      type="button"
      onClick={onSelect}
      data-floor-chip={floor.spaceId}
      className={cn(
        // Floor selector chip — slightly larger than the first pass so
        // 2–4 floors read as cards, not pills (founder 2026-07-09).
        // One fact per line; long names auto-scroll instead of cramming.
        "flex w-[17.5rem] shrink-0 flex-row items-center gap-3.5 rounded-xl border px-3.5 py-3 text-left transition",
        active
          ? "border-[color:var(--sv-brand)] bg-[color:var(--sv-brand)]/10"
          : "border-border bg-muted/20 opacity-75 hover:border-foreground/30 hover:opacity-100",
      )}
    >
      <span className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full border border-border bg-muted">
        <ScanThumbImage
          primaryUrl={thumb}
          modelId={modelId}
          hueSeed={floor.floorLabel}
          imgClassName="h-full w-full object-cover"
          placeholder={
            <span className="text-[10px] font-semibold text-white/90">
              {monogram}
            </span>
          }
        />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <MarqueeText
          text={floor.floorLabel}
          className="text-lg font-semibold text-foreground"
        />
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <FreshnessDot floor={floor} />
          <span className="text-muted-foreground/50">·</span>
          <span className="whitespace-nowrap">
            {floor.scans.length} scan{floor.scans.length === 1 ? "" : "s"}
          </span>
        </span>
      </span>
    </button>
  );
}

/**
 * Single-line text that auto-scrolls (gentle alternate marquee) when it
 * overflows its container — used for long floor names on selector chips.
 */
function MarqueeText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const outerRef = useRef<HTMLSpanElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const overflow = inner.scrollWidth - outer.clientWidth;
    if (overflow <= 4) return;
    const anim = inner.animate(
      [
        { transform: "translateX(0)" },
        { transform: `translateX(-${overflow}px)` },
      ],
      {
        duration: Math.max(2000, overflow * 60),
        direction: "alternate",
        iterations: Infinity,
        delay: 1200,
        easing: "linear",
      },
    );
    return () => anim.cancel();
  }, [text]);
  return (
    <span
      ref={outerRef}
      className={cn("block min-w-0 overflow-hidden whitespace-nowrap", className)}
    >
      <span ref={innerRef} className="inline-block whitespace-nowrap">
        {text}
      </span>
    </span>
  );
}

/** A small status pill for an archived capture. */
function ScanStatusPill({ status }: { status: string | null }) {
  if (!status) return null;
  const archived = /archiv|inactive/i.test(status);
  if (!archived) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      Archived
    </span>
  );
}

/**
 * Scan / floor thumbnail with cascade: primary URL → Matterport player
 * thumb (from model id) → gradient placeholder. Broken CDN/ORB images
 * must never leave a browser broken-image icon (founder 2026-07-09).
 */
function ScanThumbImage({
  primaryUrl,
  modelId,
  hueSeed,
  className,
  imgClassName,
  placeholder,
}: {
  primaryUrl: string | null | undefined;
  modelId: string | null | undefined;
  hueSeed: string;
  className?: string;
  imgClassName?: string;
  placeholder?: ReactNode;
}) {
  const derived = matterportThumbnailUrl(modelId);
  const candidates = useMemo(() => {
    const out: string[] = [];
    const primary = (primaryUrl ?? "").trim();
    if (primary) out.push(primary);
    if (derived && derived !== primary) out.push(derived);
    return out;
  }, [primaryUrl, derived]);

  const candidateKey = candidates.join("\0");
  const [stage, setStage] = useState(0);
  useEffect(() => {
    setStage(0);
  }, [candidateKey]);

  const src = candidates[stage] ?? null;
  const hue = hueFromString(hueSeed);

  if (!src) {
    return (
      <span
        className={cn(
          "flex h-full w-full items-center justify-center",
          className,
        )}
        style={{
          background: `linear-gradient(135deg, hsl(${hue} 38% 28%), hsl(${(hue + 40) % 360} 38% 20%))`,
        }}
      >
        {placeholder ?? <Clock className="h-5 w-5 text-white/70" />}
      </span>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      key={src}
      src={src}
      alt=""
      loading="lazy"
      className={imgClassName}
      onError={() => {
        setStage((s) => (s + 1 < candidates.length ? s + 1 : candidates.length));
      }}
    />
  );
}

/** Dated list of a floor's captures, newest first; each opens its tour. */
function FloorListMode({ floor }: { floor: BuildingFloor }) {
  const rows = [...floor.scans].reverse(); // newest first
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {rows.map((s) => {
        const url = s.token ? buildVirtualSpaceShareUrl(s.token, "launch") : null;
        const ready = !!s.modelId;
        const unavailable = !ready ? scanUnavailableLabel(s.status) : null;
        const badgeClass =
          unavailable === "Cancelled"
            ? "bg-muted-foreground/80"
            : unavailable === "Scheduled"
              ? "bg-blue-500/90"
              : unavailable === "Archived"
                ? "bg-black/60"
                : "bg-amber-500/90";
        const cardClass = cn(
          "group/cap flex flex-col overflow-hidden rounded-xl border vs-border-soft bg-[var(--surface-2)] shadow-sm transition",
          ready && url
            ? "hover:border-foreground/30 hover:shadow-md"
            : "cursor-default opacity-90",
        );
        const inner = (
          <>
            <span className="relative block aspect-[4/3] overflow-hidden bg-muted">
              <ScanThumbImage
                primaryUrl={s.thumbnailUrl}
                modelId={s.modelId}
                hueSeed={s.deliverableId}
                imgClassName="h-full w-full object-cover transition duration-200 group-hover/cap:scale-[1.04]"
              />
              {unavailable ? (
                <span
                  className={cn(
                    "absolute left-1.5 top-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white",
                    badgeClass,
                  )}
                >
                  {unavailable}
                </span>
              ) : null}
              {ready && url ? (
                <span className="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover/cap:bg-black/35 group-hover/cap:opacity-100">
                  <span className="inline-flex items-center gap-1 rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-black">
                    <Eye className="h-3.5 w-3.5" /> View tour
                  </span>
                </span>
              ) : null}
            </span>
            <span className="border-t vs-border-soft bg-muted/40 px-2.5 py-2.5 text-center text-sm font-semibold tabular-nums text-foreground sm:text-base">
              {formatCaptureDate(s.capturedAt)}
            </span>
          </>
        );
        return ready && url ? (
          <a
            key={s.deliverableId}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cardClass}
          >
            {inner}
          </a>
        ) : (
          <div
            key={s.deliverableId}
            className={cardClass}
            title={
              unavailable
                ? `${unavailable} — no tour available`
                : "Tour not available"
            }
          >
            {inner}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Date scrubber — past → future rail. Dots sit on the real capture-date
 * axis (equal spacing only when dates are missing). Active tick is brand
 * red; Prev/Next flank the rail. The track itself is clickable + draggable
 * (pointer capture) and keyboard-steppable — not visual-only
 * (founder 2026-07-09).
 */
function FloorScrubberMode({
  floor, height, selectedCaptureId, onCaptureChange,
}: {
  floor: BuildingFloor; height: number;
  selectedCaptureId?: string;
  onCaptureChange?: (capture: string, replace?: boolean) => void;
}) {
  const { enabled: enhanced, publicShareUrl } = usePublicComposition();
  const blockId = usePublicCompositionBlockId();
  const sectionId = usePublicCompositionSectionId() || blockId;
  const scans = floor.scans;
  const total = scans.length;
  const [localIdx, setLocalIdx] = useState(() => Math.max(0, total - 1));
  const [dragging, setDragging] = useState(false);
  const selectedIndex = selectedCaptureId ? scans.findIndex(scan => scan.deliverableId === selectedCaptureId) : -1;
  const idx = onCaptureChange ? selectedIndex >= 0 ? selectedIndex : Math.max(0, total - 1) : localIdx;
  const setIdx = (next: number | ((current: number) => number)) => {
    const target = Math.max(0, Math.min(total - 1, typeof next === "function" ? next(idx) : next));
    setLocalIdx(target);
    if (scans[target]) onCaptureChange?.(scans[target].deliverableId, dragging);
  };
  const [linkCopied, setLinkCopied] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const copyResetRef = useRef<number | null>(null);

  // Floor switch → jump to newest capture on that floor.
  useEffect(() => {
    setLocalIdx(Math.max(0, floor.scans.length - 1));
  }, [floor.spaceId, floor.scans.length]);

  useEffect(() => {
    setLinkCopied(false);
    if (copyResetRef.current != null) {
      window.clearTimeout(copyResetRef.current);
      copyResetRef.current = null;
    }
  }, [idx, floor.spaceId]);

  useEffect(
    () => () => {
      if (copyResetRef.current != null) {
        window.clearTimeout(copyResetRef.current);
      }
    },
    [],
  );

  const tickPercents = useMemo(() => {
    if (total <= 1) return [50];
    const times = scans.map((s) => isoToUtcMs(s.capturedAt));
    const dated = times.filter((t): t is number => t != null);
    if (dated.length < 2) {
      return scans.map((_, i) => (i / (total - 1)) * 100);
    }
    const min = Math.min(...dated);
    const max = Math.max(...dated);
    const span = Math.max(1, max - min);
    return times.map((t, i) => {
      if (t == null) return (i / (total - 1)) * 100;
      return ((t - min) / span) * 100;
    });
  }, [scans, total]);

  const seekFromClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el || total <= 0) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setIdx(nearestScanIndexAtPercent(tickPercents, pct));
  };

  const clamped = Math.max(0, Math.min(total - 1, idx));
  const scan: BuildingScan | undefined = scans[clamped];
  if (!scan) return null;
  const ready = readyScanCount(floor);
  const url = scan.token ? buildVirtualSpaceShareUrl(scan.token, "launch") : null;

  const nearest = nearestReadyScanIndex(scans, clamped);
  const fallback = (
    <>
      <Clock className="h-5 w-5 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        The {formatCaptureDate(scan.capturedAt)} capture is still being
        processed.
      </p>
      {nearest !== null && nearest !== clamped ? (
        <button
          type="button"
          onClick={() => setIdx(nearest)}
          className="rounded-md px-3 py-1.5 text-xs font-semibold text-white"
          style={{ background: "var(--sv-brand)" }}
        >
          View the nearest ready tour ·{" "}
          {formatCaptureDate(scans[nearest].capturedAt)}
        </button>
      ) : (
        <p className="text-xs text-muted-foreground">
          {ready} of {total} captures on this floor are ready to tour.
        </p>
      )}
    </>
  );

  return (
    <div className="flex flex-col gap-3">
      <MatterportShowcaseFrame
        modelId={scan.modelId}
        height={height}
        title={`${floor.floorLabel} — ${formatCaptureDate(scan.capturedAt)}`}
        fallback={fallback}
      />
      <div className="flex flex-col gap-3 rounded-xl border vs-border-soft bg-muted/20 px-3 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-base font-semibold tabular-nums text-foreground">
              {formatCaptureDate(scan.capturedAt)}
            </span>
            <ScanStatusPill status={scan.status} />
            {!scan.modelId ? (
              <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-600">
                {scanUnavailableLabel(scan.status)}
              </span>
            ) : null}
          </div>
          <span className="text-xs tabular-nums text-muted-foreground">
            {clamped + 1} of {total}
          </span>
        </div>

        {/*
          3-column grid: paddles | track | paddles. Range labels sit in the
          middle column only so they never collide with chevrons or edge ticks
          (founder 2026-07-09).
        */}
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5">
          <button
            type="button"
            aria-label="Older capture"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={clamped === 0}
            className={cn("inline-flex h-8 w-8 items-center justify-center rounded-full border vs-border-soft bg-background text-muted-foreground transition hover:text-foreground disabled:opacity-30", enhanced && "min-h-11 min-w-11")}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div
            className="relative min-w-0"
            role="slider"
            tabIndex={0}
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={clamped + 1}
            aria-valuetext={formatCaptureDate(scan.capturedAt)}
            aria-label="Capture timeline"
            onKeyDown={(e) => {
              if (
                e.key === "ArrowLeft" ||
                e.key === "ArrowDown" ||
                e.key === "ArrowRight" ||
                e.key === "ArrowUp" ||
                e.key === "Home" ||
                e.key === "End"
              ) {
                // Don't let hub/program carousel steal these while scrubbing.
                e.preventDefault();
                e.stopPropagation();
              }
              if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
                setIdx((i) => Math.max(0, i - 1));
              } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
                setIdx((i) => Math.min(total - 1, i + 1));
              } else if (e.key === "Home") {
                setIdx(0);
              } else if (e.key === "End") {
                setIdx(total - 1);
              }
            }}
          >
            {/*
              Full-height hit target: click/drag anywhere on the rail seeks to
              the nearest capture. Ticks stay pointer-events-none so they don't
              steal the drag; titles still show via the track's own title.
            */}
            <div
              ref={trackRef}
              data-scrubber-track
              title="Drag or click to jump to a capture"
              className={cn(
                "relative mx-1.5 h-10 touch-none select-none rounded-md",
                enhanced && "min-h-11",
                dragging ? "cursor-grabbing" : "cursor-pointer",
              )}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                // Seek first — capture can throw on synthetic/test events.
                seekFromClientX(e.clientX);
                try {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  setDragging(true);
                } catch {
                  /* ignore — click-to-seek still works */
                }
              }}
              onPointerMove={(e) => {
                if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
                seekFromClientX(e.clientX);
              }}
              onPointerUp={(e) => {
                try {
                  if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                  }
                } catch {
                  /* ignore */
                }
                setDragging(false);
              }}
              onPointerCancel={() => setDragging(false)}
            >
              <div
                className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-border transition group-hover:bg-border/80"
                aria-hidden
              />
              <div
                className="absolute top-1/2 left-0 h-1.5 -translate-y-1/2 rounded-full bg-[color:var(--sv-brand)]/50"
                style={{
                  width: total <= 1 ? "0%" : `${tickPercents[clamped] ?? 0}%`,
                }}
                aria-hidden
              />
              {scans.map((s, i) => {
                const left = tickPercents[i] ?? 0;
                const active = i === clamped;
                const hasModel = !!s.modelId;
                return (
                  <span
                    key={s.deliverableId}
                    aria-hidden
                    title={`${formatCaptureDate(s.capturedAt)}${hasModel ? "" : ` · ${scanUnavailableLabel(s.status).toLowerCase()}`}`}
                    className={cn(
                      "pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all",
                      active
                        ? "z-10 h-4 w-4 ring-[3px] ring-[color:var(--sv-brand)]/30"
                        : "z-0 h-2.5 w-2.5",
                      hasModel
                        ? active
                          ? "bg-[color:var(--sv-brand)]"
                          : "bg-[color:var(--sv-brand)]/55"
                        : "bg-muted-foreground/45",
                    )}
                    style={{ left: `${left}%` }}
                  />
                );
              })}
            </div>
          </div>

          <button
            type="button"
            aria-label="Newer capture"
            onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
            disabled={clamped === total - 1}
            className={cn("inline-flex h-8 w-8 items-center justify-center rounded-full border vs-border-soft bg-background text-muted-foreground transition hover:text-foreground disabled:opacity-30", enhanced && "min-h-11 min-w-11")}
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <div aria-hidden className="h-0" />
          <div className="flex min-w-0 items-center gap-2 px-1.5 text-[10px] leading-tight tabular-nums text-muted-foreground">
            <span className="min-w-0 flex-1 truncate">
              {formatCaptureDate(scans[0]?.capturedAt)}
            </span>
            <span className="shrink-0 text-muted-foreground/45">
              past → future
            </span>
            <span className="min-w-0 flex-1 truncate text-right">
              {formatCaptureDate(scans[total - 1]?.capturedAt)}
            </span>
          </div>
          <div aria-hidden className="h-0" />
        </div>

        {url ? (
          <div className="flex flex-wrap items-center justify-center gap-2 pt-0.5">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn("inline-flex items-center gap-1.5 rounded-md border vs-border-soft bg-background/80 px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition hover:border-foreground/25 hover:text-foreground", enhanced && "min-h-11")}
                >
                  <Eye className="h-3.5 w-3.5" />
                  View
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem
                  className={cn("cursor-pointer", enhanced && "min-h-11")}
                  onSelect={() => {
                    window.location.assign(url);
                  }}
                >
                  <Eye className="h-3.5 w-3.5" />
                  <span className="flex-1">Open</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={cn("cursor-pointer", enhanced && "min-h-11")}
                  onSelect={() => {
                    window.open(
                      url,
                      "_blank",
                      "noopener,noreferrer,width=1280,height=800",
                    );
                  }}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="flex-1">Open in new window</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {enhanced ? <PublicCopyMenu key={scan.deliverableId} label="Share capture" actions={[
              { label: "Copy this view", value: () => { const target = new URL(publicShareUrl || window.location.href, window.location.href); target.hash = encodePublicCompositionLocation({ section: sectionId, timeline: blockId, floor: floor.spaceId, view: "scrubber", capture: scan.deliverableId }); return target.href; } },
              { label: "Copy tour link", value: url },
            ]} /> :             <button
              type="button"
              onClick={() => {
                void (async () => {
                  try {
                    await navigator.clipboard.writeText(url);
                    setLinkCopied(true);
                    if (copyResetRef.current != null) {
                      window.clearTimeout(copyResetRef.current);
                    }
                    copyResetRef.current = window.setTimeout(() => {
                      setLinkCopied(false);
                      copyResetRef.current = null;
                    }, 1600);
                  } catch {
                    /* clipboard blocked */
                  }
                })();
              }}
              title={url}
              className={cn("inline-flex items-center gap-1.5 rounded-md border vs-border-soft bg-background/80 px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition hover:border-foreground/25 hover:text-foreground", enhanced && "min-h-11")}
            >
              {linkCopied ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {linkCopied ? "Copied" : "Copy link"}
            </button>}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Before/after compare — two captures of a floor, side by side. */
function FloorCompareMode({ floor, height, beforeId, afterId, onPairChange }: {
  floor: BuildingFloor; height: number; beforeId?: string; afterId?: string;
  onPairChange?: (before: string, after: string) => void;
}) {
  const { enabled: enhanced } = usePublicComposition();
  const scans = floor.scans;
  const [localA, setLocalA] = useState(0);
  const [localB, setLocalB] = useState(scans.length - 1);
  const foundA = scans.findIndex(scan => scan.deliverableId === beforeId);
  const foundB = scans.findIndex(scan => scan.deliverableId === afterId);
  const requestedA = Math.max(0, foundA);
  const requestedB = foundB >= 0 ? foundB : scans.length - 1;
  // Keep select labels consistent with the chronological pair rendered below.
  const aIdx = onPairChange ? Math.min(requestedA, requestedB) : localA;
  const bIdx = onPairChange ? Math.max(requestedA, requestedB) : localB;
  const emitPair = (a: number, b: number) => {
    const before = scans[Math.min(a, b)];
    const after = scans[Math.max(a, b)];
    if (before && after) onPairChange?.(before.deliverableId, after.deliverableId);
  };
  const setAIdx = (index: number) => { setLocalA(index); emitPair(index, bIdx); };
  const setBIdx = (index: number) => { setLocalB(index); emitPair(aIdx, index); };
  const pair = pickComparePair(scans, aIdx, bIdx);
  if (!pair) return null;
  const compareHeight = Math.round(height * 0.85);
  const picker = (
    value: number,
    onChange: (n: number) => void,
    label: string,
  ) => (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">{label}</span>
      <select
        value={Math.max(0, Math.min(scans.length - 1, value))}
        onChange={(e) => onChange(Number(e.target.value))}
        className={cn("rounded-md border border-border bg-background px-2 py-1 text-xs", enhanced && "min-h-11")}
      >
        {scans.map((s, i) => (
          <option key={s.deliverableId} value={i}>
            {formatCaptureDate(s.capturedAt)}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          {picker(aIdx, setAIdx, "Before")}
          <MatterportShowcaseFrame
            modelId={pair.before.modelId}
            height={compareHeight}
            title={`${floor.floorLabel} — before`}
          />
        </div>
        <div className="flex flex-col gap-2">
          {picker(bIdx, setBIdx, "After")}
          <MatterportShowcaseFrame
            modelId={pair.after.modelId}
            height={compareHeight}
            title={`${floor.floorLabel} — after`}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Building "scan timeline" — one building's floors (left selector) + the
 * active floor's scans over time on the right, in list / timeline-scrubber /
 * before-after compare modes. Resolves live from `buildingFloors` (the
 * location-anchored resolver in public-entity-loader). Sub-views are keyed
 * by floor id so switching floors resets the scrubber/compare cleanly.
 */
export function BuildingFloorTimelineBlock({
  config,
  buildingFloors,
}: {
  config: BuildingFloorTimelineConfig;
  buildingFloors: BuildingFloor[];
}) {
  const floors = buildingFloors;
  const { enabled: enhanced } = usePublicComposition();
  const blockId = usePublicCompositionBlockId();
  const sectionId = usePublicCompositionSectionId() || blockId;
  const location = usePublicCompositionLocation(enhanced);
  const timeline = location.timeline === blockId ? location : {};
  const [localFloorId, setLocalFloorId] = useState<string | null>(
    floors[0]?.spaceId ?? null,
  );
  const [localMode, setLocalMode] = useState<"list" | "scrubber" | "compare">(config.default_mode ?? "list");
  const activeFloorId = enhanced ? timeline.floor : localFloorId;
  const mode = enhanced ? timeline.view ?? config.default_mode ?? "list" : localMode;
  const setActiveFloorId = (floor: string) => {
    if (enhanced) setPublicCompositionLocation({ section: sectionId, timeline: blockId, floor, capture: undefined, before: undefined, after: undefined, view: mode });
    else setLocalFloorId(floor);
  };
  const setMode = (view: "list" | "scrubber" | "compare") => {
    if (enhanced) setPublicCompositionLocation({ section: sectionId, timeline: blockId, floor: floors.find(floor => floor.spaceId === activeFloorId)?.spaceId ?? floors[0]?.spaceId, view });
    else setLocalMode(view);
  };
  const [filterQuery, setFilterQuery] = useState("");
  const [sortMode, setSortMode] = useState<FloorSortMode>("floor");
  const height = config.height && config.height > 0 ? config.height : 640;
  const stats = useMemo(() => buildingTimelineStats(floors), [floors]);
  const visibleFloors = useMemo(
    () => sortFloors(filterFloors(floors, filterQuery), sortMode),
    [floors, filterQuery, sortMode],
  );

  if (floors.length === 0) return null;
  const activeFloor =
    floors.find((f) => f.spaceId === activeFloorId) ?? floors[0];
  const ts = isTimeSeries(activeFloor);
  const effectiveMode = ts ? mode : "list";
  const showToolbar = floors.length > 4;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-1 text-center">
        <h3 className="vs-font-brand text-base tracking-tight">
          <span className="text-black dark:text-white">Space</span>
          <span className="text-[var(--vs-red)]">Lapse</span>
        </h3>
        <span className="text-xs text-muted-foreground">
          {stats.floorCount} floor{stats.floorCount === 1 ? "" : "s"} ·{" "}
          {stats.scanCount} scan{stats.scanCount === 1 ? "" : "s"}
          {stats.earliest
            ? ` · ${formatCaptureDate(stats.earliest)} – ${formatCaptureDate(stats.latest)}`
            : ""}
        </span>
      </div>

      <div className="flex flex-col gap-4">
        {/* Floor selector — horizontal carousel (2026-07-09 timeline
            standard: rails scroll sideways, one focused panel below).
            Filter + sort toolbar stays inline for tall buildings. */}
        <div className="flex min-w-0 flex-col items-center gap-2">
          {showToolbar ? (
            <div className="flex w-full max-w-md items-center justify-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  placeholder="Filter floors…"
                  aria-label="Filter floors"
                  className={cn(enhanced && "min-h-11", "w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-xs outline-none focus:border-foreground/40")}
                />
              </div>
              <button
                type="button"
                onClick={() =>
                  setSortMode((m) => (m === "floor" ? "recent" : "floor"))
                }
                title="Toggle floor order"
                className="shrink-0 rounded-md border border-border px-2 py-1.5 text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
              >
                {sortMode === "floor" ? "By floor" : "Recent first"}
              </button>
            </div>
          ) : null}
          <div className="w-full min-w-0">
            <FloorCarouselRail
              floors={visibleFloors}
              activeSpaceId={activeFloor.spaceId}
              onSelect={setActiveFloorId}
              emptyQuery={filterQuery}
            />
          </div>
        </div>

        <div
          className="h-px w-full bg-border"
          role="separator"
          aria-hidden
        />

        {/* Active floor */}
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="vs-heading text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
              {activeFloor.floorLabel}
            </h4>
            <FreshnessDot floor={activeFloor} />
            {ts ? (
              <div
                className="ml-auto inline-flex items-center gap-0.5 rounded-lg border vs-border-soft bg-[var(--surface-2)] p-0.5"
                role="tablist"
                aria-label="Floor view"
              >
                {(
                  [
                    ["list", "List"],
                    ["scrubber", "Timeline"],
                    ["compare", "Compare"],
                  ] as const
                ).map(([m, label]) => {
                  const selected = effectiveMode === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      aria-pressed={selected}
                      tabIndex={enhanced ? selected ? 0 : -1 : undefined}
                      onKeyDown={enhanced ? event => {
                        const tabs = ["list", "scrubber", "compare"] as const;
                        const index = tabs.indexOf(m);
                        const next = event.key === "Home" ? 0 : event.key === "End" ? 2 : event.key === "ArrowRight" ? (index + 1) % 3 : event.key === "ArrowLeft" ? (index + 2) % 3 : null;
                        if (next == null) return;
                        event.preventDefault();
                        setMode(tabs[next]);
                        const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
                        buttons?.[next]?.focus();
                      } : undefined}
                      onClick={() => setMode(m)}
                      className={cn(
                        "vs-heading min-h-9 rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition",
                        enhanced && "min-h-11",
                        selected
                          ? "bg-[color:var(--vs-red)] text-white shadow-sm ring-1 ring-[color:var(--vs-red)]/40"
                          : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          {effectiveMode === "list" ? (
            <FloorListMode key={activeFloor.spaceId} floor={activeFloor} />
          ) : effectiveMode === "scrubber" ? (
            <FloorScrubberMode
              key={activeFloor.spaceId}
              floor={activeFloor}
              height={height}
              selectedCaptureId={enhanced ? timeline.capture : undefined}
              onCaptureChange={enhanced ? (capture, replace) => setPublicCompositionLocation({ section: sectionId, timeline: blockId, floor: activeFloor.spaceId, view: "scrubber", capture }, replace) : undefined}
            />
          ) : (
            <FloorCompareMode
              key={activeFloor.spaceId}
              floor={activeFloor}
              height={height}
              beforeId={enhanced ? timeline.before : undefined}
              afterId={enhanced ? timeline.after : undefined}
              onPairChange={enhanced ? (before, after) => setPublicCompositionLocation({ section: sectionId, timeline: blockId, floor: activeFloor.spaceId, view: "compare", before, after }) : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const LaunchModeContext = createContext(false);

function LaunchModeProvider({ children }: { children: React.ReactNode }) {
  return (
    <LaunchModeContext.Provider value={true}>
      {children}
    </LaunchModeContext.Provider>
  );
}

function useLaunchMode(): boolean {
  return useContext(LaunchModeContext);
}

export default function VirtualSpacePublicRenderer(props: RendererProps) {
  return <StandaloneCompositionProvider enabled={props.standaloneEnhancements} publicShareUrl={props.publicShareUrl} privatePreview={props.privatePreview}>
    <VirtualSpacePublicRendererBody {...props} />
  </StandaloneCompositionProvider>;
}

function VirtualSpacePublicRendererBody({
  blocks,
  brandColor,
  accentColor,
  googleMapsKey,
  mapboxToken,
  fileAssets,
  scans,
  pointClouds,
  locations,
  alignmentGroups,
  splatTours,
  programLocations,
  buildingFloors,
  shareToken,
  railSectionBaseIndex = 0,
  launchMode = false,
}: RendererProps) {
  // View beacon (2026-06-01). View-counting moved OFF the server
  // render so the public VS render can be served as a pure cache hit
  // / statically (the embed-scale fix — see public-share-cache.ts).
  // Fire-and-forget once per mount; the ref guards React StrictMode's
  // dev double-invoke. Pure analytics — never blocks or breaks render.
  const viewBeaconFired = useRef(false);
  useEffect(() => {
    if (!shareToken || viewBeaconFired.current) return;
    viewBeaconFired.current = true;
    const url = `/api/share/${encodeURIComponent(shareToken)}/view`;
    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        navigator.sendBeacon(url);
      } else {
        void fetch(url, { method: "POST", keepalive: true }).catch(() => {});
      }
    } catch {
      /* analytics only */
    }
  }, [shareToken]);

  // Launch shortcut: render the single block full-bleed without
  // any SpaceRow / theater button wrapping. Pairs with
  // <LaunchPublicShell> on the customer-facing /explore page when
  // block count === 1. Viewer blocks (Matterport, NavVis, etc.)
  // adapt their own sizing + gate via the LaunchModeProvider
  // context below — block-by-block updates kept opt-in so other
  // surfaces can use this renderer without participating.
  const customerBlocks = omitEmptyPublicFileListBlocks(blocks, fileAssets);

  if (launchMode && customerBlocks.length === 1) {
    const block = customerBlocks[0];
    return (
      <LaunchModeProvider>
        <div
          className="vs-forward-stack w-full h-full"
          style={
            {
              "--sv-brand": brandColor ?? "#c20000",
              "--sv-accent": accentColor ?? brandColor ?? "#c20000",
            } as React.CSSProperties
          }
        >
          <BlockRenderer
            block={block}
            googleMapsKey={googleMapsKey}
            mapboxToken={mapboxToken}
            fileAssets={fileAssets}
            scans={scans}
            pointClouds={pointClouds}
            locations={locations}
            alignmentGroups={alignmentGroups}
            splatTours={splatTours}
            programLocations={programLocations} buildingFloors={buildingFloors}
            shareToken={shareToken}
          />
        </div>
      </LaunchModeProvider>
    );
  }

  const spaceRows = planSpaceRows(customerBlocks);
  return (
    <div
      className="vs-forward-stack space-y-8 sm:space-y-10"
      style={
        {
          // Expose brand tokens as CSS vars so individual block
          // renderers can use them without prop drilling.
          "--sv-brand": brandColor ?? "#c20000",
          "--sv-accent": accentColor ?? brandColor ?? "#c20000",
        } as React.CSSProperties
      }
    >
      {spaceRows.map((spaceRow) => (
        <SpaceRowSection
          key={spaceRow.anchorBlock.id}
          spaceRow={spaceRow}
          totalSpaceRows={spaceRows.length}
          railSectionBaseIndex={railSectionBaseIndex}
          googleMapsKey={googleMapsKey}
          mapboxToken={mapboxToken}
          fileAssets={fileAssets}
          scans={scans}
          pointClouds={pointClouds}
          locations={locations}
          alignmentGroups={alignmentGroups}
          splatTours={splatTours}
          programLocations={programLocations}
          buildingFloors={buildingFloors}
          shareToken={shareToken}
        />
      ))}
    </div>
  );
}

/**
 * One Space row — wrapping `<section>` carrying the rail anchor +
 * conditional theater eligibility, body of N blocks laid out on a
 * 12-col CSS Grid.
 *
 * Theater eligibility is per-Space-row: a row qualifies iff it
 * contains exactly one block AND that block's type is in the
 * existing `PUBLIC_VIRTUAL_SPACE_THEATER_BLOCK_TYPES` set. Split
 * (multi-block) rows never enter theater because "this is the
 * show" doesn't apply when there are multiple things — see
 * `isSpaceRowTheaterEligible`.
 */
function SpaceRowSection({
  spaceRow,
  totalSpaceRows,
  railSectionBaseIndex,
  googleMapsKey,
  mapboxToken,
  fileAssets,
  scans,
  pointClouds,
  locations,
  alignmentGroups,
  splatTours,
  programLocations,
  buildingFloors,
  shareToken,
}: {
  spaceRow: SpaceRow;
  totalSpaceRows: number;
  railSectionBaseIndex: number;
  googleMapsKey: string | null;
  mapboxToken: string | null;
  fileAssets: Record<string, ResolvedFileAsset>;
  scans: Record<string, ResolvedScan>;
  pointClouds: Record<string, ResolvedPointCloud>;
  locations: Record<string, ResolvedLocation>;
  alignmentGroups?: Record<string, ResolvedAlignmentGroup>;
  splatTours?: Record<string, ResolvedSplatTour>;
  programLocations?: ResolvedProgramLocation[];
  buildingFloors?: BuildingFloor[];
  shareToken: string | null;
}) {
  const { enabled: enhanced } = usePublicComposition();
  const { anchorBlock, blocks: rowBlocks, spaceIndex } = spaceRow;
  const theaterEligible = isSpaceRowTheaterEligible(spaceRow);
  // Solo-block media Space rows render at the wider 1760px ceiling
  // on first paint without waiting for the IntersectionObserver to
  // pick a winner — when the row has exactly one block AND that
  // block IS the entire space, there's no other block competing for
  // the active slot. `theaterEligible` already implies length === 1
  // (the predicate enforces it), but spelling it out at the call
  // site makes the intent obvious next to the data-attribute below
  // and the matching `globals.css` rule.
  const isSoloMediaSpace = rowBlocks.length === 1 && theaterEligible;
  const aria = publicVirtualSpaceBlockSectionAriaLabel(
    anchorBlock,
    spaceIndex + 1,
    totalSpaceRows,
  );

  // The `<section>` itself is the Space row. Theater eligibility
  // attaches HERE (not on individual blocks) so a single full-width
  // media block grows under the existing `vs-theater-frame` CSS,
  // and split rows never enter theater. `vs-non-theater` is the
  // counterpart hook used by the dim-rule for non-media siblings;
  // we attach it to non-eligible rows so the dim still fires.
  const baseAttrs = {
    id: `vs-section-${anchorBlock.id}`,
    tabIndex: enhanced ? -1 : undefined,
    "data-vs-rail-index": railSectionBaseIndex + spaceIndex,
    "data-vs-rail-block-id": anchorBlock.id,
    "data-vs-space": spaceIndex,
    "aria-label": aria,
  } as const;

  if (theaterEligible) {
    // Single eligible media block: the `<section>` IS the
    // theater frame. The eligibility attrs sit here (not on an
    // inner wrapper) so the controller observes Space rows, not
    // individual blocks — which is the exact behaviour split rows
    // want too (they go down the non-theater path below and never
    // attach the eligibility attr at all).
    //
    // `data-vs-theater-default="active"` is independent of the
    // controller's runtime `data-vs-theater` attribute. Both can
    // be "active" simultaneously when this solo row IS the
    // centered one — the CSS `max-width` is the same for both
    // selectors, so there's no fight. When the controller picks a
    // DIFFERENT row as centered, this row keeps its default-active
    // attr and stays wide; the dim-non-media-siblings effect
    // continues to key off the controller-managed attributes only.
    return (
      <section
        {...baseAttrs}
        className={cn(
          "scroll-mt-12 vs-theater-frame group/theater relative",
          "vs-space-row mx-auto w-full",
        )}
        data-vs-theater-eligible="true"
        data-vs-theater-key={anchorBlock.id}
        data-vs-theater-default={isSoloMediaSpace ? "active" : undefined}
      >
        <PublicCompositionSectionProvider value={anchorBlock.id}><SpaceRowGrid blocks={rowBlocks}>
          {rowBlocks.map((b) => (
            <SpaceRowCell key={b.id} block={b}>
              <BlockRenderer
                block={b}
                googleMapsKey={googleMapsKey}
                mapboxToken={mapboxToken}
                fileAssets={fileAssets}
                scans={scans}
                pointClouds={pointClouds}
                locations={locations}
                alignmentGroups={alignmentGroups}
                splatTours={splatTours}
                programLocations={programLocations} buildingFloors={buildingFloors}
                shareToken={shareToken}
              />
            </SpaceRowCell>
          ))}
        </SpaceRowGrid></PublicCompositionSectionProvider>
        <VirtualSpaceTheaterToggle blockKey={anchorBlock.id} label={aria} />
      </section>
    );
  }

  // Non-theater Space row — text / split / spacer-only. `vs-non-
  // theater` opts the row into the global "fade me when something
  // else is in theater" rule so an active media surface elsewhere
  // reads as the dominant layer.
  return (
    <section
      {...baseAttrs}
      className={cn(
        "scroll-mt-12 vs-non-theater",
        "vs-space-row mx-auto w-full max-w-[min(1440px,100%)]",
      )}
    >
      <PublicCompositionSectionProvider value={anchorBlock.id}><SpaceRowGrid blocks={rowBlocks}>
        {rowBlocks.map((b) => (
          <SpaceRowCell key={b.id} block={b}>
            <BlockRenderer
              block={b}
              googleMapsKey={googleMapsKey}
              mapboxToken={mapboxToken}
              fileAssets={fileAssets}
              scans={scans}
              pointClouds={pointClouds}
              locations={locations}
              alignmentGroups={alignmentGroups}
              splatTours={splatTours}
              programLocations={programLocations} buildingFloors={buildingFloors}
              shareToken={shareToken}
            />
          </SpaceRowCell>
        ))}
      </SpaceRowGrid></PublicCompositionSectionProvider>
    </section>
  );
}

/**
 * 12-col CSS Grid wrapper. Single-block full-width Space rows
 * intentionally skip the grid wrapper to keep DOM noise low + let
 * legacy media frames sit directly inside the `<section>` — the
 * visual is identical (one col-span-12 cell), and existing
 * single-block theater rows render with one less wrapper div.
 */
function SpaceRowGrid({
  blocks,
  children,
}: {
  blocks: VirtualSpaceBlockRow[];
  children: React.ReactNode;
}) {
  if (blocks.length === 1) return <>{children}</>;
  return (
    <div className="grid grid-cols-12 gap-4 sm:gap-6">{children}</div>
  );
}

/**
 * One cell in the Space-row grid. Applies the right text/media
 * width tier so per-block renderers don't have to duplicate it,
 * and falls back to `col-span-12` on mobile via the static
 * `SM_COL_SPAN_CLASS` lookup so Tailwind's source scanner sees
 * every span name at build time.
 *
 * For single-block Space rows we skip the grid wrapper entirely
 * (see `SpaceRowGrid` above) so the cell is rendered as a bare
 * inner div — the section already carries the width tier in that
 * shape.
 */
function SpaceRowCell({
  block,
  children,
}: {
  block: VirtualSpaceBlockRow;
  children: React.ReactNode;
}) {
  const isMedia = isTheaterEligibleBlock(block);
  const isSpacer = block.block_type === "spacer";
  /*
    file_list blocks frequently embed an inline AutodeskViewer
    (BIM/CAD federations) on top of the file table, so they need
    the full media-column width — clamping them to the 880 px
    text tier squashed the embedded 3D viewer to ~660 px on a
    1440 viewport. Same for scan_list (deliverable galleries)
    which renders thumbnail grids that breathe at the wider
    width. They're not theater-eligible (no "the show" treatment),
    just wide.
  */
  const isWideCollection =
    block.block_type === "file_list" ||
    block.block_type === "scan_list" ||
    // Portfolio surfaces: the property card grid + the locations map
    // want the full media-column width, not the 880px reading tier.
    // Clamped to text width, the 3-up card grid collapsed to ~280px
    // cards floating in a narrow column with huge side margins on
    // desktop (the "feels empty / long scroll" report). At the wider
    // tier the hero cards + map fill the page.
    block.block_type === "program_property_grid" ||
    block.block_type === "program_location_map" ||
    // Building scan-timeline: floor selector + viewer want full width.
    block.block_type === "building_floor_timeline";
  const tierClass = isSpacer
    ? ""
    : isMedia || isWideCollection
      ? ""
      : PUBLIC_VIRTUAL_SPACE_TEXT_TIER_CLASS;
  return (
    <div
      className={cn("col-span-12 min-w-0", smColSpanClassFor(block.col_span))}
    >
      <div className={tierClass || undefined}>{children}</div>
    </div>
  );
}

function BlockRenderer(props: Parameters<typeof BlockBody>[0]) {
  return <PublicCompositionBlockProvider value={props.block.id}><BlockBody {...props} /></PublicCompositionBlockProvider>;
}

function BlockBody({
  block,
  googleMapsKey,
  mapboxToken,
  fileAssets,
  scans,
  pointClouds,
  locations,
  alignmentGroups,
  splatTours,
  programLocations,
  buildingFloors,
  shareToken,
}: {
  block: VirtualSpaceBlockRow;
  googleMapsKey: string | null;
  mapboxToken: string | null;
  fileAssets: Record<string, ResolvedFileAsset>;
  scans: Record<string, ResolvedScan>;
  pointClouds: Record<string, ResolvedPointCloud>;
  locations: Record<string, ResolvedLocation>;
  alignmentGroups?: Record<string, ResolvedAlignmentGroup>;
  splatTours?: Record<string, ResolvedSplatTour>;
  programLocations?: ResolvedProgramLocation[];
  buildingFloors?: BuildingFloor[];
  shareToken: string | null;
}) {
  switch (block.block_type) {
    case "heading":
      return <HeadingBlock config={block.config as HeadingConfig} />;
    case "rich_text":
      return <RichTextBlock config={block.config as RichTextConfig} />;
    case "spacer":
      return <SpacerBlock config={block.config as SpacerConfig} />;
    case "hero_image":
      return <HeroImageBlock config={block.config as HeroImageConfig} />;
    case "hero_aerial":
      return (
        <HeroAerialBlock
          config={block.config as HeroAerialConfig}
          locations={locations}
        />
      );
    case "point_cloud":
      return (
        <PointCloudBlock
          config={block.config as PointCloudConfig}
          resolved={pointClouds[(block.config as PointCloudConfig).point_cloud_set_id ?? ""] ?? null}
        />
      );
    case "matterport":
      return (
        <MultiDeliverableViewerTabs
          config={block.config as MatterportConfig}
          scans={scans}
          candidateTypes={["matterport_scan"]}
          renderOne={(deliverableId) => (
            <MatterportBlock
              config={
                {
                  ...(block.config as MatterportConfig),
                  deliverable_id: deliverableId,
                  scan_id: deliverableId,
                } as MatterportConfig
              }
              scans={scans}
            />
          )}
        />
      );
    case "google_map":
      return <GoogleMapBlock config={block.config as GoogleMapConfig} apiKey={googleMapsKey} />;
    case "street_view":
      return <StreetViewBlock config={block.config as StreetViewConfig} apiKey={googleMapsKey} />;
    case "google_3d_tiles":
      return <Google3DTilesBlock config={block.config as Google3DTilesConfig} apiKey={googleMapsKey} />;
    case "aerial_image":
      return (
        <AerialImageBlock
          config={block.config as AerialImageConfig}
          googleMapsKey={googleMapsKey}
          mapboxToken={mapboxToken}
        />
      );
    case "file_list":
      return (
        <FileListBlock
          config={block.config as FileListConfig}
          assets={fileAssets}
          shareToken={shareToken}
        />
      );
    case "scan_list":
      return <ScanListBlock config={block.config as ScanListConfig} scans={scans} />;
    case "contact_card":
      return <ContactCardBlock config={block.config as ContactCardConfig} />;
    case "video":
      return <VideoBlock config={block.config as VideoConfig} />;
    case "calendar_booking":
      return <CalendarBookingBlock config={block.config as CalendarBookingConfig} />;
    case "restaurant_booking":
      return <RestaurantBookingBlock config={block.config as RestaurantBookingConfig} />;
    case "external_embed":
      return <ExternalEmbedBlock config={block.config as ExternalEmbedConfig} />;
    case "link_buttons":
      return <LinkButtonsBlock config={block.config as LinkButtonsConfig} />;
    case "location_card":
      return (
        <LocationCardBlock
          config={block.config as LocationCardConfig}
          locations={locations}
          scans={scans}
          googleMapsKey={googleMapsKey}
        />
      );
    case "bim_viewer":
      return (
        <MultiDeliverableViewerTabs
          config={block.config as BimViewerConfig}
          scans={scans}
          candidateTypes={["bim_model"]}
          renderOne={(deliverableId) => (
            <BimViewerBlock
              config={
                {
                  ...(block.config as BimViewerConfig),
                  deliverable_id: deliverableId,
                } as BimViewerConfig
              }
              scans={scans}
              fileAssets={fileAssets}
              shareToken={shareToken}
            />
          )}
        />
      );
    case "navvis_embed":
      return (
        <MultiDeliverableViewerTabs
          config={block.config as NavvisEmbedConfig}
          scans={scans}
          candidateTypes={["navvis_scan"]}
          renderOne={(deliverableId) => (
            <NavvisEmbedBlock
              config={
                {
                  ...(block.config as NavvisEmbedConfig),
                  deliverable_id: deliverableId,
                } as NavvisEmbedConfig
              }
              scans={scans}
              pointClouds={pointClouds}
              shareToken={shareToken}
            />
          )}
        />
      );
    case "lidar_viewer":
      return (
        <MultiDeliverableViewerTabs
          config={block.config as LidarViewerConfig}
          scans={scans}
          candidateTypes={["leica_scan", "faro_scan", "navvis_scan"]}
          renderOne={(deliverableId) => (
            <LidarViewerBlock
              config={
                {
                  ...(block.config as LidarViewerConfig),
                  deliverable_id: deliverableId,
                } as LidarViewerConfig
              }
              scans={scans}
              pointClouds={pointClouds}
              shareToken={shareToken}
            />
          )}
        />
      );
    case "floor_plan_view":
      return (
        <MultiDeliverableViewerTabs
          config={block.config as FloorPlanViewConfig}
          scans={scans}
          candidateTypes={["floor_plan"]}
          renderOne={(deliverableId) => (
            <FloorPlanViewBlock
              config={
                {
                  ...(block.config as FloorPlanViewConfig),
                  deliverable_id: deliverableId,
                } as FloorPlanViewConfig
              }
              scans={scans}
              fileAssets={fileAssets}
              shareToken={shareToken}
            />
          )}
        />
      );
    case "drone_video":
      return (
        <MultiDeliverableViewerTabs
          config={block.config as DroneVideoConfig}
          scans={scans}
          candidateTypes={["drone_scan"]}
          renderOne={(deliverableId) => (
            <DroneVideoBlock
              config={
                {
                  ...(block.config as DroneVideoConfig),
                  deliverable_id: deliverableId,
                } as DroneVideoConfig
              }
              scans={scans}
            />
          )}
        />
      );
    case "space_survey_link":
      return (
        <MultiDeliverableViewerTabs
          config={block.config as SpaceSurveyLinkConfig}
          scans={scans}
          candidateTypes={["space_survey"]}
          renderOne={(deliverableId) => (
            <SpaceSurveyLinkBlock
              config={
                {
                  ...(block.config as SpaceSurveyLinkConfig),
                  deliverable_id: deliverableId,
                } as SpaceSurveyLinkConfig
              }
              scans={scans}
            />
          )}
        />
      );
    case "aligned_bim_cloud":
      return (
        <AlignedBimCloudBlock
          config={block.config as AlignedBimCloudConfig}
          alignmentGroups={alignmentGroups}
        />
      );
    case "gaussian_splat_tour":
      return (
        <GaussianSplatTourBlock
          config={block.config as GaussianSplatTourConfig}
          splatTours={splatTours}
        />
      );
    case "program_location_map":
      return (
        <ProgramLocationMapBlock
          config={block.config as ProgramLocationMapConfig}
          programLocations={programLocations ?? []}
        />
      );
    case "program_property_grid":
      return (
        <ProgramPropertyGridBlock
          config={block.config as ProgramPropertyGridConfig}
          programLocations={programLocations ?? []}
          googleMapsKey={googleMapsKey}
        />
      );
    case "building_floor_timeline":
      return (
        <BuildingFloorTimelineBlock
          config={block.config as BuildingFloorTimelineConfig}
          buildingFloors={buildingFloors ?? []}
        />
      );
    default:
      return null;
  }
}

/**
 * Renders the aligned BIM-cloud overlay (migration 382) on a
 * customer-facing share page. Read-only mode — no transform editing,
 * but the Overlay vs Compare mode toggle is exposed because it's
 * the customer value.
 *
 * Public bookmarks (migration 384) surface as a tour dropdown so
 * the operator can curate the customer's viewing experience —
 * "Front entrance", "Rooftop HVAC", "Storefront elevation" — and
 * customers click to jump to those preset viewpoints.
 *
 * Empty / pending states fall back to friendly placeholder copy
 * so the share page degrades gracefully when the operator hasn't
 * picked a group yet OR the conversion is still cooking.
 */
function AlignedBimCloudBlock({
  config,
  alignmentGroups,
}: {
  config: AlignedBimCloudConfig;
  alignmentGroups?: Record<string, ResolvedAlignmentGroup>;
}) {
  const groupId = config.alignment_group_id;
  if (!groupId) {
    return <NotSet message="No alignment group selected." />;
  }
  const group = alignmentGroups?.[groupId];
  if (!group) {
    return (
      <NotSet message="This alignment isn't ready yet — check back in a few minutes." />
    );
  }
  if (!group.copc_url) {
    return (
      <NotSet message="The anchor point cloud is still processing." />
    );
  }
  const height =
    typeof config.height === "number" && config.height > 0
      ? config.height
      : 600;
  return (
    <VirtualSpaceExploreGate label="Aligned BIM overlay" engageOnWheel>
      <AlignedBimCloudPublicShell
        copcUrl={group.copc_url}
        members={group.members}
        bookmarks={group.bookmarks ?? []}
        initialMode={config.initial_mode === "compare" ? "compare" : "overlay"}
        height={height}
      />
    </VirtualSpaceExploreGate>
  );
}

/**
 * Photoreal Gaussian Splat tour block (migration 385).
 *
 * Resolves the splat URL by joining through to the linked
 * `gaussian_splat_tour` deliverable's `gaussian_splat_tours.splat_url`.
 * Falls through to `config.splat_url` when set (operator escape
 * hatch for externally-hosted splats not tracked as deliverables).
 *
 * Empty / preparing / failed states fall back to friendly
 * placeholder copy so the share page degrades gracefully.
 */
function GaussianSplatTourBlock({
  config,
  splatTours,
}: {
  config: GaussianSplatTourConfig;
  splatTours?: Record<string, ResolvedSplatTour>;
  programLocations?: ResolvedProgramLocation[];
}) {
  const height =
    typeof config.height === "number" && config.height > 0
      ? config.height
      : 600;
  // Resolve the splat URL: deliverable lookup wins over direct
  // URL when both are set (operator picked a deliverable, the
  // direct URL is just the legacy paste-the-URL fallback).
  const tour = config.deliverable_id
    ? splatTours?.[config.deliverable_id]
    : null;
  const directUrl =
    typeof config.splat_url === "string" && config.splat_url.trim()
      ? config.splat_url.trim()
      : null;

  if (!tour && !directUrl) {
    return <NotSet message="No Photoreal Tour selected yet." />;
  }
  if (tour && tour.processing_status !== "ready") {
    if (tour.processing_status === "failed") {
      return (
        <NotSet
          message={
            tour.processing_error ??
            "Photoreal Tour conversion failed — your operator can retry."
          }
        />
      );
    }
    return (
      <NotSet message="Photoreal Tour is still preparing — check back in a few minutes." />
    );
  }
  const url = tour?.splat_url ?? directUrl;
  if (!url) {
    return <NotSet message="Photoreal Tour artefact not yet available." />;
  }
  // Phase B — sweep orbs default ON (operator opts out per-block
  // for marketing hero shots). The customer always sees the
  // "Show scan positions" toggle defaulting to off.
  const orbsAllowed = config.show_sweep_orbs !== false;
  const sweepPositions =
    orbsAllowed && tour?.sweep_positions ? tour.sweep_positions : undefined;

  // Phase D — equipment hotspots default OFF; operator opts in
  // explicitly. Server-resolved hotspots are filtered to >= 0.5
  // confidence; the block's threshold (default 0.99) narrows
  // further client-side so the slider is responsive without
  // re-fetching. Optional type allowlist also filters
  // client-side.
  const hotspotsAllowed = config.show_equipment_hotspots === true;
  const hotspotThreshold = config.hotspot_confidence_threshold ?? 0.99;
  const hotspotAllowlist = (config.hotspot_filter_types ?? []).map((t) =>
    t.toLowerCase().trim(),
  );
  const hotspots =
    hotspotsAllowed && tour?.equipment_hotspots
      ? tour.equipment_hotspots.filter((h) => {
          if (h.fused_confidence < hotspotThreshold) return false;
          if (hotspotAllowlist.length === 0) return true;
          return hotspotAllowlist.includes(
            h.equipment_type.toLowerCase().trim(),
          );
        })
      : undefined;

  // Phase F — alt formats from the resolver. Tours with only
  // the splat ready render no toggle pill; tours with multiple
  // ready formats expose Photoreal / Surface / Showcase.
  const altFormats = tour?.alt_formats;

  return (
    <VirtualSpaceExploreGate label="Photoreal walkthrough" engageOnWheel>
      <div style={{ height: `${height}px` }} className="w-full">
        <CompositionViewerMount label="photoreal tour"><MultiFormatTourViewer
          splatUrl={url}
          surfaceMeshUrl={altFormats?.surface_mesh_url ?? null}
          pointCloudUrl={altFormats?.point_cloud_url ?? null}
          matterportModelId={altFormats?.matterport_model_id ?? null}
          defaultFormat={config.default_format ?? "photoreal"}
          hideFormatToggle={config.hide_format_toggle === true}
          title={config.title ?? "Photoreal walkthrough"}
          panoPositions={sweepPositions}
          panoPositionsStorageKey={tour?.deliverable_id ?? null}
          equipmentHotspots={hotspots}
        /></CompositionViewerMount>
      </div>
    </VirtualSpaceExploreGate>
  );
}

/**
 * Public-facing wrapper around `AlignedBimCloudViewer`. Adds the
 * curated-tour dropdown when public bookmarks exist; without
 * bookmarks it just renders the bare viewer.
 *
 * Owns the viewer API handle so clicking a tour entry can apply
 * the saved camera + per-asset state to the live scene.
 */
function AlignedBimCloudPublicShell({
  copcUrl,
  members,
  bookmarks,
  initialMode,
  height,
}: {
  copcUrl: string;
  members: ResolvedAlignmentGroup["members"];
  bookmarks: NonNullable<ResolvedAlignmentGroup["bookmarks"]>;
  initialMode: "overlay" | "compare";
  height: number;
}) {
  const [viewerApi, setViewerApi] = useState<
    import("./aligned-bim-cloud-viewer").ViewerApi | null
  >(null);
  return (
    <div style={{ position: "relative" }}>
      <CompositionViewerMount label="aligned viewer" height={height}><AlignedBimCloudViewer
        copcUrl={copcUrl}
        alignments={members}
        initialMode={initialMode}
        height={height}
        onViewerApiReady={setViewerApi}
      /></CompositionViewerMount>
      {bookmarks.length > 0 && viewerApi && (
        <div
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            zIndex: 10,
            background: "rgba(8,11,16,0.85)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 9999,
            padding: "4px 6px 4px 14px",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#fff",
            backdropFilter: "blur(6px)",
          }}
        >
          <span
            style={{
              fontSize: 10,
              opacity: 0.65,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Tour
          </span>
          <select
            onChange={(e) => {
              const b = bookmarks.find((x) => x.id === e.target.value);
              if (!b) return;
              if (b.mode) viewerApi.setMode(b.mode);
              viewerApi.applySnapshot(b.state as any);
              // Reset the dropdown so re-selecting fires onChange again.
              e.target.value = "";
            }}
            defaultValue=""
            style={{
              background: "transparent",
              color: "#fff",
              border: "none",
              fontSize: 12,
              padding: "4px 6px",
              borderRadius: 6,
              cursor: "pointer",
              outline: "none",
            }}
            aria-label="Jump to a saved viewpoint"
          >
            <option value="" disabled>
              Jump to a view…
            </option>
            {bookmarks.map((b) => (
              <option
                key={b.id}
                value={b.id}
                style={{ background: "#0b0e14", color: "#fff" }}
              >
                {b.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ── Shared chrome ────────────────────────────────────────────────

function Section({
  title,
  children,
  align = "left",
}: {
  title?: string | null;
  children: React.ReactNode;
  /**
   * Horizontal alignment for the section's title + children.
   * Defaults to `left` so the established blocks (file_list,
   * scan_list, contact_card, calendar/restaurant booking,
   * external_embed, etc.) keep their left-aligned reading
   * rhythm. `center` is opt-in for blocks that read as
   * brochure-style CTAs (link_buttons today; reserved for
   * future operator-configurable alignment).
   */
  align?: "left" | "center";
}) {
  return (
    <section className={cn("space-y-3", align === "center" && "text-center")}>
      {title && (
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}

function ViewerSkeleton({
  height,
  label,
}: {
  /** Pixel height (number) or any CSS length (string), e.g.
   *  `"min(70dvh, 800px)"` to match the corresponding viewer's
   *  viewport-aware default so the loading box doesn't shrink/grow
   *  on hydration. */
  height: number | string;
  label: string;
}) {
  return (
    <div
      className="w-full rounded-lg bg-muted/40 flex items-center justify-center"
      style={{ height }}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function NotSet({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed vs-border-soft bg-muted/20 px-4 py-6 text-center">
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}

// ── Primitive blocks ──────────────────────────────────────────────

function HeadingBlock({ config }: { config: HeadingConfig }) {
  const text = config.text?.trim() ?? "";
  if (!text) return null;
  const subtitle = config.subtitle?.trim() ?? "";
  const Tag = (config.level === 2 ? "h2" : config.level === 3 ? "h3" : "h1") as
    | "h1"
    | "h2"
    | "h3";
  const sizeClass =
    config.level === 3
      ? "text-xl"
      : config.level === 2
        ? "text-2xl sm:text-3xl"
        : "text-3xl sm:text-4xl";
  return (
    <header className="space-y-2 text-center">
      <Tag className={`${sizeClass} font-semibold leading-tight`}>{text}</Tag>
      {subtitle && (
        <p className="text-base text-muted-foreground">{subtitle}</p>
      )}
    </header>
  );
}

/**
 * Safe minimal Markdown renderer for v1 — supports bold, italics,
 * paragraphs, unordered lists, and inline links. Full Markdown
 * parser can drop in here later (react-markdown + rehype-sanitize)
 * without any consumer changes.
 *
 * Escapes HTML up front so user-controlled content can never inject
 * tags. The regex substitutions operate on escaped text and only
 * produce the specific tags we emit, so this is XSS-safe.
 */
function renderMinimalMarkdown(md: string): string {
  const escaped = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  // Transform line-by-line for headings + lists; keep a simple
  // state machine to wrap <li>s into <ul>.
  const lines = escaped.split(/\r?\n/);
  const out: string[] = [];
  let inList = false;
  let paragraphBuffer: string[] = [];
  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    const text = paragraphBuffer.join(" ");
    out.push(`<p>${inlineTransforms(text)}</p>`);
    paragraphBuffer = [];
  };
  const flushList = () => {
    if (inList) {
      out.push("</ul>");
      inList = false;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    if (/^###\s+/.test(line)) {
      flushParagraph();
      flushList();
      out.push(`<h3>${inlineTransforms(line.replace(/^###\s+/, ""))}</h3>`);
      continue;
    }
    if (/^##\s+/.test(line)) {
      flushParagraph();
      flushList();
      out.push(`<h2>${inlineTransforms(line.replace(/^##\s+/, ""))}</h2>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      if (!inList) {
        out.push("<ul>");
        inList = true;
      }
      out.push(`<li>${inlineTransforms(line.replace(/^[-*]\s+/, ""))}</li>`);
      continue;
    }
    flushList();
    paragraphBuffer.push(line);
  }
  flushParagraph();
  flushList();
  return out.join("");
}

function inlineTransforms(s: string): string {
  return (
    s
      // **bold**
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      // *italic* — greedy guard: not adjacent to other * to avoid
      // clobbering bold syntax.
      .replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, "$1<em>$2</em>")
      // [label](https://…)
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline">$1</a>',
      )
  );
}

function RichTextBlock({ config }: { config: RichTextConfig }) {
  const md = config.markdown?.trim() ?? "";
  if (!md) return null;
  return (
    <div
      className="prose prose-sm sm:prose-base vs-dark:prose-invert max-w-none prose-a:text-[color:var(--sv-brand)] text-center"
      // eslint-disable-next-line react/no-danger -- escaped upstream
      dangerouslySetInnerHTML={{ __html: renderMinimalMarkdown(md) }}
    />
  );
}

function SpacerBlock({ config }: { config: SpacerConfig }) {
  const height = Math.max(8, config.height ?? 48);
  // Default to "line" so existing blank spacers automatically pick
  // up the on-brand divider; opt-out is `style: 'none'`.
  const style = config.style ?? "line";

  if (style === "none") {
    return <div aria-hidden="true" style={{ height }} />;
  }

  // Common wrapper — block-level flex container that vertically
  // centers the divider inside the user-defined height. The height
  // still controls breathing room; the divider just fills the
  // middle.
  const wrapStyle = { height } as const;

  if (style === "line") {
    // Short horizontal rule in brand red. Reads as a deliberate
    // section break without splitting the page like a full hr.
    // Subtle drop-glow keeps it from looking like a rendering
    // artefact on dark mode.
    return (
      <div
        aria-hidden="true"
        className="flex items-center justify-center"
        style={wrapStyle}
      >
        <span
          className="block h-px w-16 rounded-full"
          style={{
            backgroundColor: "var(--vs-red-text)",
            boxShadow: "0 0 8px rgb(var(--vs-accent-rgb) / 0.45)",
          }}
        />
      </div>
    );
  }

  if (style === "gradient") {
    // Page-wide line that fades at both ends. Softer than `line` —
    // good for cover-image → narrative transitions where a hard
    // mark would feel abrupt.
    return (
      <div
        aria-hidden="true"
        className="flex items-center"
        style={wrapStyle}
      >
        <span
          className="block h-px w-full"
          style={{
            backgroundImage:
              "linear-gradient(to right, transparent, rgb(var(--vs-accent-rgb) / 0.65), transparent)",
          }}
        />
      </div>
    );
  }

  // style === "dot" — single 6px dot centered, brand red. Subtle
  // glow ring so it reads on light + dark equally.
  return (
    <div
      aria-hidden="true"
      className="flex items-center justify-center"
      style={wrapStyle}
    >
      <span
        className="block h-1.5 w-1.5 rounded-full"
        style={{
          backgroundColor: "var(--vs-red-text)",
          boxShadow: "0 0 6px rgb(var(--vs-accent-rgb) / 0.55)",
        }}
      />
    </div>
  );
}

function HeroImageBlock({ config }: { config: HeroImageConfig }) {
  // Prefer `file_asset_id` (routes through our on-domain /f/[id]
  // proxy so the Supabase / R2 origin doesn't leak). Fall back to
  // a raw pasted URL for authors who haven't uploaded to our
  // file_assets registry.
  const url = config.file_asset_id
    ? `/f/${encodeURIComponent(config.file_asset_id)}`
    : config.image_url;
  if (!url) return null;
  return (
    <figure className="w-full">
      <div className="relative w-full aspect-[3/1] overflow-hidden rounded-lg bg-muted">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={url}
          alt={config.caption ?? ""}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      {config.caption && (
        <figcaption className="mt-2 text-xs text-muted-foreground text-center">
          {config.caption}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Hero aerial fly-over block.
 *
 * Renders the Google Aerial View fly-over video for the
 * picked location as a full-width hero, fetched on demand
 * via `<AerialViewEmbed>` — same component the dashboard's
 * location card uses for its "Aerial Video" tab. AerialViewEmbed
 * has its own client + server cache + state machine
 * (fetching / processing / ready / error / no_video), so the
 * hero gracefully:
 *   - renders the actual fly-over video when Google has one
 *   - shows "Google is still rendering this fly-over" when the
 *     render is queued (first lookup for a brand-new address
 *     fires the render automatically, returns ~2-5 min later)
 *   - hides cleanly via `quiet` mode when no video exists at
 *     all (the hero block just disappears instead of painting
 *     a broken / empty tile)
 *
 * Composes the address from the resolved location's
 * `address_line_1, city, state, country` (zip excluded — it
 * confuses Google's geocoder when it points at a different
 * suburb than the street address).
 */
function HeroAerialBlock({
  config,
  locations,
}: {
  config: HeroAerialConfig;
  locations: Record<string, ResolvedLocation>;
}) {
  const loc = config.location_id ? locations[config.location_id] ?? null : null;
  if (!loc) return null;
  const address = [loc.address_line_1, loc.city, loc.state, loc.country]
    .filter((piece): piece is string => Boolean(piece))
    .join(", ");
  if (!address) return null;
  // Aspect-ratio sizing — the figure governs proportions, the embed fills
  // the resulting box. Theater mode bumps the wrapper width which
  // automatically scales the hero height proportionally (no hard-coded
  // pixel value to fight). Default 16:9 reads as a cinematic landscape;
  // operators can pick 21:9 (wider letterbox) or 4:3 (taller thumbnail).
  const aspectClass =
    config.aspect === "21:9"
      ? "aspect-[21/9]"
      : config.aspect === "4:3"
        ? "aspect-[4/3]"
        : "aspect-[16/9]";
  return (
    <figure className="w-full">
      <div className={`relative w-full overflow-hidden rounded-lg ${aspectClass}`}>
        <div className="absolute inset-0">
          <AerialViewEmbed
            address={address}
            height="100%"
            className="!rounded-lg !h-full"
            quiet
          />
        </div>
        {/* Subtle bottom fade so the hero reads as "scroll continues" — a
            polish straight off Apple's product hero treatment. Pointer-
            events:none so it doesn't block clicks on the underlying video. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/55 to-transparent"
        />
      </div>
      {config.caption && (
        <figcaption className="mt-2 text-xs text-muted-foreground text-center">
          {config.caption}
        </figcaption>
      )}
    </figure>
  );
}

// ── Spatial blocks ────────────────────────────────────────────────

function PointCloudBlock({
  config,
  resolved,
}: {
  config: PointCloudConfig;
  resolved: ResolvedPointCloud | null;
}) {
  const { publicShareUrl, privatePreview } = usePublicComposition();
  if (!resolved?.copc_url) {
    return (
      <NotSet message="Point cloud isn't available yet — it may still be processing or the link has expired." />
    );
  }
  return (
    <div
      className="w-full rounded-lg overflow-hidden bg-black touch-pan-y sm:touch-auto"
      style={{ height: config.height ?? "min(70dvh, 800px)" }}
    >
      <CompositionViewerMount label="point cloud"><VirtualSpaceExploreGate label="point cloud viewer">
        <PointCloudViewer
          copcUrl={resolved.copc_url}
          panos={resolved.panos}
          floorLabelOverrides={resolved.floor_labels}
          readOnly
          publicShareUrl={publicShareUrl ?? undefined}
          sharingDisabled={privatePreview}
        />
      </VirtualSpaceExploreGate></CompositionViewerMount>
    </div>
  );
}

function MatterportBlock({
  config,
  scans,
}: {
  config: MatterportConfig;
  scans: Record<string, ResolvedScan>;
}) {
  // Prefer a resolved deliverable's matterport_details.provider_model_id
  // when the block targets a deliverable id; falls back to the raw
  // `model_id` when the author pasted one directly. Post-216 the
  // config key is `deliverable_id`; legacy blocks still serialise
  // `scan_id` so we accept either shape. The polymorphic projection
  // (added in PR for migration 256) now joins matterport_details
  // server-side, so `scans[id].matterport_details.provider_model_id`
  // is the authoritative source — falling back to the legacy
  // `provider_model_id` shim that's also populated for back-compat.
  const launchMode = useLaunchMode();
  const { enabled: enhanced } = usePublicComposition();
  const resolvedId =
    (config as { deliverable_id?: string | null; scan_id?: string | null }).deliverable_id ??
    (config as { scan_id?: string | null }).scan_id ??
    null;
  const resolvedScan = resolvedId ? scans[resolvedId] : null;
  const scanModelId =
    resolvedScan?.matterport_details?.provider_model_id ??
    resolvedScan?.provider_model_id ??
    null;
  // Phase 1 (2026-05-23): when this Launch has sibling models
  // (multi-floor / repeat-capture), the active model id comes from
  // the LaunchActiveModelProvider (driven by the menu + elevator
  // strip + URL hash). Falls back to the block's config-derived id
  // when there's no provider in scope (single-model Launches, all
  // Orbit/Landing pages, every operator-side renderer).
  const activeModel = useLaunchActiveModel();
  const overrideModelId =
    activeModel?.activeProviderModelId &&
    activeModel.activeProviderModelId.length > 0
      ? activeModel.activeProviderModelId
      : null;
  const modelId = (overrideModelId ?? scanModelId ?? config.model_id ?? "").trim();
  if (!modelId) {
    return <NotSet message="No Matterport model selected yet." />;
  }
  // `buildMatterportEmbedUrl` strips the share/VR/highlight-reel/help
  // toolbar buttons by default — those affordances belong to the
  // standalone Matterport.com landing page, not to an embedded
  // VirtualSpace where clicking Share would lift the visitor out of
  // the Spatialist context. Defaults compose `play=1` + `qs=1` for
  // the standard "skip the splash + orientation" embed feel.
  const src = buildMatterportEmbedUrl(modelId);
  if (!src) {
    return <NotSet message="No Matterport model selected yet." />;
  }
  // Launch mode (2026-05-22): the viewer IS the page, so render
  // full-bleed (no rounded corners, fills its container) AND skip
  // the click-to-explore gate. The gate exists to protect long-
  // scroll Orbit/Landing pages from "trapped in the embed" scroll-
  // capture; on a single-block Launch the visitor's intent IS to
  // interact with the iframe, so loading + interaction should be
  // immediate.
  //
  // 2026-05-23 (v7): when in Launch mode AND the SDK key is
  // configured, use the Matterport Web SDK (`setupSdk` from
  // `@matterport/sdk`, already wired for the SpaceSight equipment
  // classifier) to render the viewer. The SDK iframe path honors
  // the c*-prefixed customization params (csnapshot, cDisableTagDock,
  // cDisableTagDockPaging) that the public Showcase URL ignores,
  // giving us mpskin-level chrome suppression (no Matterport logo,
  // no toolbar, no help/share/snapshot/tag-dock). If the SDK key
  // isn't set or fails to load, fall through to the basic iframe
  // path so the Launch always renders SOMETHING. NEXT_PUBLIC_
  // prefix means the env var is inlined at build time — safe to
  // read on the client.
  const sdkKey = process.env.NEXT_PUBLIC_MATTERPORT_SDK_KEY ?? "";
  if (launchMode && sdkKey) {
    return (
      <div className="w-full h-full bg-black">
        <MatterportLaunchViewer
          key={modelId}
          modelId={modelId}
          sdkKey={sdkKey}
          previewUrl={modelId === scanModelId ? resolvedScan?.thumbnail_url : null}
        />
      </div>
    );
  }
  return (
    <div
      className={
        launchMode
          ? "w-full h-full bg-black"
          : "w-full rounded-lg overflow-hidden bg-black touch-pan-y sm:touch-auto"
      }
      style={
        launchMode ? undefined : { height: config.height ?? "min(70dvh, 800px)" }
      }
    >
      <CompositionViewerMount label="tour">
      <VirtualSpaceExploreGate
        label="Matterport walkthrough"
        active={!launchMode}
      >
        {enhanced && sdkKey ? <LaunchViewerSessionProvider><MatterportLaunchViewer key={modelId} modelId={modelId} sdkKey={sdkKey} previewUrl={resolvedScan?.thumbnail_url} /></LaunchViewerSessionProvider> : <iframe
          src={src}
          title="Matterport walkthrough"
          className="w-full h-full"
          allow="xr-spatial-tracking; vr; gyroscope; accelerometer; fullscreen"
          allowFullScreen
        />}
      </VirtualSpaceExploreGate>
      </CompositionViewerMount>
    </div>
  );
}

// ── Map blocks ────────────────────────────────────────────────────

function googleStaticMapUrl(
  apiKey: string,
  lat: number,
  lng: number,
  zoom: number,
  style: GoogleMapConfig["style"],
  width = 1200,
  height = 600,
): string {
  const params = new URLSearchParams({
    center: `${lat},${lng}`,
    zoom: String(zoom),
    size: `${width}x${height}`,
    scale: "2",
    maptype: style ?? "roadmap",
    key: apiKey,
  });
  params.append("markers", `color:${MAP_PIN_GOOGLE}|${lat},${lng}`);
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

function GoogleMapBlock({
  config,
  apiKey,
}: {
  config: GoogleMapConfig;
  apiKey: string | null;
}) {
  if (!apiKey) return <NotSet message="Google Maps API key not configured." />;
  if (config.lat === null || config.lng === null) {
    return <NotSet message="No coordinates set for this map." />;
  }
  const url = googleStaticMapUrl(
    apiKey,
    config.lat,
    config.lng,
    config.zoom ?? 15,
    config.style ?? "roadmap",
  );
  return (
    <div className="w-full aspect-[2/1] rounded-lg overflow-hidden bg-muted">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt="Map"
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
  );
}

function StreetViewBlock({
  config,
  apiKey,
}: {
  config: StreetViewConfig;
  apiKey: string | null;
}) {
  if (!apiKey) return <NotSet message="Google Maps API key not configured." />;
  if (config.lat === null || config.lng === null) {
    return <NotSet message="No coordinates set for Street View." />;
  }
  const params = new URLSearchParams({
    key: apiKey,
    location: `${config.lat},${config.lng}`,
    heading: String(config.heading ?? 0),
    pitch: String(config.pitch ?? 0),
  });
  const src = `https://www.google.com/maps/embed/v1/streetview?${params.toString()}`;
  return (
    <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-muted">
      <iframe
        src={src}
        title="Street View"
        className="w-full h-full"
        loading="lazy"
        allowFullScreen
      />
    </div>
  );
}

function Google3DTilesBlock({
  config,
  apiKey,
}: {
  config: Google3DTilesConfig;
  apiKey: string | null;
}) {
  if (!apiKey) return <NotSet message="Google Maps API key not configured." />;
  if (config.lat === null || config.lng === null) {
    return <NotSet message="No coordinates set for 3D tiles." />;
  }
  // Use config.altitude as the rendered viewport height (min/max
  // clamped to keep layouts sensible). `ThreeDTilesView` treats its
  // `height` prop as the container pixel height; the Cesium camera
  // defaults take care of framing the scene.
  //
  // When the operator hasn't customised altitude we let CSS pick the
  // height (viewport-aware so the viewer doesn't look squat in a wide
  // theater frame); the Cesium child still needs a numeric pixel hint
  // for its initial canvas allocation, so pass a generous default.
  const operatorAltitude = config.altitude;
  const wrapperHeight =
    typeof operatorAltitude === "number"
      ? Math.min(Math.max(operatorAltitude, 280), 900)
      : "min(70dvh, 800px)";
  const cesiumRequestHeight =
    typeof operatorAltitude === "number"
      ? Math.min(Math.max(operatorAltitude, 280), 900)
      : 800;
  return (
    <div
      className="w-full rounded-lg overflow-hidden"
      style={{ height: wrapperHeight }}
    >
      <CompositionViewerMount label="3D map"><ThreeDTilesView
        lat={config.lat}
        lng={config.lng}
        height={cesiumRequestHeight}
      /></CompositionViewerMount>
    </div>
  );
}

function AerialImageBlock({
  config,
  googleMapsKey,
  mapboxToken,
}: {
  config: AerialImageConfig;
  googleMapsKey: string | null;
  mapboxToken: string | null;
}) {
  if (config.lat === null || config.lng === null) {
    return <NotSet message="No coordinates set." />;
  }
  let url: string | null = null;
  if (config.provider === "mapbox" && mapboxToken) {
    url = `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/${config.lng},${config.lat},${config.zoom ?? 18},0/1200x600@2x?access_token=${mapboxToken}`;
  } else if (googleMapsKey) {
    url = googleStaticMapUrl(
      googleMapsKey,
      config.lat,
      config.lng,
      config.zoom ?? 18,
      "satellite",
    );
  }
  if (!url) return <NotSet message="Map tile provider key not configured." />;
  return (
    <div className="w-full aspect-[2/1] rounded-lg overflow-hidden bg-muted">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt="Aerial view"
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
  );
}

// ── Collection blocks ────────────────────────────────────────────

function formatBytes(bytes: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Pure helper — pick the best download URL for a resolved file
 * asset, mirroring the priority used by the share-token-gated
 * `/api/share/[token]/download` route. R2 mirror first (free
 * egress, instant), then `public_url` for Supabase-stored
 * non-APS assets, then the share-token-gated route for
 * APS-only files (which requires server-side signing). When the
 * file is APS-only AND we don't have a share token to gate the
 * download, returns null — the row falls back to a disabled
 * state so we don't ship a broken link.
 */
function downloadUrlForFile(
  f: ResolvedFileAsset,
  shareToken: string | null,
): string | null {
  if (f.r2_mirror_state === "done" && f.r2_public_url) return f.r2_public_url;
  // After client-file buckets go private, stored `/object/public/…`
  // URLs 400. The share-token route mints a signed URL (or APS).
  if (shareToken) {
    return `/api/share/${encodeURIComponent(shareToken)}/download?fileAssetId=${encodeURIComponent(f.id)}`;
  }
  if (f.public_url) return f.public_url;
  return null;
}

function isPdfAsset(f: ResolvedFileAsset): boolean {
  const mt = (f.mime_type ?? "").toLowerCase();
  return (
    mt === "application/pdf" ||
    mt.includes("pdf") ||
    (f.file_name ?? "").toLowerCase().endsWith(".pdf")
  );
}

/** Share-token download route with explicit Content-Disposition (see API route). */
function sharePdfFileHref(
  f: ResolvedFileAsset,
  shareToken: string,
  disposition: "inline" | "attachment",
): string {
  return `/api/share/${encodeURIComponent(shareToken)}/download?fileAssetId=${encodeURIComponent(f.id)}&disposition=${disposition}`;
}

const fileActionBtnClass =
  "shrink-0 inline-flex items-center gap-1 rounded-md border vs-border-soft bg-card px-3 py-2 sm:py-1.5 min-h-9 sm:min-h-0 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors touch-manipulation";

/**
 * Inline PDF preview URL for `<iframe src>` — prefers the gated
 * `disposition=inline` route on shares so R2/Supabase objects that
 * ship `attachment` still open in the browser viewer.
 */
function fileInlinePreviewSrc(
  f: ResolvedFileAsset,
  shareToken: string | null,
): string | null {
  if (shareToken && isPdfAsset(f)) {
    return sharePdfFileHref(f, shareToken, "inline");
  }
  if (f.r2_mirror_state === "done" && f.r2_public_url) return f.r2_public_url;
  if (f.public_url) return f.public_url;
  return null;
}

function PdfViewDownloadActions({
  file,
  shareToken,
}: {
  file: ResolvedFileAsset;
  shareToken: string | null;
}) {
  if (!shareToken || !isPdfAsset(file)) return null;
  return (
    <div className="shrink-0 flex flex-wrap items-center justify-end gap-2">
      <a
        href={sharePdfFileHref(file, shareToken, "inline")}
        target="_blank"
        rel="noopener noreferrer"
        className={fileActionBtnClass}
      >
        <Eye className="h-3 w-3" aria-hidden />
        View
      </a>
      <a
        href={sharePdfFileHref(file, shareToken, "attachment")}
        target="_blank"
        rel="noopener noreferrer"
        className={fileActionBtnClass}
      >
        <Download className="h-3 w-3" aria-hidden />
        Download
      </a>
    </div>
  );
}

/**
 * Federation-load priority for grouping. Mirrors
 * `cadBimLoadPriority` from `cad-bim-extensions.ts` — RVT
 * surfaces first, then IFC, then NWD, etc. Group ordering uses
 * the priority of the highest-priority extension in the group
 * so the heaviest format ends up at the top.
 */
function fileExt(name: string | null | undefined): string {
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}
function groupPriority(ext: string): number {
  const lower = ext.toLowerCase();
  switch (lower) {
    case "rvt":
      return 100;
    case "rfa":
      return 95;
    case "ifc":
      return 90;
    case "nwd":
      return 80;
    case "nwc":
      return 70;
    case "dgn":
      return 65;
    case "3dm":
    case "3ds":
      return 60;
    case "skp":
      return 55;
    case "dwfx":
    case "dwf":
      return 50;
    case "stp":
    case "step":
    case "igs":
    case "iges":
      return 45;
    case "f3d":
      return 40;
    case "obj":
    case "fbx":
      return 35;
    case "dwg":
      return 20;
    case "dxf":
      return 15;
    case "pdf":
      return 10;
    default:
      return 5;
  }
}

const CAD_BIM_EXT_SET: ReadonlySet<string> = new Set([
  "rvt",
  "rfa",
  "ifc",
  "nwd",
  "nwc",
  "dgn",
  "dwfx",
  "dwf",
  "dwg",
  "dxf",
  "stp",
  "step",
  "igs",
  "iges",
  "obj",
  "fbx",
  "skp",
  "3dm",
  "3ds",
  "f3d",
]);
function isCadBim(file: ResolvedFileAsset): boolean {
  return (
    CAD_BIM_EXT_SET.has(fileExt(file.file_name)) ||
    Boolean(file.storage_path?.startsWith("aps:"))
  );
}

/**
 * File list block — type-grouped + version-aware view that
 * matches the dashboard's BIM/CAD file library 1:1.
 *
 * What it renders (top → bottom):
 *   1. Embedded Autodesk Viewer for any CAD/BIM files that
 *      have an APS URN (storage_path = "aps:<urn>"). Loaded
 *      with the federation Models panel; primary model is
 *      visible by default with the others toggleable. Public
 *      shares mint a viewables-only APS token via the
 *      share-token-gated `/api/autodesk/public-token` route.
 *   2. "Download all as ZIP" button — calls
 *      `/api/share/[token]/zip` with the file_asset_ids and
 *      streams a single archive. Free egress for non-APS files;
 *      APS files are signed-S3 streamed server-side into the
 *      zip.
 *   3. Version chrome (V1 / V2 dividers + reviewer notes) when
 *      the files span multiple `deliverable_revisions`. Single-
 *      version bundles render flat — no extra UI.
 *   4. Type-grouped file rows (RVT first, then PDF, then DWG,
 *      etc.) with full filenames; within each type, rows sort
 *      by inferred floor (low → high) then sheet kind + name
 *      (`compareBimCadFileOrder`). PDFs on shares get **View**
 *      + **Download** (both via `/api/share/.../download` with
 *      `disposition=inline|attachment`); other types keep a
 *      single Download link (direct URL or APS-gated route).
 *
 * Per-file URL priority for non-PDF — same as the /download route:
 *   R2 mirror URL > Supabase public_url > share-token-gated
 *   APS-signed URL (via /api/share/[token]/download).
 */
function FileListBlock({
  config,
  assets,
  shareToken,
}: {
  config: FileListConfig;
  assets: Record<string, ResolvedFileAsset>;
  shareToken: string | null;
}) {
  const { privatePreview } = usePublicComposition();
  // Operator previews use the existing session + internal-membership gate;
  // public viewers must keep using their narrower share-token endpoint.
  const tokenEndpoint = privatePreview ? "/api/autodesk/token" : shareToken ? `/api/autodesk/public-token?token=${encodeURIComponent(shareToken)}` : null;
  // Active model in the inline AutodeskViewer below — drives
  // the per-file note banner above it (migration 371). Same
  // mechanism as `BimViewerBlock`'s state.
  const [activeUrn, setActiveUrn] = useState<string | null>(null);
  const ids = config.file_asset_ids ?? [];
  const rows = ids
    .map((id) => assets[id])
    .filter((r): r is ResolvedFileAsset => !!r);
  if (rows.length === 0) {
    return null;
  }

  // Group files by extension. Each group's order in the
  // rendered output is the highest priority of any of its
  // members; within a group, sort by inferred floor (low →
  // high) + sheet kind + name — same heuristic as the internal
  // BIM deliverable file library.
  const groups = new Map<string, ResolvedFileAsset[]>();
  for (const f of rows) {
    const ext = fileExt(f.file_name) || "other";
    const list = groups.get(ext) ?? [];
    list.push(f);
    groups.set(ext, list);
  }
  const orderedGroups = Array.from(groups.entries())
    .map(([ext, list]) => ({
      ext,
      list: [...list].sort((a, b) => compareBimCadFileOrder(a, b)),
      priority: ext === "other" ? 0 : groupPriority(ext),
    }))
    .sort((a, b) => b.priority - a.priority || a.ext.localeCompare(b.ext));

  // Federate every CAD/BIM file with a known URN into one
  // Autodesk Viewer scene at the top of the section.
  //
  // Every URN carries its `revisionNumber` + `versionLabel` +
  // `versionNotes` straight off the `ResolvedFileAsset` so the
  // viewer's Models panel groups rows by version (latest
  // expanded, older collapsed) — same UX as the dashboard
  // detail page. Without this, the viewer panel renders a
  // single flat list of all 2+ versions' files mixed together,
  // and a multi-version BIM bundle ends up looking like
  // duplicate uploads ("why are there two RVTs of the same
  // name?"). Sort key mirrors the dashboard's federation
  // builder: revision desc → priority desc → filename asc, so
  // the viewer's initial camera frames the latest version's
  // top-priority file (RVT in V2 over RVT in V1).
  const sortedFlatRows = [...rows].sort((a, b) => compareBimCadFileOrder(a, b));
  const apsUrns = rows
    .filter((f) => f.storage_path?.startsWith("aps:"))
    .map((f) => ({
      urn: f.storage_path!.slice("aps:".length),
      fileName: f.file_name ?? null,
      revisionNumber: f.revision_number ?? null,
      versionLabel:
        f.revision_number != null
          ? f.revision_name
            ? `V${f.revision_number} — ${f.revision_name}`
            : `V${f.revision_number}`
          : null,
      versionNotes: f.reviewer_notes ?? null,
      createdAt: f.created_at ?? null,
      fileSize: f.file_size ?? null,
      displaySortKey: f.display_sort_key ?? null,
    }))
    .sort((a, b) => {
      const ra = a.revisionNumber ?? -1;
      const rb = b.revisionNumber ?? -1;
      if (ra !== rb) return rb - ra;
      const pa = groupPriority(fileExt(a.fileName));
      const pb = groupPriority(fileExt(b.fileName));
      if (pa !== pb) return pb - pa;
      return compareBimCadFileOrder(
        {
          fileName: a.fileName,
          createdAt: a.createdAt,
          display_sort_key: a.displaySortKey,
        },
        {
          fileName: b.fileName,
          createdAt: b.createdAt,
          display_sort_key: b.displaySortKey,
        },
      );
    });
  // Paired download rails (`deliverable_id` set) sit beside a
  // dedicated `bim_viewer` / `lidar_viewer` / `floor_plan_view`
  // on auto-composed shares. Re-embedding Autodesk here made
  // Spatial As-Built pages show the CAD viewer twice — prefer
  // the dedicated viewer (and, when available, a same-address
  // Matterport / NavVis / point-cloud companion) instead.
  // Standalone curated file_lists leave deliverable_id null and
  // still get the inline Autodesk embed.
  const showViewer =
    apsUrns.length > 0 && Boolean(tokenEndpoint) && !config.deliverable_id;

  // Version chrome — only render when the bundle spans 2+
  // `revision_id`s (i.e. multiple uploads). Files without a
  // revision_id collapse into an "Unfiled" bucket so legacy
  // file_lists keep rendering.
  const versionMap = new Map<
    string,
    {
      revision_id: string;
      revision_number: number | null;
      reviewer_notes: string | null;
      revision_name: string | null;
      revision_date: string | null;
      files: ResolvedFileAsset[];
    }
  >();
  let unfiledCount = 0;
  for (const f of rows) {
    if (!f.revision_id) {
      unfiledCount++;
      continue;
    }
    const existing = versionMap.get(f.revision_id);
    if (existing) {
      existing.files.push(f);
    } else {
      versionMap.set(f.revision_id, {
        revision_id: f.revision_id,
        revision_number: f.revision_number,
        reviewer_notes: f.reviewer_notes,
        revision_name: f.revision_name,
        revision_date: f.revision_date,
        files: [f],
      });
    }
  }
  for (const v of versionMap.values()) {
    v.files.sort((a, b) => compareBimCadFileOrder(a, b));
  }
  const distinctVersions = versionMap.size;
  const hasMultipleVersions = distinctVersions > 1;
  // Sort versions newest-first.
  const versionList = Array.from(versionMap.values()).sort(
    (a, b) => (b.revision_number ?? 0) - (a.revision_number ?? 0),
  );
  const unfiledFiles = rows
    .filter((f) => !f.revision_id)
    .sort((a, b) => compareBimCadFileOrder(a, b));
  // Notes from the latest version surface as a callout above the
  // file grid — operators frequently use this to communicate
  // "what's new in V2" or hand-off context. Suppressed when the
  // block is paired with a viewer (deliverable_id set) since the
  // viewer's own RevisionHeaderStrip already shows the same
  // notes immediately above; duplicating here is just noise on
  // an auto-composed BIM/CAD/floor-plan share.
  const latestNotes =
    !hasMultipleVersions &&
    !config.deliverable_id &&
    versionList[0]?.reviewer_notes
      ? versionList[0].reviewer_notes
      : null;

  const zipUrl = shareToken
    ? `/api/share/${encodeURIComponent(shareToken)}/zip?ids=${encodeURIComponent(rows.map((r) => r.id).join(","))}`
    : null;

  // Resolve the active model's per-file note (migration 371)
  // by walking the rows for an aps:<urn> match. Mirrors the
  // BimViewerBlock logic — recompute on activeUrn change is
  // cheap (typical scene < 30 files), no need for a memoised
  // map. Falls back to null when no match (legacy uploads,
  // transient transitions between models).
  const activeFileNote = (() => {
    if (!activeUrn) return null;
    const target = `aps:${activeUrn}`;
    for (const row of rows) {
      if (row.storage_path === target) {
        const trimmed = row.note?.trim();
        return trimmed && trimmed.length > 0 ? trimmed : null;
      }
    }
    return null;
  })();

  return (
    <Section title={config.title}>
      {showViewer && activeFileNote && (
        <div className="mb-3">
          <ActiveFileNoteBanner note={activeFileNote} />
        </div>
      )}
      {showViewer && (
        <div className="mb-4">
          <VirtualSpaceExploreGate label="BIM/CAD viewer" engageOnWheel>
            <CompositionViewerMount label="BIM viewer" height="min(70dvh, 800px)"><AutodeskViewer
              urns={apsUrns}
              height="min(70dvh, 800px)"
              tokenEndpoint={tokenEndpoint!}
              onActiveModelChange={setActiveUrn}
            /></CompositionViewerMount>
          </VirtualSpaceExploreGate>
        </div>
      )}

      {/* Header strip — file count + (single-version) bundle
          ZIP CTA. Multi-version bundles INTENTIONALLY skip the
          bundle-wide ZIP button: every version has its own
          "Download V1 as ZIP" / "Download V2 as ZIP" trigger
          on its section header (with the older-version
          confirm gate), so a top-level "Download all" button
          would (a) duplicate the action surface and (b)
          quietly bundle stale V1 files into the same archive
          as the latest V2 work — exactly the failure mode
          the per-version confirm gate was added to prevent.
          When there's only ONE version, the bundle button
          stays — that's the same as "Download V1 as ZIP" and
          carries no stale-version risk. */}
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-muted-foreground">
          {rows.length} file{rows.length === 1 ? "" : "s"}
          {hasMultipleVersions
            ? ` · ${distinctVersions} versions`
            : null}
        </p>
        {zipUrl && rows.length > 1 && !hasMultipleVersions && (
          <DownloadZipButton
            url={zipUrl}
            filename={`${(config.title || "files").replace(/[^a-z0-9-_]+/gi, "-")}.zip`}
          />
        )}
      </div>

      {/* Notes on a single-version bundle render as a soft
          callout above the file grid. Multi-version bundles
          show notes inline with each version header (below). */}
      {latestNotes && (
        <div className="mb-4 rounded-md border vs-border-soft bg-muted/20 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Notes
          </p>
          <p className="mt-1 text-sm whitespace-pre-line">{latestNotes}</p>
        </div>
      )}

      {hasMultipleVersions ? (
        <div className="space-y-5">
          {versionList.map((v, idx) => (
            // Every version section is a collapsible disclosure
            // and now starts COLLAPSED — including the latest.
            // Pre-2026-05-06 the latest version auto-expanded so
            // the recipient saw "what's current" immediately, but
            // with file lists routinely hitting 60+ files (CFA
            // RAMP, 260 Madison) the expanded-by-default version
            // pushed every other block below the fold. Operators
            // flagged the auto-scroll distance as "too long for a
            // customer share". Click the header (or chevron) on
            // any version to expand it. `isLatest` stays
            // semantically correct (idx===0) so the green "Latest"
            // badge + confirm-gate-on-older-downloads stay intact.
            <VersionSection
              key={v.revision_id}
              version={v}
              isLatest={idx === 0}
              defaultOpen={false}
              orderedGroups={orderedGroups}
              shareToken={shareToken}
            />
          ))}
          {unfiledCount > 0 && (
            <FileGrid
              files={unfiledFiles}
              groupedByExt={groupFilesByExt(unfiledFiles, orderedGroups)}
              shareToken={shareToken}
            />
          )}
        </div>
      ) : (
        <FileGrid
          files={sortedFlatRows}
          groupedByExt={groupFilesByExt(sortedFlatRows, orderedGroups)}
          shareToken={shareToken}
        />
      )}
    </Section>
  );
}

/**
 * One collapsible version section inside a multi-version
 * `FileListBlock`. Latest version is open by default; every
 * older version is collapsed so the recipient sees "what's
 * current" without scrolling past archive history. Click the
 * header (or chevron) to toggle.
 *
 * Why a controlled disclosure instead of native `<details>`?
 * Two reasons:
 *  - The header has interactive children (per-version
 *    Download all (ZIP) button) which `<summary>` would
 *    eat clicks for if not wrapped carefully.
 *  - We want the chevron icon styled inline with the rest of
 *    the brand, and `<details>` polyfilling the marker is
 *    trickier than just rendering a `ChevronRight` /
 *    `ChevronDown` pair.
 */
function VersionSection({
  version,
  isLatest,
  defaultOpen,
  orderedGroups,
  shareToken,
}: {
  version: {
    revision_id: string;
    revision_number: number | null;
    reviewer_notes: string | null;
    revision_name: string | null;
    revision_date: string | null;
    files: ResolvedFileAsset[];
  };
  /**
   * Whether this is the most-recent version. Drives the green
   * "Latest" badge + the confirm-dialog gate on older-version
   * downloads. Independent of the disclosure's open state — see
   * `defaultOpen` below.
   */
  isLatest: boolean;
  /**
   * Whether the disclosure starts expanded. Pre-2026-05-06 this
   * was implicitly tied to `isLatest`; now opt-in so a Latest
   * version can render collapsed-by-default on long file
   * bundles without losing its "Latest" badge or its always-
   * visible ZIP button.
   */
  defaultOpen?: boolean;
  orderedGroups: Array<{
    ext: string;
    list: ResolvedFileAsset[];
    priority: number;
  }>;
  shareToken: string | null;
}) {
  const [open, setOpen] = useState(defaultOpen ?? isLatest);
  const baseLabel = `V${version.revision_number ?? "?"}`;
  // Per-version ZIP — same share-token-gated zip route as the
  // bundle-wide download, just with `?ids=` narrowed to this
  // version's files. Hidden when the version has fewer than 2
  // files (the per-row Download button covers that case).
  const versionZipUrl =
    shareToken && version.files.length > 1
      ? `/api/share/${encodeURIComponent(shareToken)}/zip?ids=${encodeURIComponent(version.files.map((f) => f.id).join(","))}`
      : null;

  // Show the per-version ZIP button on:
  //   - the LATEST version always (no risk of downloading
  //     stale work, no need to hide the affordance), AND
  //   - any OLDER version only when its section is expanded
  //     (collapsed older versions hide the button so the
  //     header reads as a quiet archive entry, not an action
  //     surface).
  const showZipButton = !!versionZipUrl && (isLatest || open);

  // Older-version downloads gate behind a confirm() dialog so
  // the recipient can't silently grab a stale bundle and treat
  // it as authoritative. The latest version is always free to
  // download — that's the whole point of "Latest".
  const confirmMessage = isLatest
    ? null
    : `You're about to download ${baseLabel}${
        version.revision_name ? ` — ${version.revision_name}` : ""
      }. This is NOT the latest version. Continue?`;

  // Operator-set editorial date for the version (migration 372).
  // Independent of `delivered_at` and `created_at`; rendered
  // next to the V-label as a small dot-separated segment.
  const formattedDate = formatVersionDate(version.revision_date);

  // First-line preview of the version's reviewer notes — surfaced
  // inline on the collapsed header so the recipient sees what
  // changed without having to expand the section. The full notes
  // (preserving line breaks) still render in the expanded body
  // below — this preview is additive. `firstNonEmptyLine` skips
  // multi-paragraph leading blank lines + trims so the chip
  // never starts with whitespace.
  const trimmedNotes = version.reviewer_notes?.trim() ?? "";
  const inlineNotePreview = firstNonEmptyLine(version.reviewer_notes);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left -ml-1 px-2 py-2 sm:py-0.5 min-h-11 sm:min-h-0 rounded hover:bg-muted/30 transition-colors touch-manipulation min-w-0 flex-1"
          aria-expanded={open}
          aria-controls={`version-section-${version.revision_id}`}
        >
          <span
            className="text-muted-foreground transition-transform shrink-0"
            style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
            aria-hidden
          >
            ▸
          </span>
          <span className="text-base sm:text-lg font-bold shrink-0">{baseLabel}</span>
          {version.revision_name && (
            <>
              <span className="text-muted-foreground text-sm shrink-0">—</span>
              <span className="text-sm font-medium shrink-0">{version.revision_name}</span>
            </>
          )}
          {isLatest && (
            <span className="text-[10px] uppercase tracking-wider text-emerald-700 vs-dark:text-emerald-400 border border-emerald-300 vs-dark:border-emerald-800 rounded px-1.5 py-0.5 shrink-0">
              Latest
            </span>
          )}
          {formattedDate && (
            <span className="text-[11px] text-muted-foreground shrink-0">
              · {formattedDate}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground shrink-0">
            · {version.files.length} file{version.files.length === 1 ? "" : "s"}
          </span>
          {/* Inline note preview — only when the section is
              collapsed and a note exists. Truncates with
              text-ellipsis so a one-paragraph note still tells
              the recipient what's new at a glance. The full
              note (preserving newlines) renders inside the
              expanded body below; this preview is additive. */}
          {!open && inlineNotePreview && (
            <span
              className="text-[11px] text-muted-foreground italic min-w-0 truncate"
              title={trimmedNotes}
            >
              · {inlineNotePreview}
            </span>
          )}
        </button>
        {showZipButton && versionZipUrl && (
          <DownloadZipButton
            url={versionZipUrl}
            filename={`${(version.revision_name ? `${baseLabel} - ${version.revision_name}` : baseLabel).replace(/[^a-z0-9-_ ]+/gi, "-")}.zip`}
            idleLabel={`Download ${baseLabel} as ZIP`}
            confirmMessage={confirmMessage}
          />
        )}
      </div>
      {open && (
        <div
          id={`version-section-${version.revision_id}`}
          className="ml-3 sm:ml-5 pl-3 sm:pl-4 border-l-2 vs-border-soft space-y-2"
        >
          {version.reviewer_notes && (
            <div className="rounded-md border vs-border-soft bg-muted/20 px-3 py-2 text-sm whitespace-pre-line">
              {version.reviewer_notes}
            </div>
          )}
          <FileGrid
            files={version.files}
            groupedByExt={groupFilesByExt(version.files, orderedGroups)}
            shareToken={shareToken}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Slice a flat file array into the same group structure used
 * by `FileListBlock`'s top-level grouping. Used by the
 * multi-version branch where each version renders its own
 * grouped grid.
 */
function fileHasDisplaySortKey(f: ResolvedFileAsset): boolean {
  const k = f.display_sort_key;
  return k != null && Number.isFinite(k);
}

function groupFilesByExt(
  files: ResolvedFileAsset[],
  templateOrder: Array<{ ext: string; priority: number }>,
): Array<{ ext: string; list: ResolvedFileAsset[]; priority: number }> {
  const anyKeyed = files.some(fileHasDisplaySortKey);
  if (anyKeyed) {
    const sorted = [...files].sort((a, b) => compareBimCadFileOrder(a, b));
    return [{ ext: "__manual__", list: sorted, priority: 1_000_000 }];
  }
  const map = new Map<string, ResolvedFileAsset[]>();
  for (const f of files) {
    const ext = fileExt(f.file_name) || "other";
    const list = map.get(ext) ?? [];
    list.push(f);
    map.set(ext, list);
  }
  return Array.from(map.entries())
    .map(([ext, list]) => ({
      ext,
      list: [...list].sort((a, b) => compareBimCadFileOrder(a, b)),
      priority:
        templateOrder.find((g) => g.ext === ext)?.priority ??
        groupPriority(ext),
    }))
    .sort((a, b) => b.priority - a.priority || a.ext.localeCompare(b.ext));
}

function FileGrid({
  files,
  groupedByExt,
  shareToken,
}: {
  files: ResolvedFileAsset[];
  groupedByExt: Array<{ ext: string; list: ResolvedFileAsset[]; priority: number }>;
  shareToken: string | null;
}) {
  const skipFloorBands = files.some(fileHasDisplaySortKey);
  const useFolders = shouldUseFolderTree(
    files,
    (f) => f.file_name,
    (f) => f.display_sort_key,
  );

  function renderBandedList(list: ResolvedFileAsset[]) {
    if (skipFloorBands) {
      return list.map((f) => (
        <FileRow key={f.id} file={f} shareToken={shareToken} />
      ));
    }
    return interleaveFloorBands(list, (row) => row.file_name).map((row, i) =>
      row.kind === "band" ? (
        <li key={`band-${i}-${row.label}`} className={BIM_FLOOR_BAND_ROW_CLASS}>
          <span className={BIM_FLOOR_BAND_TITLE_CLASS}>{row.label}</span>
        </li>
      ) : (
        <FileRow key={row.item.id} file={row.item} shareToken={shareToken} />
      ),
    );
  }

  if (useFolders) {
    return (
      <BimCadFolderTree
        files={files}
        getFileName={(f) => f.file_name}
        getRowKey={(f) => f.id}
        renderFile={(f) => (
          <FileRow
            key={f.id}
            file={f}
            shareToken={shareToken}
            inFolderTree
          />
        )}
      />
    );
  }

  // Single-extension bundles (e.g. all PDFs) render flat — no
  // need for a header that just repeats the extension. Multi-
  // extension bundles get type-grouped rails.
  if (groupedByExt.length <= 1) {
    return (
      <ul className="divide-y vs-border-soft rounded-md border vs-border-soft">
        {renderBandedList(files)}
      </ul>
    );
  }
  return (
    <div className="space-y-3">
      {groupedByExt.map((g) => (
        <FileTypeSubgroup
          key={g.ext}
          ext={g.ext}
          list={g.list}
          renderBandedList={renderBandedList}
        />
      ))}
    </div>
  );
}

/**
 * Collapsible format-type subgroup inside a multi-extension
 * `FileGrid`. RVT / DWG / PDF / etc. each render as their own
 * controlled disclosure, collapsed by default so a 60-file
 * bundle doesn't dominate the viewport. Expanding shows the
 * existing floor-banded list (SUB-CELLAR 2 / SUB-CELLAR / …)
 * unchanged.
 *
 * Visual hierarchy: format-type headers render bigger
 * (`text-sm font-semibold`) and are wrapped in a soft-bordered
 * container (`bg-muted/10` + `border vs-border-soft`) so they
 * read as the primary level above the per-floor sub-headers
 * nested inside.
 *
 * Exported (lower-case, named) so the component test in
 * `file-type-subgroup.test.tsx` can mount it in isolation. The
 * production import from `FileGrid` doesn't change.
 */
export function FileTypeSubgroup({
  ext,
  list,
  renderBandedList,
}: {
  ext: string;
  list: ResolvedFileAsset[];
  renderBandedList: (l: ResolvedFileAsset[]) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const label =
    ext === "other"
      ? "Other"
      : ext === "__manual__"
        ? "Files"
        : ext.toUpperCase();
  const sectionId = `file-type-${ext.replace(/[^a-z0-9_-]/gi, "_")}`;
  return (
    <div className="rounded-md border vs-border-soft bg-muted/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted/20 transition-colors touch-manipulation min-h-11 sm:min-h-0"
        aria-expanded={open}
        aria-controls={sectionId}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-muted-foreground transition-transform shrink-0"
            style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
            aria-hidden
          >
            ▸
          </span>
          <span className="text-sm font-semibold tracking-wide">{label}</span>
          <span className={BIM_FILE_TYPE_COUNT_CLASS}>
            {list.length} file{list.length === 1 ? "" : "s"}
          </span>
        </div>
      </button>
      {open && (
        <div id={sectionId} className="border-t vs-border-soft bg-background">
          <ul className="divide-y vs-border-soft">{renderBandedList(list)}</ul>
        </div>
      )}
    </div>
  );
}

function FileRow({
  file,
  shareToken,
  inFolderTree = false,
}: {
  file: ResolvedFileAsset;
  shareToken: string | null;
  inFolderTree?: boolean;
}) {
  const url = downloadUrlForFile(file, shareToken);
  const pdfActions = shareToken && isPdfAsset(file);
  const ext = fileExt(file.file_name);
  const cadBim = isCadBim(file);
  const note = file.note?.trim() ?? "";
  const fullName = file.file_name ?? "Untitled";
  const visibleName = inFolderTree
    ? deliverableFileBaseName(file.file_name) || fullName
    : fullName;
  const leafStyle = useFolderTreeLeafStyle();
  return (
    <li
      className={cn("py-2.5 flex items-start gap-3", !leafStyle && "px-3")}
      style={leafStyle}
    >
      <span
        className={`shrink-0 mt-0.5 text-[9px] uppercase font-semibold tracking-wider opacity-80 px-1.5 py-0.5 rounded ${cadBim ? "bg-(--vs-red)/20 text-(--vs-red-text)" : "bg-muted text-muted-foreground"}`}
        title={`${ext.toUpperCase()} file`}
      >
        {ext || "?"}
      </span>
      <div className="flex-1 min-w-0">
        {/* Full filename — `break-all` lets long single-token
            BIM/CAD names ("260 Madison Avenue - P01-Sheet -
            A114 - Section 1.dwg") wrap across lines instead of
            truncating to "260 Madison Avenue…" with no way to
            see what they are. Files with an operator-set note
            get a brand-red star inline so the recipient can
            spot the noteworthy files at a glance without
            having to read every callout chip. */}
        <p
          className="text-sm break-all leading-snug"
          title={file.file_name ?? undefined}
        >
          {note && (
            <Star
              className="inline-block h-3.5 w-3.5 mr-1 -mt-0.5 fill-amber-400 text-amber-400 align-middle shrink-0"
              aria-label="Has note"
            />
          )}
          {visibleName}
        </p>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
          <span>{formatBytes(file.file_size)}</span>
          {file.created_at && (
            <span>· {new Date(file.created_at).toLocaleDateString()}</span>
          )}
          {file.mime_type && file.mime_type !== "application/octet-stream" && (
            <span className="font-mono">· {file.mime_type}</span>
          )}
        </div>
        {/* Operator-set per-file note (migration 371). Renders
            inline as a soft callout chip below the metadata row
            so the customer sees the operator's "Final, supersedes
            earlier RVT" / "PDF version of the BIM" / etc. without
            having to expand a panel. Wrap on long notes — these
            are capped at 280 chars server-side so a truncating
            single line risks losing the punchline. */}
        {note && (
          <p
            className="mt-1.5 inline-block max-w-full rounded-md border vs-border-soft bg-muted/30 px-2 py-1 text-[11px] text-foreground/80 italic leading-snug whitespace-pre-line"
            title={note}
          >
            {note}
          </p>
        )}
      </div>
      {pdfActions ? (
        <PdfViewDownloadActions file={file} shareToken={shareToken} />
      ) : url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={fileActionBtnClass}
        >
          <Download className="h-3 w-3" aria-hidden />
          Download
        </a>
      ) : (
        <span
          className="shrink-0 text-[11px] italic text-muted-foreground"
          title="This file isn't downloadable from this share — contact the sender."
        >
          No download
        </span>
      )}
    </li>
  );
}

function ScanListBlock({
  config,
  scans,
}: {
  config: ScanListConfig;
  scans: Record<string, ResolvedScan>;
}) {
  // Accept both shapes — `deliverable_ids` (post-216) + `scan_ids`
  // (pre-refactor rows).
  const ids =
    (config as { deliverable_ids?: string[]; scan_ids?: string[] }).deliverable_ids ??
    (config as { scan_ids?: string[] }).scan_ids ??
    [];
  const rows = ids
    .map((id) => scans[id])
    .filter((r): r is ResolvedScan => !!r);
  if (rows.length === 0) {
    return (
      <Section title={config.title}>
        <NotSet message="No scans attached yet." />
      </Section>
    );
  }
  return (
    <Section title={config.title}>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((scan) => (
          <li
            key={scan.id}
            className="vs-glass-card overflow-hidden"
          >
            <div className="aspect-[4/3] bg-muted relative">
              {scan.thumbnail_url && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={scan.thumbnail_url}
                  alt={scan.space_name ?? scan.id}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </div>
            <div className="p-3 space-y-1">
              <p className="text-sm font-medium truncate">
                {scan.space_name ?? `Scan ${scan.id.slice(0, 8)}`}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {[scan.location_name, scan.provider].filter(Boolean).join(" · ")}
              </p>
              {scan.showcase_link && (
                <a
                  href={scan.showcase_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-[color:var(--sv-brand)] inline-flex items-center gap-1 mt-1 hover:underline"
                >
                  Open showcase
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ── Identity / contact ────────────────────────────────────────────

function ContactCardBlock({ config }: { config: ContactCardConfig }) {
  // Hooks MUST run unconditionally — previously this component had
  // `useState` after an early `return null` which violates the Rules
  // of Hooks and throws "Rendered fewer hooks than expected" as soon
  // as the block config flips between "has content" and "empty" at
  // runtime (e.g. the author clearing the name field in the editor).
  // Move state above the early return.
  const [copiedField, setCopiedField] = useState<"email" | "phone" | null>(null);
  const hasAny = config.name || config.email || config.phone;
  if (!hasAny) return null;
  const copy = (field: "email" | "phone", value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
      })
      .catch(() => {});
  };
  return (
    // Outer wrapper centers the card + caps its width. Without
    // the cap the card stretches across the full section,
    // which left a single email + phone floating across ~900px
    // of empty space on desktop. `max-w-lg` keeps the photo +
    // contact rail compact and reads as an intentional
    // "person card" composition; `mx-auto` centers it inside
    // the parent block container. Mobile is unchanged — the
    // card hits its natural width on narrow viewports.
    <div className="mx-auto w-full max-w-lg">
    <div className="vs-glass-card p-4 sm:p-5 flex items-stretch gap-4 sm:gap-5">
      {/* Photo column. Larger + square (rounded-lg) so a
          portrait shot doesn't get its bottom half hard-cropped
          by a circular avatar mask. `object-cover` +
          `object-top` keeps the face in frame on tall photos
          while letting the rest of the photo fill the tile
          when it's roughly square. Spans the height of the
          right-column list so the card reads as a single
          composition instead of an avatar floating beside a
          paragraph. */}
      <div className="shrink-0 self-stretch">
        {config.photo_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={config.photo_url}
            alt={config.name ?? ""}
            className="h-full w-24 sm:w-28 min-h-[6rem] rounded-lg object-cover object-top"
          />
        ) : (
          <div className="h-full w-24 sm:w-28 min-h-[6rem] rounded-lg bg-muted" />
        )}
      </div>

      {/* Right column — stacked list. Name + role at the top,
          contact methods below with each method on its own
          row + an inline Copy affordance. */}
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        {config.name && (
          <p className="text-base font-semibold leading-tight">{config.name}</p>
        )}
        {config.role && (
          <p className="text-xs text-muted-foreground">{config.role}</p>
        )}
        <ul className="mt-2 space-y-1.5">
          {config.email && (
            <li className="flex items-center gap-2 flex-wrap">
              <a
                href={`mailto:${config.email}`}
                className="inline-flex items-center gap-1.5 rounded-md border vs-border-soft px-3 py-2 sm:py-1 min-h-9 sm:min-h-0 text-xs hover:bg-muted transition-colors touch-manipulation"
              >
                <Mail className="h-3 w-3" />
                {config.email}
              </a>
              <button
                type="button"
                onClick={() => copy("email", config.email!)}
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 py-2 sm:py-0 min-h-9 sm:min-h-0 rounded touch-manipulation"
              >
                <Copy className="h-3 w-3" />
                {copiedField === "email" ? "Copied" : "Copy"}
              </button>
            </li>
          )}
          {config.phone && (
            <li className="flex items-center gap-2 flex-wrap">
              <a
                href={`tel:${config.phone}`}
                className="inline-flex items-center gap-1.5 rounded-md border vs-border-soft px-3 py-2 sm:py-1 min-h-9 sm:min-h-0 text-xs hover:bg-muted transition-colors touch-manipulation"
              >
                <Phone className="h-3 w-3" />
                {config.phone}
              </a>
              <button
                type="button"
                onClick={() => copy("phone", config.phone!)}
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 py-2 sm:py-0 min-h-9 sm:min-h-0 rounded touch-manipulation"
              >
                <Copy className="h-3 w-3" />
                {copiedField === "phone" ? "Copied" : "Copy"}
              </button>
            </li>
          )}
        </ul>
      </div>
    </div>
    </div>
  );
}

// ── External media / bookings / embed ────────────────────────────

/**
 * Turn a YouTube watch/short/youtu.be URL into an embed URL. Falls
 * back to the original URL if we don't recognise the shape so users
 * can paste anything that loads in an iframe.
 */
function youtubeEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    if (hostMatchesDomain(u.hostname, "youtube.com")) {
      const vid = u.searchParams.get("v");
      if (vid) return `https://www.youtube.com/embed/${vid}`;
      // /shorts/VIDEOID
      const m = u.pathname.match(/\/shorts\/([\w-]{6,})/);
      if (m) return `https://www.youtube.com/embed/${m[1]}`;
    }
    if (u.hostname === "youtu.be") {
      const id = u.pathname.replace(/^\//, "");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
  } catch {
    /* fall through */
  }
  return url;
}

function vimeoEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    if (hostMatchesDomain(u.hostname, "vimeo.com")) {
      const id = u.pathname.match(/\/(\d+)/)?.[1];
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch {
    /* fall through */
  }
  return url;
}

function VideoBlock({ config }: { config: VideoConfig }) {
  const { enabled: enhanced } = usePublicComposition();
  if (!config.url) {
    return <NotSet message="No video URL set." />;
  }
  if (enhanced) {
    const safeUrl = publicVideoSource(config.url);
    if (!safeUrl) return <NotSet message="A valid video URL is required." />;
    const src = config.provider === "youtube" ? youtubeEmbedUrl(safeUrl) : config.provider === "vimeo" ? vimeoEmbedUrl(safeUrl) : safeUrl;
    return <Section title={config.title}><div className="aspect-video w-full overflow-hidden rounded-lg bg-black"><CompositionViewerMount label="video"><PublicMediaFrame src={src} title={config.title || "Video"} kind={config.provider === "direct" ? "video" : "iframe"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" /></CompositionViewerMount></div></Section>;
  }
  if (config.provider === "direct") {
    return (
      <Section title={config.title}>
        <video
          src={config.url}
          controls
          className="w-full rounded-lg bg-black max-h-[70vh]"
          preload="metadata"
        />
      </Section>
    );
  }
  const src =
    config.provider === "youtube"
      ? youtubeEmbedUrl(config.url)
      : config.provider === "vimeo"
        ? vimeoEmbedUrl(config.url)
        : config.url;
  return (
    <Section title={config.title}>
      <div className="w-full aspect-video rounded-lg overflow-hidden bg-black">
        <iframe
          src={src}
          title={config.title ?? "Video"}
          className="w-full h-full"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </Section>
  );
}

/**
 * Calendly exposes a specific embed script, Cal.com has its own
 * inline embed, Google Appointments renders as a plain iframe. In
 * all three cases the simplest path is to iframe the page directly;
 * providers handle responsiveness themselves.
 */
function CalendarBookingBlock({
  config,
}: {
  config: CalendarBookingConfig;
}) {
  if (!config.url) {
    return <NotSet message="No booking URL set." />;
  }
  return (
    <Section title={config.title}>
      <div className="rounded-lg overflow-hidden border vs-border-soft">
        <iframe
          src={config.url}
          title={config.title ?? "Schedule an appointment"}
          className="w-full"
          style={{ height: 720 }}
          loading="lazy"
        />
      </div>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-1">
        <Calendar className="h-3 w-3" />
        Powered by {providerLabel(config.provider)}
      </p>
    </Section>
  );
}

function providerLabel(p: CalendarBookingConfig["provider"]): string {
  switch (p) {
    case "calcom":
      return "Cal.com";
    case "calendly":
      return "Calendly";
    case "google_appointments":
      return "Google Appointments";
    case "native":
      return "this site";
  }
}

function RestaurantBookingBlock({
  config,
}: {
  config: RestaurantBookingConfig;
}) {
  if (!config.url) {
    return <NotSet message="No reservation URL set." />;
  }
  return (
    <Section title={config.title}>
      <div className="rounded-lg overflow-hidden border vs-border-soft">
        <iframe
          src={config.url}
          title={config.title ?? "Make a reservation"}
          className="w-full"
          style={{ height: 620 }}
          loading="lazy"
        />
      </div>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-1">
        <Utensils className="h-3 w-3" />
        Powered by {restaurantProviderLabel(config.provider)}
      </p>
    </Section>
  );
}

function restaurantProviderLabel(
  p: RestaurantBookingConfig["provider"],
): string {
  switch (p) {
    case "opentable":
      return "OpenTable";
    case "resy":
      return "Resy";
    case "tock":
      return "Tock";
    case "sevenrooms":
      return "SevenRooms";
  }
}

function ExternalEmbedBlock({ config }: { config: ExternalEmbedConfig }) {
  if (!config.url) {
    return <NotSet message="No embed URL set." />;
  }
  const sandbox =
    config.sandbox ?? "allow-scripts allow-same-origin allow-forms allow-popups";
  return (
    <Section title={config.title}>
      <div className="rounded-lg overflow-hidden border vs-border-soft">
        <iframe
          src={config.url}
          title={config.title ?? "Embedded content"}
          className="w-full"
          style={{ height: Math.max(200, Math.min(1400, config.height ?? 500)) }}
          sandbox={sandbox}
          loading="lazy"
        />
      </div>
    </Section>
  );
}

function safeExternalButtonHref(raw: string): string | null {
  return safeExternalHref(raw);
}

function LinkButtonsBlock({ config }: { config: LinkButtonsConfig }) {
  const layout = config.layout === "stacked" ? "stacked" : "inline";
  const resolved = (config.items ?? [])
    .map((item) => {
      const href = safeExternalButtonHref(item.url ?? "");
      if (!href) return null;
      const label = (item.label ?? "").trim() || href;
      return { href, label };
    })
    .filter(Boolean) as { href: string; label: string }[];

  if (resolved.length === 0) {
    return <NotSet message="Add at least one button with a valid URL." />;
  }

  const title = config.title?.trim() ?? "";

  return (
    <Section title={title || null} align="center">
      <div
        className={
          layout === "stacked"
            ? "flex flex-col gap-2 items-stretch sm:max-w-md mx-auto"
            : "flex flex-wrap gap-2 justify-center"
        }
      >
        {resolved.map((b, idx) => (
          <a
            key={`${idx}-${b.href}`}
            href={b.href}
            target="_blank"
            rel="noopener noreferrer"
            className="vs-btn-solid inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium text-center"
          >
            {b.label}
          </a>
        ))}
      </div>
    </Section>
  );
}

// ── Deliverable-aware blocks (migration 256) ─────────────────────

/**
 * Location card — same design as the dashboard locations page
 * (`LocationViewerTabs` for the body, AccentPill header with
 * address + Map / Directions chips). Built up from the shared
 * `LocationViewerTabs` component so the share page and the
 * dashboard stay visually identical; the only difference is the
 * Parcel View tab is hidden via `hideParcel` because parcel
 * boundaries are operator-internal scoping data, not something
 * we surface to the property's recipient.
 *
 * The legacy inline tabs (and the matterport-tab-inside-the-card
 * concept) were retired in favour of this. Matterport now renders
 * as its own sibling block beneath the card — cleaner separation
 * and one less special case in the auto-composer.
 */
function LocationCardBlock({
  config,
  locations,
  googleMapsKey,
}: {
  config: LocationCardConfig;
  locations: Record<string, ResolvedLocation>;
  scans: Record<string, ResolvedScan>;
  googleMapsKey: string | null;
}) {
  const loc = config.location_id ? locations[config.location_id] ?? null : null;

  // Effective values — resolved location row > inline overrides.
  const lat = loc?.lat ?? config.lat ?? null;
  const lng = loc?.lng ?? config.lng ?? null;
  const addr1 = loc?.address_line_1 ?? config.address_line_1 ?? null;
  const city = loc?.city ?? config.city ?? null;
  const state = loc?.state ?? config.state ?? null;
  const country = loc?.country ?? null;
  const postal = loc?.postal_code ?? config.postal_code ?? null;
  const programLabel = loc?.program_name ?? null;

  // Single-line address composition matches the dashboard
  // ("260 Madison Avenue, New York, NY, United States, 10016") —
  // commas between every populated piece, with the country in
  // its conventional pre-postal-code position.
  const addressLine = [addr1, city, state, country, postal]
    .filter((piece): piece is string => Boolean(piece))
    .join(", ");

  const hasCoords = lat !== null && lng !== null;
  const coordsParam = hasCoords
    ? encodeURIComponent(`${lat},${lng}`)
    : null;

  if (!loc && !addressLine && !hasCoords) {
    return (
      <NotSet message="Location card needs an address or coordinates to render." />
    );
  }

  // Aerial-tab needs a human address string for Google's API
  // (the tab does its own fallback when no flyover video is
  // cached). Compose minus postal so Google's geocoder isn't
  // confused by zip-only matches.
  const aerialAddress = [addr1, city, state, country]
    .filter((piece): piece is string => Boolean(piece))
    .join(", ");

  return (
    <section className="vs-glass-card overflow-hidden">
      {/* Header — full address as the primary line + chip row of
          program label / Map / Directions affordances. The previous
          design opened with a red AccentPill of the location name
          ("260 Madison"), but the address line right below already
          contains "260 Madison Avenue, …" so the pill duplicated the
          first 9 characters and read as visual noise. Address now
          carries the eye instead. */}
      <div className="px-4 sm:px-6 py-4 space-y-2 text-center">
        {addressLine && (
          <p className="text-base sm:text-lg font-medium leading-snug">
            {addressLine}
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap justify-center">
          {programLabel && (
            <span
              className="inline-flex items-center gap-1 text-[10px] rounded-sm border vs-border-soft bg-muted/40 px-1.5 py-0.5 text-muted-foreground"
              title="Project / program label"
            >
              Program · {programLabel}
            </span>
          )}
          {coordsParam && (
            <>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${coordsParam}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] rounded-md border vs-border-soft bg-muted/40 px-2.5 py-2 sm:py-1 min-h-9 sm:min-h-0 text-muted-foreground hover:text-foreground transition-colors touch-manipulation"
                title="Open on Google Maps"
              >
                Map ↗
              </a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${coordsParam}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] rounded-md border vs-border-soft bg-muted/40 px-2.5 py-2 sm:py-1 min-h-9 sm:min-h-0 text-muted-foreground hover:text-foreground transition-colors touch-manipulation"
                title="Get driving directions"
              >
                Directions ↗
              </a>
            </>
          )}
        </div>
      </div>

      {/* Tabbed body — Aerial · Map · Street · 3D · Aerial Video.
          Parcel View is intentionally hidden on public shares
          (operator-internal scoping data). The component lazy-
          mounts each tab's payload so a share with the default
          (Aerial / Mapbox static PNG) tab pays zero WebGL
          contexts on first paint. */}
      {hasCoords ? (
        <LocationSurface
          data={{
            id: loc?.id ?? config.location_id ?? null,
            name: loc?.name ?? null,
            addressLine1: addr1,
            city,
            state,
            country,
            postalCode: postal,
            lat: lat as number,
            lng: lng as number,
          }}
          audience="customer"
          showHeader={false}
          showAddress={false}
          preset={{
            viewer: {
              height: "min(70dvh, 800px)",
              lazyMount: false,
            },
          }}
        />
      ) : (
        <NotSet message="No coordinates for this location yet — viewer tabs unavailable." />
      )}
    </section>
  );
}

/* `googleStaticMapUrl` is referenced by the legacy
   `google_map` / `aerial_image` blocks below. Keep this comment
   so future edits don't accidentally remove the helper if the
   `LocationCardBlock` rewrite makes it look unused at first
   glance. */
void googleStaticMapUrl;

/**
 * BIM/CAD viewer block — embeds the Autodesk Viewer with the
 * federation Models panel from PR 075a330. URN list is the
 * latest revision's APS files, sorted by `cadBimLoadPriority` so
 * RVT lands first if present. Public APS token is minted via
 * `/api/autodesk/public-token?token=<share-token>` (separate
 * APS app with `viewables:read` scope only — see migration's
 * deployment notes).
 */
function BimViewerBlock({
  config,
  scans,
  fileAssets,
  shareToken,
}: {
  config: BimViewerConfig;
  scans: Record<string, ResolvedScan>;
  fileAssets: Record<string, ResolvedFileAsset>;
  shareToken: string | null;
}) {
  const { privatePreview } = usePublicComposition();
  // Operator previews use the existing session + internal-membership gate;
  // public viewers must keep using their narrower share-token endpoint.
  const tokenEndpoint = privatePreview ? "/api/autodesk/token" : shareToken ? `/api/autodesk/public-token?token=${encodeURIComponent(shareToken)}` : null;
  const [activeUrn, setActiveUrn] = useState<string | null>(null);
  const id = config.deliverable_id;
  const deliverable = id ? scans[id] : null;
  const allUrns = deliverable?.bim_details?.aps_urns ?? [];
  const latestRev = deliverable?.bim_details?.latest_revision ?? null;

  // Latest-version-only mode: filter the federation set down
  // to URNs from the highest revision_number. Operators flip
  // this on for "clean" customer-facing shares where the
  // version history isn't part of the story. Older URNs are
  // dropped entirely — they don't appear in the Models panel
  // at all (vs. the default mode where they collapse into
  // older-version sections).
  const urns = (() => {
    if (!config.latest_version_only) return allUrns;
    const maxRev = allUrns.reduce<number>(
      (m, u) => Math.max(m, u.revisionNumber ?? -1),
      -1,
    );
    if (maxRev < 0) return allUrns;
    return allUrns.filter((u) => (u.revisionNumber ?? -1) === maxRev);
  })();

  // Inline file list — only when the operator explicitly opts
  // in via `show_file_list`. The resolver pre-stamped the
  // deliverable's file ids on `config.derived_file_asset_ids`;
  // we synthesise a FileListConfig inline so the existing
  // FileListBlock body handles the per-version grouping +
  // download buttons + reviewer-notes callouts.
  //
  // When `latest_version_only` is also on, we additionally
  // filter the file ids to the latest revision's files —
  // keeps the viewer + file list consistent so a "clean"
  // share doesn't sneak older versions in via the downloads
  // rail. ResolvedFileAsset already carries `revision_number`
  // (populated by the resolver via the revisionMap fetch), so
  // we can compute max-revision client-side without any extra
  // round trips.
  const showFileList = config.show_file_list === true;
  const allDerivedIds = (
    (config as { derived_file_asset_ids?: string[] }).derived_file_asset_ids
  ) ?? [];
  const derivedFileIds = (() => {
    if (!showFileList || allDerivedIds.length === 0) return allDerivedIds;
    if (!config.latest_version_only) return allDerivedIds;
    const fileObjs = allDerivedIds
      .map((fid) => fileAssets[fid])
      .filter((f): f is ResolvedFileAsset => Boolean(f));
    const maxRevNum = fileObjs.reduce<number>(
      (m, f) => Math.max(m, f.revision_number ?? -1),
      -1,
    );
    if (maxRevNum < 0) return allDerivedIds;
    return fileObjs
      .filter((f) => (f.revision_number ?? -1) === maxRevNum)
      .map((f) => f.id);
  })();

  if (!id || !deliverable) {
    return <NotSet message="No BIM deliverable selected." />;
  }
  if (urns.length === 0) {
    return <NotSet message="No BIM files have been uploaded yet." />;
  }
  if (!tokenEndpoint) {
    return <NotSet message="Viewer not available outside a share link." />;
  }

  // Resolve the active model's per-file note (migration 371) by
  // walking `fileAssets` for an aps:<urn> match. Cheaper than a
  // pre-built map — the typical scene has 1-30 URNs and this
  // recomputes only on `activeUrn` change. Falls back to null
  // when the URN doesn't correspond to a known file_asset (e.g.
  // legacy uploads without the matching row, viewer state
  // transitioning between models).
  const activeFileNote = (() => {
    if (!activeUrn) return null;
    const target = `aps:${activeUrn}`;
    for (const asset of Object.values(fileAssets)) {
      if (asset.storage_path === target) {
        const trimmed = asset.note?.trim();
        return trimmed && trimmed.length > 0 ? trimmed : null;
      }
    }
    return null;
  })();

  return (
    <div className="space-y-2">
      <RevisionHeaderStrip revision={latestRev} />
      {activeFileNote && (
        <ActiveFileNoteBanner note={activeFileNote} />
      )}
      <VirtualSpaceExploreGate label="BIM/CAD viewer" engageOnWheel>
        <CompositionViewerMount label="BIM viewer" height={config.height ?? "min(70dvh, 800px)"}><AutodeskViewer
          urns={urns}
          height={config.height ?? "min(70dvh, 800px)"}
          tokenEndpoint={tokenEndpoint}
          onActiveModelChange={setActiveUrn}
        /></CompositionViewerMount>
      </VirtualSpaceExploreGate>
      {showFileList && derivedFileIds.length > 0 && (
        <div className="mt-4">
          <FileListBlock
            config={{
              file_asset_ids: derivedFileIds,
              deliverable_id: id,
              title: null,
              layout: "list",
            }}
            assets={fileAssets}
            shareToken={shareToken}
          />
        </div>
      )}
      <SpatialAsBuiltDisclaimer />
    </div>
  );
}

/**
 * Banner above the Autodesk Viewer that surfaces the
 * operator's per-file note (migration 371) for whichever
 * model is currently active in the scene. Lets the customer
 * see "PDF version of the BIM" or "Final, supersedes earlier
 * RVT" without having to expand the file panel.
 *
 * Keeps the visual treatment lightweight — a soft callout
 * with an icon. Auto-hides when the active model has no
 * note set; the renderer above only mounts this when the
 * note exists.
 */
function ActiveFileNoteBanner({ note }: { note: string }) {
  return (
    <div
      role="status"
      className="rounded-md border vs-border-soft bg-muted/20 px-3 py-2 flex items-start gap-2 text-sm"
    >
      <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Note
        </p>
        <p className="mt-0.5 text-sm whitespace-pre-line break-words">{note}</p>
      </div>
    </div>
  );
}

/**
 * Compact "V2 · Latest" chip + reviewer-notes callout that
 * sits above the inline BIM / CAD / floor-plan viewer. Renders
 * nothing when the deliverable has zero or one revisions and
 * no notes — at that point there's no signal to surface and
 * the chip would just be visual noise on a fresh upload.
 *
 * Why above the viewer (not below)? When V2 lands, the
 * recipient's first instinct is "something's different — what
 * changed?" — they need the version label + notes BEFORE the
 * viewer asks for ~10 s of focus. Below the viewer would put
 * the answer behind a 600-px scroll.
 */
function RevisionHeaderStrip({
  revision,
}: {
  revision: ResolvedDeliverableRevision | null;
}) {
  if (!revision) return null;
  const hasName = Boolean(revision.name?.trim());
  // Show the chip when there's signal to surface: multiple
  // versions on file, a not-V1 revision number, OR an
  // operator-set version name (even on V1 — operators name
  // versions for customer-facing context, e.g. "Final
  // architectural set").
  const showChip =
    revision.total_revisions > 1 ||
    revision.revision_number > 1 ||
    hasName;
  const showNotes = Boolean(revision.reviewer_notes?.trim());
  if (!showChip && !showNotes) return null;
  return (
    <div className="space-y-2">
      {showChip && (
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-semibold"
            style={{
              borderColor: "rgb(var(--vs-accent-rgb) / 0.45)",
              color: "var(--vs-red-text)",
              backgroundColor: "rgb(var(--vs-accent-rgb) / 0.06)",
            }}
          >
            V{revision.revision_number}
            {hasName && (
              <>
                <span className="opacity-50">·</span>
                <span>{revision.name}</span>
              </>
            )}
            <span className="text-[10px] uppercase tracking-wider opacity-80">
              Latest
            </span>
          </span>
          {revision.total_revisions > 1 && (
            <span className="text-[11px] text-muted-foreground">
              {revision.total_revisions} versions on file
            </span>
          )}
        </div>
      )}
      {showNotes && (
        <div className="rounded-md border vs-border-soft bg-muted/20 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {hasName
              ? `What's new in V${revision.revision_number} — ${revision.name}`
              : `What's new in V${revision.revision_number}`}
          </p>
          <p className="mt-1 text-sm whitespace-pre-line">
            {revision.reviewer_notes}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Floor plan block — federates APS files when present (multi-sheet
 * floor plan + RCP + elevations all loaded into one Autodesk
 * Viewer scene), falls back to embedding the floor_plan_details
 * PDF when no APS files exist (legacy uploads).
 */
function FloorPlanViewBlock({
  config,
  scans,
  fileAssets,
  shareToken,
}: {
  config: FloorPlanViewConfig;
  scans: Record<string, ResolvedScan>;
  fileAssets: Record<string, ResolvedFileAsset>;
  shareToken: string | null;
}) {
  const { privatePreview } = usePublicComposition();
  // Operator previews use the existing session + internal-membership gate;
  // public viewers must keep using their narrower share-token endpoint.
  const tokenEndpoint = privatePreview ? "/api/autodesk/token" : shareToken ? `/api/autodesk/public-token?token=${encodeURIComponent(shareToken)}` : null;
  const { enabled: enhanced } = usePublicComposition();
  const id = config.deliverable_id;
  const deliverable = id ? scans[id] : null;
  const details = deliverable?.floor_plan_details;
  const urns = details?.aps_urns ?? [];
  const pdfUrl = details?.pdf_url ?? null;
  const attachedIds = config.file_asset_ids ?? [];
  const attachedAssets = attachedIds
    .map((id) => fileAssets[id])
    .filter((a): a is ResolvedFileAsset => Boolean(a));

  // Resolution priority (most → least specific):
  //   1. Deliverable APS files → multi-URN Autodesk Viewer
  //   2. Deliverable's `floor_plan_details.pdf_url` → PDF
  //      iframe
  //   3. Operator-uploaded attachments (when no deliverable
  //      is picked) → first PDF inline + a download list for
  //      every attached file
  const latestRev = details?.latest_revision ?? null;
  if (id && deliverable) {
    if (urns.length > 0) {
      if (!tokenEndpoint) {
        return <NotSet message="Viewer not available outside a share link." />;
      }
      return (
        <div className="space-y-2">
          <RevisionHeaderStrip revision={latestRev} />
          <VirtualSpaceExploreGate label="floor plan viewer">
            <CompositionViewerMount label="BIM viewer" height={config.height ?? "min(70dvh, 800px)"}><AutodeskViewer
              urns={urns}
              height={config.height ?? "min(70dvh, 800px)"}
              tokenEndpoint={tokenEndpoint}
            /></CompositionViewerMount>
          </VirtualSpaceExploreGate>
        </div>
      );
    }

    if (pdfUrl) {
      return (
        <div className="space-y-2">
          <RevisionHeaderStrip revision={latestRev} />
          <div
            className="w-full rounded-lg overflow-hidden border vs-border-soft bg-muted touch-pan-y sm:touch-auto"
            style={{ height: config.height ?? "min(70dvh, 800px)" }}
          >
            <CompositionViewerMount label="floor plan"><VirtualSpaceExploreGate label="floor plan PDF">
              {enhanced ? <PublicMediaFrame src={pdfUrl} title="Floor plan" /> : <iframe src={pdfUrl} title="Floor plan" className="w-full h-full" />}
            </VirtualSpaceExploreGate></CompositionViewerMount>
          </div>
        </div>
      );
    }

    return <NotSet message="No floor plan files available yet." />;
  }

  if (attachedAssets.length > 0) {
    // Pick the first PDF for the inline viewer; everything
    // else lands in a download grid beneath. Routing the
    // download URLs through `downloadUrlForFile` gives us
    // R2-mirror > public_url fallback for free.
    const firstPdf =
      attachedAssets.find(
        (a) =>
          a.mime_type === "application/pdf" ||
          (a.file_name ?? "").toLowerCase().endsWith(".pdf"),
      ) ?? attachedAssets[0];
    const previewSrc = firstPdf
      ? fileInlinePreviewSrc(firstPdf, shareToken)
      : null;
    return (
      <div className="space-y-3">
        {previewSrc ? (
          <div
            className="w-full rounded-lg overflow-hidden border vs-border-soft bg-muted touch-pan-y sm:touch-auto"
            style={{ height: config.height ?? "min(70dvh, 800px)" }}
          >
            <CompositionViewerMount label="floor plan"><VirtualSpaceExploreGate label="floor plan PDF">
              {enhanced ? <PublicMediaFrame src={previewSrc} title={firstPdf?.file_name ?? "Floor plan"} /> : <iframe src={previewSrc} title={firstPdf?.file_name ?? "Floor plan"} className="w-full h-full" />}
            </VirtualSpaceExploreGate></CompositionViewerMount>
          </div>
        ) : null}
        {attachedAssets.length > 1 && (
          <ul className="vs-glass-card divide-y vs-glass-hairline">
            {attachedAssets.map((a) => {
              const url = downloadUrlForFile(a, shareToken);
              const pdfRow = shareToken && isPdfAsset(a);
              return (
                <li key={a.id} className="px-3 py-2 flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span
                    className="flex-1 min-w-0 text-sm truncate"
                    title={a.file_name ?? undefined}
                  >
                    {a.file_name ?? "Attachment"}
                  </span>
                  {pdfRow ? (
                    <PdfViewDownloadActions file={a} shareToken={shareToken} />
                  ) : url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={fileActionBtnClass}
                    >
                      <Download className="h-3 w-3" aria-hidden />
                      Download
                    </a>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return <NotSet message="Pick a floor plan deliverable or upload your own attachment in the editor." />;
}

/**
 * NavVis IVION embed. Pulls the share URL from the block's
 * `share_url_override` (no first-class `navvis_details.share_url`
 * column today; operators paste it directly). Always renders
 * the iframe AND a link-out card below it — when the iframe is
 * X-Frame-Options-blocked the customer still has a working path
 * to the model.
 *
 * When IVION is unset but a registered COPC is ready (common for
 * atomic Launches minted before conversion finished), fall through
 * to LidarViewerBlock so View tour is not a dead "No NavVis viewer
 * URL" page.
 */
function NavvisEmbedBlock({
  config,
  scans,
  pointClouds,
  shareToken,
}: {
  config: NavvisEmbedConfig;
  scans: Record<string, ResolvedScan>;
  pointClouds: Record<string, ResolvedPointCloud>;
  shareToken: string | null;
}) {
  const id = config.deliverable_id;
  const deliverable = id ? scans[id] : null;
  // Resolve the URL in cascade order: block-level override wins
  // (operator can intentionally point a single embed at a
  // different IVION instance, e.g. an annotated walkthrough)
  // then fall back to the deliverable's `navvis_details.viewer_url`
  // (migration 422 — set once on the deliverable, surfaced
  // everywhere it's embedded). Empty string from either source
  // counts as unset.
  const overrideUrl = config.share_url_override?.trim() || null;
  const deliverableUrl = deliverable?.navvis_details?.viewer_url?.trim() || null;
  const url = overrideUrl ?? deliverableUrl;
  if (!id || !deliverable) {
    return <NotSet message="No NavVis deliverable selected." />;
  }
  if (
    shouldFallbackNavvisEmbedToLidarViewer({
      ivionUrl: url,
      linkedSetIds: linkedPointCloudSetIdsFromScan(deliverable),
      pointClouds,
    })
  ) {
    return (
      <LidarViewerBlock
        config={{
          deliverable_id: id,
          height: config.height,
        }}
        scans={scans}
        pointClouds={pointClouds}
        shareToken={shareToken}
      />
    );
  }
  if (!url) {
    return (
      <NotSet message="No NavVis viewer URL configured — paste the IVION link on the deliverable's LiDAR panel." />
    );
  }
  return (
    <div className="space-y-2">
      <div
        className="w-full rounded-lg overflow-hidden bg-black touch-pan-y sm:touch-auto"
        style={{ height: config.height ?? "min(95dvh, 1300px)" }}
      >
        <VirtualSpaceExploreGate label="NavVis walkthrough">
          {/*
            tabIndex={-1} + loading="lazy" prevents the
            scroll-jump bug where the NavVis IVION iframe
            stole keyboard focus on load and the browser
            then scrolled the page down to bring the focused
            iframe into view, landing the customer mid-page
            instead of at scrollY=0. tabIndex=-1 keeps the
            iframe out of the keyboard tab order so a fresh
            tab press from the document body never lands
            inside the embed (the click-to-explore overlay
            still drives explicit interaction). loading=lazy
            defers the iframe content load until the block
            is near the viewport, which also keeps NavVis's
            internal `window.focus()` from running before
            the user has scrolled to it.
          */}
          <iframe
            src={url}
            title="NavVis walkthrough"
            className="w-full h-full"
            allow="xr-spatial-tracking; vr; gyroscope; accelerometer; fullscreen"
            allowFullScreen
            loading="lazy"
            tabIndex={-1}
          />
        </VirtualSpaceExploreGate>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-2 sm:py-0 min-h-11 sm:min-h-0 touch-manipulation"
      >
        <ExternalLink className="h-3 w-3" />
        Open in NavVis IVION
      </a>
    </div>
  );
}

/**
 * Lidar viewer — point-cloud viewer for Leica / Faro / NavVis
 * deliverables. Supports multiple registered clouds on one
 * deliverable (migration 906): switcher when more than one COPC
 * is ready, plus a download link per registered source.
 */
function LidarViewerBlock({
  config,
  scans,
  pointClouds,
  shareToken,
}: {
  config: LidarViewerConfig;
  scans: Record<string, ResolvedScan>;
  pointClouds: Record<string, ResolvedPointCloud>;
  shareToken: string | null;
}) {
  const { publicShareUrl, privatePreview } = usePublicComposition();
  const id = config.deliverable_id;
  const deliverable = id ? scans[id] : null;
  const primarySetId =
    deliverable?.lidar_details?.point_cloud_set_id ??
    deliverable?.navvis_details?.point_cloud_set_id ??
    null;
  const linkedSetIds = [
    ...new Set(
      [
        ...(deliverable?.lidar_details?.linked_point_cloud_set_ids ?? []),
        ...(deliverable?.navvis_details?.linked_point_cloud_set_ids ?? []),
        ...(primarySetId ? [primarySetId] : []),
      ].filter(Boolean),
    ),
  ];
  const playableSets = linkedSetIds
    .map((setId) => {
      const resolved = pointClouds[setId] ?? null;
      if (!resolved?.copc_url) return null;
      return { setId, resolved };
    })
    .filter((row): row is { setId: string; resolved: ResolvedPointCloud } =>
      Boolean(row),
    );
  // Prefer primary when playable; otherwise first playable secondary.
  const defaultSetId =
    (primarySetId && playableSets.some((p) => p.setId === primarySetId)
      ? primarySetId
      : playableSets[0]?.setId) ?? null;
  const [activeSetId, setActiveSetId] = useState<string | null>(defaultSetId);
  const setId =
    (activeSetId && playableSets.some((p) => p.setId === activeSetId)
      ? activeSetId
      : defaultSetId) ?? null;
  const resolved = setId ? pointClouds[setId] ?? null : null;

  if (!id || !deliverable) {
    return <NotSet message="No lidar deliverable selected." />;
  }
  if (!resolved?.copc_url || !setId) {
    return (
      <NotSet message="Point cloud isn't available yet — it may still be processing." />
    );
  }
  const downloadableSets = linkedSetIds.filter((sid) => {
    const pc = pointClouds[sid];
    // Source download works when the set is resolved (completed COPC
    // path). Allowlist still gates the API.
    return Boolean(pc);
  });
  const multi = playableSets.length > 1;
  return (
    <div className="space-y-3">
      {multi && (
        <div
          className="flex flex-wrap gap-2"
          role="tablist"
          aria-label="Registered point clouds"
        >
          {playableSets.map((entry, index) => {
            const label =
              entry.resolved.file_name?.trim() ||
              entry.resolved.source_format?.toUpperCase() ||
              `Cloud ${index + 1}`;
            const selected = entry.setId === setId;
            return (
              <button
                key={entry.setId}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveSetId(entry.setId)}
                className={
                  selected
                    ? "vs-btn-solid text-xs h-8 px-3"
                    : "vs-btn-outline text-xs h-8 px-3"
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      <div
        className="w-full rounded-lg overflow-hidden bg-black touch-pan-y sm:touch-auto"
        style={{ height: config.height ?? "min(70dvh, 800px)" }}
      >
        <CompositionViewerMount label="point cloud"><VirtualSpaceExploreGate label="lidar point cloud viewer">
          <PointCloudViewer
            key={setId}
            copcUrl={resolved.copc_url}
            panos={resolved.panos}
            floorLabelOverrides={resolved.floor_labels}
            readOnly
            // Launch delivery: customers view the COPC and download
            // the registered E57/LAZ source. setId + share token let
            // `/api/point-cloud/[id]/download` authorize against the
            // VirtualSpace share (same rail as "Source files").
            setId={setId}
            allowSourceDownload={Boolean(setId && shareToken)}
            publicShareToken={shareToken ?? undefined}
            publicShareUrl={publicShareUrl ?? undefined}
            sharingDisabled={privatePreview}
            sourceFormat={resolved.source_format ?? null}
          />
        </VirtualSpaceExploreGate></CompositionViewerMount>
      </div>
      {/* Always-visible downloads outside the explore gate so customers
          get every registered E57 even if they never engage the viewer. */}
      {shareToken && downloadableSets.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {downloadableSets.length === 1
              ? `Download the registered point cloud (${
                  pointClouds[downloadableSets[0]!]?.source_format
                    ? `.${pointClouds[downloadableSets[0]!]?.source_format?.toUpperCase()}`
                    : "source file"
                }).`
              : `Download ${downloadableSets.length} registered point clouds.`}
          </p>
          <div className="flex flex-wrap gap-2">
            {downloadableSets.map((sid, index) => {
              const pc = pointClouds[sid];
              const ext = pc?.source_format
                ? `.${pc.source_format.toUpperCase()}`
                : "source";
              const name = pc?.file_name?.trim() || `Cloud ${index + 1}`;
              const href = `/api/point-cloud/${encodeURIComponent(sid)}/download?token=${encodeURIComponent(shareToken)}`;
              return (
                <a
                  key={sid}
                  href={href}
                  className="vs-btn-solid inline-flex items-center gap-2 text-sm"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  {downloadableSets.length === 1
                    ? `Download ${ext}`
                    : `Download ${name}`}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Drone video block. Renders YouTube / Vimeo / direct mp4-webm
 * URLs based on URL pattern detection. Source priority:
 * `video_url_override` (manual) > `drone_details.video_url`
 * (server-resolved).
 */
function DroneVideoBlock({
  config,
  scans,
}: {
  config: DroneVideoConfig;
  scans: Record<string, ResolvedScan>;
}) {
  const { enabled: enhanced } = usePublicComposition();
  const id = config.deliverable_id;
  const deliverable = id ? scans[id] : null;
  const url = publicVideoSource(config.video_url_override, deliverable?.drone_details?.video_url, deliverable?.output_url);

  if (!id || !deliverable) {
    return <NotSet message="No drone deliverable selected." />;
  }
  if (!url) {
    return <NotSet message="Drone video isn't available yet." />;
  }

  // Validate the actual hostname, not a provider-looking path on another host.
  const parsedUrl = new URL(url);
  const ytSource = hostMatchesDomain(parsedUrl.hostname, "youtube.com") || parsedUrl.hostname === "youtu.be" ? youtubeEmbedUrl(url) : null;
  const vimeoSource = hostMatchesDomain(parsedUrl.hostname, "vimeo.com") ? vimeoEmbedUrl(url) : null;
  const ytMatch = ytSource?.match(/youtube\.com\/embed\/([\w-]{11})/);
  const vimeoMatch = vimeoSource?.match(/vimeo\.com\/video\/(\d+)/);
  let embed: React.ReactNode = null;
  if (ytMatch) {
    embed = (
      <iframe
        src={`https://www.youtube.com/embed/${ytMatch[1]}?rel=0`}
        title="Drone video"
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  } else if (vimeoMatch) {
    embed = (
      <iframe
        src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
        title="Drone video"
        className="w-full h-full"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  } else {
    embed = (
      <video
        src={url}
        controls
        className="w-full h-full bg-black"
        playsInline
        preload="metadata"
      />
    );
  }

  return (
    <Section>
      <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
        {enhanced ? <CompositionViewerMount label="video"><PublicMediaFrame src={ytMatch ? `https://www.youtube.com/embed/${ytMatch[1]}?rel=0` : vimeoMatch ? `https://player.vimeo.com/video/${vimeoMatch[1]}` : url} title="Video" kind={ytMatch || vimeoMatch ? "iframe" : "video"} allow="autoplay; fullscreen; picture-in-picture" /></CompositionViewerMount> : embed}
      </div>
      {config.caption && (
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
          <Plane className="h-3 w-3" />
          {config.caption}
        </p>
      )}
    </Section>
  );
}

/**
 * SpaceSurvey link block — branded card linking to the existing
 * `/survey/[token]/gallery` public surface. Cheaper + more
 * consistent than re-rendering the gallery inline; the
 * SpaceSurvey gallery already has a polished interface that
 * customers know.
 */
function SpaceSurveyLinkBlock({
  config,
  scans,
}: {
  config: SpaceSurveyLinkConfig;
  scans: Record<string, ResolvedScan>;
}) {
  const id = config.deliverable_id;
  const deliverable = id ? scans[id] : null;
  const survey = deliverable?.space_survey;

  if (!id || !deliverable) {
    return <NotSet message="No SpaceSurvey deliverable selected." />;
  }
  if (!survey?.share_token) {
    return <NotSet message="SpaceSurvey hasn't been shared yet." />;
  }
  const title = config.title ?? survey.template_name ?? "SpaceSurvey";
  const galleryUrl = `/survey/${encodeURIComponent(survey.share_token)}/gallery`;
  return (
    <a
      href={galleryUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="vs-glass-card block p-5 sm:p-6 hover:bg-[var(--glass-card)]/90 transition-colors group"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <ClipboardList className="h-3 w-3" />
            SpaceSurvey
          </p>
          <p className="text-base sm:text-lg font-semibold truncate">{title}</p>
          <p className="text-xs text-muted-foreground">
            Every photo + answer captured on site, organised by section.
          </p>
        </div>
        <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0 group-hover:text-foreground transition-colors" />
      </div>
    </a>
  );
}

// Silence unused-import linter for convenience icons that some configs
// may or may not reference depending on what blocks render.
void Link;
